import type { NamespaceCatalog } from '@/i18n/ns';

export type WorkspaceErrorKey =
  | 'uploadRule'
  | 'emptyFile'
  | 'uploadPart'
  | 'uploadEtag'
  | 'uploadGrant'
  | 'uploadParts'
  | 'browserChecksum'
  | 'history401'
  | 'history403'
  | 'historyUnavailable'
  | 'historyUnreadable'
  | 'search401'
  | 'searchUnavailable'
  | 'searchUnreadable'
  | 'skillsUnavailable'
  | 'skillsUnreadable'
  | 'csvColumns'
  | 'csvField'
  | 'csvQuote'
  | 'meetingRecording'
  | 'uploadInterrupted'
  | 'uploadRefused'
  | 'downloadDeck'
  | 'downloadFile'
  | 'integrationsUnavailable'
  | 'inboxActionUnavailable'
  | 'inboxUnavailable'
  | 'referencesUnavailable'
  | 'fileVerification'
  | 'uploadChecksum'
  | 'agentUnreadable'
  | 'office_unreadable'
  | 'office_encrypted'
  | 'office_has_no_text'
  | 'office_too_complex'
  | 'office_extraction_timeout'
  | 'file_text_too_large'
  | 'pdf_has_no_text'
  | 'pdf_encrypted'
  | 'pdf_unreadable'
  | 'pdf_too_many_pages'
  | 'pdf_too_complex'
  | 'file_too_complex'
  | 'pdf_extraction_timeout'
  | 'file_too_large'
  | 'invalid_workspace_input'
  | 'workspace_storage_full'
  | 'workspace_capacity_reached'
  | 'workspace_upload_busy'
  | 'workspace_storage_unavailable'
  | 'company_admin_required'
  | 'uploadGeneric'
  | 'uploadTooLarge'
  | 'attachmentWorkspaceChanged'
  | 'attachmentSignInImage'
  | 'attachmentImageSize'
  | 'attachmentImageUnavailable'
  | 'attachmentUploadImage'
  | 'attachmentFileChanged'
  | 'attachmentSignInFile'
  | 'attachmentEmpty'
  | 'attachmentFileTypes'
  | 'attachmentUploadFile'
  | 'attachmentReadable'
  | 'repairWorkspaceChanged'
  | 'repairCannotSave'
  | 'repairOldUnavailable'
  | 'repairUpload'
  | 'repairUnavailable'
  | 'repairMessageUnavailable'
  | 'repairChatChanged'
  | 'turnUnavailable'
  | 'imagePreparation'
  | 'documentSize'
  | 'projectFileJoinFailed';

const catalog = {
  en: {
    uploadRule:
      'Use Excel (.xlsx), Word (.docx), PowerPoint (.pptx), PDF, CSV, TSV, text, JSON, transcripts, audio or video up to 500 MB. Save older Office files as .xlsx, .docx or .pptx first.',
    emptyFile: '{name} is empty. Choose a file with content.',
    uploadPart: 'A file part could not be uploaded. Try again.',
    uploadEtag: 'The storage service did not confirm a file part. Try again.',
    uploadGrant: 'The upload session is missing its storage grant.',
    uploadParts: 'The upload session returned incomplete part grants.',
    browserChecksum: 'This browser cannot verify a large upload. Use a current browser.',
    history401: 'Sign in again to search your conversations.',
    history403: 'A company invitation is required to search conversations.',
    historyUnavailable: 'Conversation search is unavailable. Try again.',
    historyUnreadable: 'Conversation search returned an unreadable response. Try again.',
    search401: 'Sign in again to search your workspace.',
    searchUnavailable: 'Workspace search is unavailable. Try again.',
    searchUnreadable: 'Workspace search returned an unreadable response. Try again.',
    skillsUnavailable: 'Skills are unavailable. Try again.',
    skillsUnreadable: 'Skills returned an unreadable response. Try again.',
    csvColumns: 'This file has more than 200 columns. Download the original to inspect it.',
    csvField: 'A value is too large to preview. Download the original to inspect it.',
    csvQuote:
      'This CSV has an unfinished quoted value. Download the original or upload a corrected file.',
    meetingRecording: 'This meeting has no retained recording yet.',
    uploadInterrupted: 'The upload was interrupted. Try again.',
    uploadRefused: 'The storage service refused the file. Try again.',
    downloadDeck: 'We could not build the PowerPoint file. Please try again.',
    downloadFile: 'We could not download this file. Please try again.',
    integrationsUnavailable: 'We couldn’t load integrations. Please try again.',
    inboxActionUnavailable: 'This action is unavailable.',
    inboxUnavailable: 'We could not load your Inbox.',
    referencesUnavailable:
      'Some selected references are unavailable. Check their access before continuing.',
    fileVerification: 'File verification failed.',
    uploadChecksum: 'This browser cannot verify a large upload. Use a current browser.',
    agentUnreadable: 'Poneva returned a response this page could not read.',
    office_unreadable:
      '{name} could not be read. Open it in Office and save a new .xlsx, .docx or .pptx copy.',
    office_encrypted: '{name} is password protected. Remove the password and upload it again.',
    office_has_no_text:
      '{name} has no readable text or cells. Upload a text version, or a PDF with selectable text.',
    office_too_complex: '{name} is too complex to read. Split it into smaller files.',
    office_extraction_timeout: '{name} took too long to read. Split it into smaller files.',
    file_text_too_large:
      '{name} is saved for download. Upload a copy under 50 MiB for Poneva to read.',
    pdf_has_no_text:
      '{name} has no selectable text, so it may be a scanned image. Attach a copy up to 4 MB directly to a chat message, or upload a text version.',
    pdf_encrypted: '{name} is password protected. Remove the password and upload it again.',
    pdf_unreadable: '{name} could not be read as a PDF.',
    pdf_too_many_pages: '{name} has more than 1,000 pages. Split it into smaller PDFs.',
    pdf_too_complex: '{name} is too complex to read. Split it into smaller PDFs.',
    file_too_complex:
      '{name} is too deeply nested or too slow to check. Simplify its structure or split it into smaller files.',
    pdf_extraction_timeout:
      '{name} took too long to read. Split it into smaller PDFs and try again.',
    file_too_large: '{name} is larger than 500 MB.',
    invalid_workspace_input:
      '{name} could not be saved. Check that the file matches its type (for example valid JSON).',
    workspace_storage_full:
      'Your company Library has reached its storage limit, so this file was not saved.',
    workspace_capacity_reached:
      'Your company Library has reached its file capacity, so this file was not saved.',
    workspace_upload_busy: 'Other uploads are still being processed. Try again in a moment.',
    workspace_storage_unavailable:
      'File uploads are currently unavailable. Please contact Poneva support.',
    company_admin_required: 'Only company owners can add files to the Library.',
    uploadGeneric: 'We could not upload {name}. Please try again.',
    uploadTooLarge: '{name} is larger than 500 MB.',
    attachmentWorkspaceChanged: 'Your workspace changed. Choose the image again.',
    attachmentSignInImage: 'Sign in to attach an image.',
    attachmentImageSize: 'Choose an image under 16 MiB, or remove it to continue.',
    attachmentImageUnavailable: 'The image is unavailable. Remove it or choose it again.',
    attachmentUploadImage: 'The image could not be uploaded. Remove it or choose another image.',
    attachmentFileChanged: 'Your workspace changed. Attach the file again.',
    attachmentSignInFile: 'Sign in to upload a business file.',
    attachmentEmpty: '{name} is empty.',
    attachmentFileTypes: 'Choose a PDF, text, Excel, Word or PowerPoint file.',
    attachmentUploadFile: 'We could not upload this file.',
    attachmentReadable: 'Remove the failed attachment and upload a readable copy.',
    repairWorkspaceChanged: 'Your workspace changed. Reload the chat before editing.',
    repairCannotSave: 'This attachment cannot be saved. Remove it and choose the file again.',
    repairOldUnavailable: 'This old attachment is unavailable. Remove it or choose the file again.',
    repairUpload: 'The file could not be uploaded. Remove it or choose it again.',
    repairUnavailable: 'The file is unavailable. Remove it or choose it again.',
    repairMessageUnavailable: 'This message is unavailable. Reload the chat.',
    repairChatChanged: 'This chat changed. Reload it before saving your edit.',
    turnUnavailable: 'This turn is unavailable. Reload the chat and send a new message.',
    imagePreparation: 'This image could not be prepared. Remove it and choose another image.',
    documentSize: '{name} is larger than 4 MB.',
    projectFileJoinFailed:
      "{name} is in your files, but it couldn't be added to the project. Add it from the project.",
  },
  bg: {
    uploadRule:
      'Използвайте Excel (.xlsx), Word (.docx), PowerPoint (.pptx), PDF, CSV, TSV, текстови файлове, JSON, транскрипции, аудио или видео до 500 MB. Първо запазете по-старите файлове на Office като .xlsx, .docx или .pptx.',
    emptyFile: '{name} е празен. Изберете файл със съдържание.',
    uploadPart: 'Част от файла не можа да бъде качена. Опитайте отново.',
    uploadEtag: 'Услугата за съхранение не потвърди част от файла. Опитайте отново.',
    uploadGrant: 'В сесията за качване липсва разрешение за съхранение.',
    uploadParts: 'Сесията за качване върна непълни разрешения за частите.',
    browserChecksum:
      'Този браузър не може да потвърди голямо качване. Използвайте актуален браузър.',
    history401: 'Влезте отново, за да търсите в разговорите си.',
    history403: 'За търсене в разговорите е необходима покана от компанията.',
    historyUnavailable: 'Търсенето в разговорите не е налично. Опитайте отново.',
    historyUnreadable: 'Търсенето в разговорите върна нечетим отговор. Опитайте отново.',
    search401: 'Влезте отново, за да търсите в работното си пространство.',
    searchUnavailable: 'Търсенето в работното пространство не е налично. Опитайте отново.',
    searchUnreadable: 'Търсенето в работното пространство върна нечетим отговор. Опитайте отново.',
    skillsUnavailable: 'Уменията не са налични. Опитайте отново.',
    skillsUnreadable: 'Уменията върнаха нечетим отговор. Опитайте отново.',
    csvColumns: 'Този файл съдържа повече от 200 колони. Изтеглете оригинала, за да го прегледате.',
    csvField:
      'Една от стойностите е твърде голяма за визуализация. Изтеглете оригинала, за да я прегледате.',
    csvQuote:
      'Този CSV файл съдържа незавършена стойност в кавички. Изтеглете оригинала или качете коригиран файл.',
    meetingRecording: 'За тази среща все още няма запазен запис.',
    uploadInterrupted: 'Качването беше прекъснато. Опитайте отново.',
    uploadRefused: 'Услугата за съхранение отказа файла. Опитайте отново.',
    downloadDeck: 'Не успяхме да създадем файла на PowerPoint. Опитайте отново.',
    downloadFile: 'Не успяхме да изтеглим този файл. Опитайте отново.',
    integrationsUnavailable: 'Не успяхме да заредим интеграциите. Опитайте отново.',
    inboxActionUnavailable: 'Това действие не е достъпно.',
    inboxUnavailable: 'Не успяхме да заредим входящата ви поща.',
    referencesUnavailable:
      'Някои от избраните препратки не са достъпни. Проверете достъпа до тях, преди да продължите.',
    fileVerification: 'Проверката на файла не бе успешна.',
    uploadChecksum: 'Този браузър не може да провери голямо качване. Използвайте актуален браузър.',
    agentUnreadable: 'Poneva върна отговор, който тази страница не можа да прочете.',
    office_unreadable:
      '{name} не може да бъде прочетен. Отворете го в Office и запазете ново копие във формат .xlsx, .docx или .pptx.',
    office_encrypted: '{name} е защитен с парола. Премахнете паролата и го качете отново.',
    office_has_no_text:
      '{name} не съдържа четим текст или клетки. Качете текстова версия или PDF с избираем текст.',
    office_too_complex: '{name} е твърде сложен за прочитане. Разделете го на по-малки файлове.',
    office_extraction_timeout:
      'Прочитането на {name} отне твърде много време. Разделете го на по-малки файлове.',
    file_text_too_large:
      '{name} е запазен за изтегляне. Качете копие с размер под 50 MiB, за да може Poneva да го прочете.',
    pdf_has_no_text:
      '{name} не съдържа избираем текст и вероятно е сканирано изображение. Прикачете копие с размер до 4 MB директно към чат съобщение или качете текстова версия.',
    pdf_encrypted: '{name} е защитен с парола. Премахнете паролата и го качете отново.',
    pdf_unreadable: '{name} не може да бъде прочетен като PDF.',
    pdf_too_many_pages:
      '{name} съдържа повече от 1000 страници. Разделете го на по-малки PDF файлове.',
    pdf_too_complex: '{name} е твърде сложен за прочитане. Разделете го на по-малки PDF файлове.',
    file_too_complex:
      '{name} е твърде дълбоко вложен или проверката му отнема твърде много време. Опростете структурата му или го разделете на по-малки файлове.',
    pdf_extraction_timeout:
      'Прочитането на {name} отне твърде много време. Разделете го на по-малки PDF файлове и опитайте отново.',
    file_too_large: '{name} е по-голям от 500 MB.',
    invalid_workspace_input:
      '{name} не може да бъде запазен. Проверете дали файлът съответства на типа си (например дали е валиден JSON).',
    workspace_storage_full:
      'Библиотеката на компанията ви достигна ограничението за съхранение и този файл не беше запазен.',
    workspace_capacity_reached:
      'Библиотеката на компанията ви достигна ограничението за броя файлове и този файл не беше запазен.',
    workspace_upload_busy: 'Други качвания все още се обработват. Опитайте отново след малко.',
    workspace_storage_unavailable:
      'Качването на файлове временно не е достъпно. Свържете се с поддръжката на Poneva.',
    company_admin_required:
      'Само собствениците на компанията могат да добавят файлове в Библиотеката.',
    uploadGeneric: 'Не успяхме да качим {name}. Опитайте отново.',
    uploadTooLarge: '{name} е по-голям от 500 MB.',
    attachmentWorkspaceChanged:
      'Работното ви пространство се промени. Изберете изображението отново.',
    attachmentSignInImage: 'Влезте в профила си, за да прикачите изображение.',
    attachmentImageSize:
      'Изберете изображение с размер под 16 MiB или го премахнете, за да продължите.',
    attachmentImageUnavailable: 'Изображението не е налично. Премахнете го или го изберете отново.',
    attachmentUploadImage:
      'Изображението не можа да бъде качено. Премахнете го или изберете друго изображение.',
    attachmentFileChanged: 'Работното ви пространство се промени. Прикачете файла отново.',
    attachmentSignInFile: 'Влезте в профила си, за да качите служебен файл.',
    attachmentEmpty: '{name} е празен.',
    attachmentFileTypes: 'Изберете PDF, текстов, Excel, Word или PowerPoint файл.',
    attachmentUploadFile: 'Не успяхме да качим този файл.',
    attachmentReadable: 'Премахнете неуспешно прикачения файл и качете четливо копие.',
    repairWorkspaceChanged:
      'Работното ви пространство се промени. Презаредете чата, преди да редактирате.',
    repairCannotSave:
      'Този прикачен файл не може да бъде запазен. Премахнете го и изберете файла отново.',
    repairOldUnavailable:
      'Този стар прикачен файл не е наличен. Премахнете го или изберете файла отново.',
    repairUpload: 'Файлът не можа да бъде качен. Премахнете го или го изберете отново.',
    repairUnavailable: 'Файлът не е наличен. Премахнете го или го изберете отново.',
    repairMessageUnavailable: 'Това съобщение не е налично. Презаредете чата.',
    repairChatChanged: 'Този чат се промени. Презаредете го, преди да запазите редакцията си.',
    turnUnavailable: 'Този отговор не е наличен. Презаредете чата и изпратете ново съобщение.',
    imagePreparation:
      'Това изображение не можа да бъде подготвено. Премахнете го и изберете друго изображение.',
    documentSize: '{name} е по-голям от 4 MB.',
    projectFileJoinFailed:
      '{name} е във вашите файлове, но не можа да бъде добавен към проекта. Добавете го от проекта.',
  },
  hr: {
    uploadRule:
      'Upotrebljavajte Excel (.xlsx), Word (.docx), PowerPoint (.pptx), PDF, CSV, TSV, tekstualne datoteke, JSON, transkripte, audiozapise ili videozapise veličine do 500 MB. Starije Officeove datoteke najprije spremite kao .xlsx, .docx ili .pptx.',
    emptyFile: '{name} je prazna. Odaberite datoteku sa sadržajem.',
    uploadPart: 'Dio datoteke nije moguće prenijeti. Pokušajte ponovno.',
    uploadEtag: 'Usluga pohrane nije potvrdila dio datoteke. Pokušajte ponovno.',
    uploadGrant: 'Sesiji prijenosa nedostaje odobrenje za pohranu.',
    uploadParts: 'Sesija prijenosa vratila je nepotpuna odobrenja za dijelove.',
    browserChecksum:
      'Ovaj preglednik ne može provjeriti veliki prijenos. Upotrijebite noviji preglednik.',
    history401: 'Ponovno se prijavite za pretraživanje svojih razgovora.',
    history403: 'Za pretraživanje razgovora potrebna je pozivnica tvrtke.',
    historyUnavailable: 'Pretraživanje razgovora nije dostupno. Pokušajte ponovno.',
    historyUnreadable: 'Pretraživanje razgovora vratilo je nečitljiv odgovor. Pokušajte ponovno.',
    search401: 'Ponovno se prijavite za pretraživanje svog radnog prostora.',
    searchUnavailable: 'Pretraživanje radnog prostora nije dostupno. Pokušajte ponovno.',
    searchUnreadable:
      'Pretraživanje radnog prostora vratilo je nečitljiv odgovor. Pokušajte ponovno.',
    skillsUnavailable: 'Vještine nisu dostupne. Pokušajte ponovno.',
    skillsUnreadable: 'Vještine su vratile nečitljiv odgovor. Pokušajte ponovno.',
    csvColumns: 'Ova datoteka ima više od 200 stupaca. Preuzmite izvornik da biste ga pregledali.',
    csvField: 'Vrijednost je prevelika za pretpregled. Preuzmite izvornik da biste je pregledali.',
    csvQuote:
      'Ovaj CSV sadrži nedovršenu vrijednost pod navodnicima. Preuzmite izvornik ili prenesite ispravljenu datoteku.',
    meetingRecording: 'Za ovaj sastanak još nema sačuvane snimke.',
    uploadInterrupted: 'Prijenos je prekinut. Pokušajte ponovno.',
    uploadRefused: 'Usluga pohrane odbila je datoteku. Pokušajte ponovno.',
    downloadDeck: 'Nismo mogli izraditi datoteku programa PowerPoint. Pokušajte ponovno.',
    downloadFile: 'Nismo mogli preuzeti ovu datoteku. Pokušajte ponovno.',
    integrationsUnavailable: 'Nismo mogli učitati integracije. Pokušajte ponovno.',
    inboxActionUnavailable: 'Ova radnja nije dostupna.',
    inboxUnavailable: 'Nismo mogli učitati vašu ulaznu poštu.',
    referencesUnavailable:
      'Neke odabrane reference nisu dostupne. Provjerite svoj pristup prije nastavka.',
    fileVerification: 'Provjera datoteke nije uspjela.',
    uploadChecksum:
      'Ovaj preglednik ne može provjeriti veliki prijenos. Koristite noviju verziju preglednika.',
    agentUnreadable: 'Poneva je vratila odgovor koji ova stranica nije mogla pročitati.',
    office_unreadable:
      '{name} nije moguće pročitati. Otvorite ga u Officeu i spremite novu kopiju u formatu .xlsx, .docx ili .pptx.',
    office_encrypted: '{name} je zaštićen lozinkom. Uklonite lozinku i ponovno ga prenesite.',
    office_has_no_text:
      '{name} nema čitljiv tekst ni ćelije. Prenesite tekstualnu verziju ili PDF s tekstom koji je moguće označiti.',
    office_too_complex: '{name} je previše složen za čitanje. Podijelite ga u manje datoteke.',
    office_extraction_timeout: '{name} se predugo učitavao. Podijelite ga u manje datoteke.',
    file_text_too_large:
      '{name} je spremljen za preuzimanje. Prenesite kopiju manju od 50 MiB kako bi ga Poneva mogla pročitati.',
    pdf_has_no_text:
      '{name} nema tekst koji je moguće označiti, pa je možda skenirana slika. Priložite kopiju veličine do 4 MB izravno u poruku chata ili prenesite tekstualnu verziju.',
    pdf_encrypted: '{name} je zaštićen lozinkom. Uklonite lozinku i ponovno ga prenesite.',
    pdf_unreadable: '{name} nije moguće pročitati kao PDF.',
    pdf_too_many_pages: '{name} ima više od 1.000 stranica. Podijelite ga u manje PDF datoteke.',
    pdf_too_complex: '{name} je previše složen za čitanje. Podijelite ga u manje PDF datoteke.',
    file_too_complex:
      '{name} je preduboko ugniježđen ili ga je presporo provjeriti. Pojednostavite njegovu strukturu ili ga podijelite u manje datoteke.',
    pdf_extraction_timeout:
      '{name} se predugo učitavao. Podijelite ga u manje PDF datoteke i pokušajte ponovno.',
    file_too_large: '{name} je veći od 500 MB.',
    invalid_workspace_input:
      '{name} nije moguće spremiti. Provjerite odgovara li datoteka svojoj vrsti (na primjer, je li valjani JSON).',
    workspace_storage_full:
      'Biblioteka vaše tvrtke dosegla je ograničenje pohrane pa ova datoteka nije spremljena.',
    workspace_capacity_reached:
      'Biblioteka vaše tvrtke dosegla je ograničenje broja datoteka pa ova datoteka nije spremljena.',
    workspace_upload_busy: 'Druge se datoteke još obrađuju. Pokušajte ponovno za trenutak.',
    workspace_storage_unavailable:
      'Prijenos datoteka trenutačno nije dostupan. Obratite se podršci Poneve.',
    company_admin_required: 'Samo vlasnici tvrtke mogu dodavati datoteke u Biblioteku.',
    uploadGeneric: 'Nije moguće prenijeti {name}. Pokušajte ponovno.',
    uploadTooLarge: '{name} je veći od 500 MB.',
    attachmentWorkspaceChanged: 'Vaš se radni prostor promijenio. Ponovno odaberite sliku.',
    attachmentSignInImage: 'Prijavite se da biste priložili sliku.',
    attachmentImageSize: 'Odaberite sliku manju od 16 MiB ili je uklonite da biste nastavili.',
    attachmentImageUnavailable: 'Slika nije dostupna. Uklonite je ili je ponovno odaberite.',
    attachmentUploadImage: 'Sliku nije moguće prenijeti. Uklonite je ili odaberite drugu sliku.',
    attachmentFileChanged: 'Vaš se radni prostor promijenio. Ponovno priložite datoteku.',
    attachmentSignInFile: 'Prijavite se da biste prenijeli poslovnu datoteku.',
    attachmentEmpty: '{name} je prazna.',
    attachmentFileTypes: 'Odaberite PDF, tekstnu, Excelovu, Wordovu ili PowerPointovu datoteku.',
    attachmentUploadFile: 'Ovu datoteku nije moguće prenijeti.',
    attachmentReadable: 'Uklonite neuspjelo priloženu datoteku i prenesite čitljivu kopiju.',
    repairWorkspaceChanged:
      'Vaš se radni prostor promijenio. Ponovno učitajte razgovor prije uređivanja.',
    repairCannotSave:
      'Ovaj privitak nije moguće spremiti. Uklonite ga i ponovno odaberite datoteku.',
    repairOldUnavailable:
      'Ovaj stari privitak nije dostupan. Uklonite ga ili ponovno odaberite datoteku.',
    repairUpload: 'Datoteku nije moguće prenijeti. Uklonite je ili je ponovno odaberite.',
    repairUnavailable: 'Datoteka nije dostupna. Uklonite je ili je ponovno odaberite.',
    repairMessageUnavailable: 'Ova poruka nije dostupna. Ponovno učitajte razgovor.',
    repairChatChanged: 'Ovaj se razgovor promijenio. Ponovno ga učitajte prije spremanja izmjene.',
    turnUnavailable: 'Ovaj potez nije dostupan. Ponovno učitajte razgovor i pošaljite novu poruku.',
    imagePreparation: 'Ovu sliku nije moguće pripremiti. Uklonite je i odaberite drugu sliku.',
    documentSize: '{name} je veća od 4 MB.',
    projectFileJoinFailed:
      '{name} je u vašim datotekama, ali nije bilo moguće dodati ga u projekt. Dodajte ga iz projekta.',
  },
  cs: {
    uploadRule:
      'Použijte Excel (.xlsx), Word (.docx), PowerPoint (.pptx), PDF, CSV, TSV, textové soubory, JSON, přepisy, zvuk nebo video do 500 MB. Starší soubory Office nejprve uložte jako .xlsx, .docx nebo .pptx.',
    emptyFile: '{name} je prázdný. Vyberte soubor s obsahem.',
    uploadPart: 'Část souboru se nepodařilo nahrát. Zkuste to znovu.',
    uploadEtag: 'Úložiště nepotvrdilo část souboru. Zkuste to znovu.',
    uploadGrant: 'Nahrávací relaci chybí oprávnění úložiště.',
    uploadParts: 'Nahrávací relace vrátila neúplná oprávnění částí souboru.',
    browserChecksum:
      'Tento prohlížeč nedokáže ověřit velké nahrávání. Použijte aktuální prohlížeč.',
    history401: 'Znovu se přihlaste a vyhledejte své konverzace.',
    history403: 'K vyhledávání v konverzacích je nutná pozvánka do společnosti.',
    historyUnavailable: 'Vyhledávání v konverzacích není k dispozici. Zkuste to znovu.',
    historyUnreadable: 'Vyhledávání v konverzacích vrátilo nečitelnou odpověď. Zkuste to znovu.',
    search401: 'Znovu se přihlaste a vyhledejte obsah svého pracovního prostoru.',
    searchUnavailable: 'Vyhledávání v pracovním prostoru není k dispozici. Zkuste to znovu.',
    searchUnreadable:
      'Vyhledávání v pracovním prostoru vrátilo nečitelnou odpověď. Zkuste to znovu.',
    skillsUnavailable: 'Dovednosti nejsou k dispozici. Zkuste to znovu.',
    skillsUnreadable: 'Dovednosti vrátily nečitelnou odpověď. Zkuste to znovu.',
    csvColumns:
      'Tento soubor obsahuje více než 200 sloupců. Stáhněte si originál a prohlédněte si ho.',
    csvField: 'Hodnota je příliš velká pro náhled. Stáhněte si originál a prohlédněte si ji.',
    csvQuote:
      'Tento soubor CSV obsahuje neukončenou hodnotu v uvozovkách. Stáhněte si originál nebo nahrajte opravený soubor.',
    meetingRecording: 'Tato schůzka zatím nemá uchovanou nahrávku.',
    uploadInterrupted: 'Nahrávání bylo přerušeno. Zkuste to znovu.',
    uploadRefused: 'Úložiště soubor odmítlo. Zkuste to znovu.',
    downloadDeck: 'Soubor PowerPoint se nepodařilo vytvořit. Zkuste to znovu.',
    downloadFile: 'Tento soubor se nepodařilo stáhnout. Zkuste to znovu.',
    integrationsUnavailable: 'Integrace se nepodařilo načíst. Zkuste to znovu.',
    inboxActionUnavailable: 'Tato akce není dostupná.',
    inboxUnavailable: 'Doručenou poštu se nepodařilo načíst.',
    referencesUnavailable:
      'Některé vybrané reference nejsou dostupné. Před pokračováním zkontrolujte svůj přístup.',
    fileVerification: 'Ověření souboru se nezdařilo.',
    uploadChecksum:
      'Tento prohlížeč nemůže ověřit velký nahrávaný soubor. Použijte aktuální prohlížeč.',
    agentUnreadable: 'Poneva vrátila odpověď, kterou tato stránka nedokázala přečíst.',
    office_unreadable:
      '{name} se nepodařilo přečíst. Otevřete ho v Office a uložte novou kopii ve formátu .xlsx, .docx nebo .pptx.',
    office_encrypted: '{name} je chráněný heslem. Odeberte heslo a nahrajte ho znovu.',
    office_has_no_text:
      '{name} neobsahuje žádný čitelný text ani buňky. Nahrajte textovou verzi nebo PDF s textem, který lze označit.',
    office_too_complex: '{name} je příliš složitý na přečtení. Rozdělte ho na menší soubory.',
    office_extraction_timeout:
      'Čtení souboru {name} trvalo příliš dlouho. Rozdělte ho na menší soubory.',
    file_text_too_large:
      '{name} byl uložen ke stažení. Nahrajte kopii menší než 50 MiB, aby ho Poneva mohla přečíst.',
    pdf_has_no_text:
      '{name} neobsahuje text, který lze označit, takže může jít o naskenovaný obrázek. Přiložte kopii o velikosti až 4 MB přímo ke zprávě v chatu nebo nahrajte textovou verzi.',
    pdf_encrypted: '{name} je chráněný heslem. Odeberte heslo a nahrajte ho znovu.',
    pdf_unreadable: '{name} se nepodařilo přečíst jako PDF.',
    pdf_too_many_pages: '{name} má více než 1 000 stran. Rozdělte ho na menší soubory PDF.',
    pdf_too_complex: '{name} je příliš složitý na přečtení. Rozdělte ho na menší soubory PDF.',
    file_too_complex:
      '{name} je příliš hluboce vnořený nebo jeho kontrola trvá příliš dlouho. Zjednodušte jeho strukturu nebo ho rozdělte na menší soubory.',
    pdf_extraction_timeout:
      'Čtení souboru {name} trvalo příliš dlouho. Rozdělte ho na menší soubory PDF a zkuste to znovu.',
    file_too_large: '{name} je větší než 500 MB.',
    invalid_workspace_input:
      '{name} se nepodařilo uložit. Zkontrolujte, zda soubor odpovídá svému typu (například zda jde o platný JSON).',
    workspace_storage_full:
      'Firemní Knihovna dosáhla limitu úložiště, takže tento soubor nebyl uložen.',
    workspace_capacity_reached:
      'Firemní Knihovna dosáhla limitu počtu souborů, takže tento soubor nebyl uložen.',
    workspace_upload_busy: 'Stále se zpracovávají jiné nahrané soubory. Zkuste to znovu za chvíli.',
    workspace_storage_unavailable:
      'Nahrávání souborů je momentálně nedostupné. Kontaktujte podporu Poneva.',
    company_admin_required: 'Soubory do Knihovny mohou přidávat pouze vlastníci společnosti.',
    uploadGeneric: 'Soubor {name} se nepodařilo nahrát. Zkuste to znovu.',
    uploadTooLarge: '{name} je větší než 500 MB.',
    attachmentWorkspaceChanged: 'Váš pracovní prostor se změnil. Vyberte obrázek znovu.',
    attachmentSignInImage: 'Přihlaste se a připojte obrázek.',
    attachmentImageSize: 'Vyberte obrázek menší než 16 MiB, nebo ho odeberte a pokračujte.',
    attachmentImageUnavailable: 'Obrázek není k dispozici. Odeberte ho, nebo ho vyberte znovu.',
    attachmentUploadImage: 'Obrázek se nepodařilo nahrát. Odeberte ho, nebo vyberte jiný obrázek.',
    attachmentFileChanged: 'Váš pracovní prostor se změnil. Připojte soubor znovu.',
    attachmentSignInFile: 'Přihlaste se a nahrajte pracovní soubor.',
    attachmentEmpty: '{name} je prázdný.',
    attachmentFileTypes:
      'Vyberte soubor PDF, textový soubor nebo soubor Excel, Word či PowerPoint.',
    attachmentUploadFile: 'Tento soubor se nepodařilo nahrát.',
    attachmentReadable: 'Odeberte neúspěšně připojený soubor a nahrajte čitelnou kopii.',
    repairWorkspaceChanged: 'Váš pracovní prostor se změnil. Před úpravou znovu načtěte chat.',
    repairCannotSave: 'Tuto přílohu nelze uložit. Odeberte ji a vyberte soubor znovu.',
    repairOldUnavailable:
      'Tato stará příloha není k dispozici. Odeberte ji, nebo vyberte soubor znovu.',
    repairUpload: 'Soubor se nepodařilo nahrát. Odeberte ho, nebo ho vyberte znovu.',
    repairUnavailable: 'Soubor není k dispozici. Odeberte ho, nebo ho vyberte znovu.',
    repairMessageUnavailable: 'Tato zpráva není k dispozici. Znovu načtěte chat.',
    repairChatChanged: 'Tento chat se změnil. Před uložením úpravy ho znovu načtěte.',
    turnUnavailable: 'Toto kolo není k dispozici. Znovu načtěte chat a odešlete novou zprávu.',
    imagePreparation: 'Tento obrázek se nepodařilo připravit. Odeberte ho a vyberte jiný obrázek.',
    documentSize: '{name} je větší než 4 MB.',
    projectFileJoinFailed:
      '{name} je ve vašich souborech, ale nepodařilo se ho přidat do projektu. Přidejte ho z projektu.',
  },
  da: {
    uploadRule:
      'Brug Excel (.xlsx), Word (.docx), PowerPoint (.pptx), PDF, CSV, TSV, tekst, JSON, transskripter, lyd eller video på op til 500 MB. Gem ældre Office-filer som .xlsx, .docx eller .pptx først.',
    emptyFile: '{name} er tom. Vælg en fil med indhold.',
    uploadPart: 'En fildel kunne ikke uploades. Prøv igen.',
    uploadEtag: 'Lagringstjenesten bekræftede ikke en fildel. Prøv igen.',
    uploadGrant: 'Uploadsessionen mangler sin lagringstilladelse.',
    uploadParts: 'Uploadsessions svar indeholdt ikke komplette tilladelser til fildelene.',
    browserChecksum:
      'Denne browser kan ikke kontrollere en stor upload. Brug en opdateret browser.',
    history401: 'Log ind igen for at søge i dine samtaler.',
    history403: 'Der kræves en invitation til virksomheden for at søge i samtaler.',
    historyUnavailable: 'Søgning i samtaler er ikke tilgængelig. Prøv igen.',
    historyUnreadable: 'Søgning i samtaler returnerede et svar, der ikke kunne læses. Prøv igen.',
    search401: 'Log ind igen for at søge i dit arbejdsområde.',
    searchUnavailable: 'Søgning i arbejdsområdet er ikke tilgængelig. Prøv igen.',
    searchUnreadable:
      'Søgning i arbejdsområdet returnerede et svar, der ikke kunne læses. Prøv igen.',
    skillsUnavailable: 'Skills er ikke tilgængelige. Prøv igen.',
    skillsUnreadable: 'Skills returnerede et svar, der ikke kunne læses. Prøv igen.',
    csvColumns: 'Denne fil har mere end 200 kolonner. Download originalen for at undersøge den.',
    csvField:
      'En værdi er for stor til at blive forhåndsvist. Download originalen for at undersøge den.',
    csvQuote:
      'Denne CSV-fil har en ufærdig værdi i anførselstegn. Download originalen, eller upload en rettet fil.',
    meetingRecording: 'Dette møde har endnu ingen gemt optagelse.',
    uploadInterrupted: 'Uploaden blev afbrudt. Prøv igen.',
    uploadRefused: 'Lagringstjenesten afviste filen. Prøv igen.',
    downloadDeck: 'Vi kunne ikke oprette PowerPoint-filen. Prøv igen.',
    downloadFile: 'Vi kunne ikke downloade denne fil. Prøv igen.',
    integrationsUnavailable: 'Vi kunne ikke indlæse integrationerne. Prøv igen.',
    inboxActionUnavailable: 'Denne handling er ikke tilgængelig.',
    inboxUnavailable: 'Vi kunne ikke indlæse din indbakke.',
    referencesUnavailable:
      'Nogle valgte referencer er ikke tilgængelige. Kontrollér deres adgang, før du fortsætter.',
    fileVerification: 'Filbekræftelsen mislykkedes.',
    uploadChecksum: 'Denne browser kan ikke bekræfte en stor upload. Brug en opdateret browser.',
    agentUnreadable: 'Poneva returnerede et svar, som denne side ikke kunne læse.',
    office_unreadable:
      '{name} kunne ikke læses. Åbn den i Office, og gem en ny .xlsx-, .docx- eller .pptx-kopi.',
    office_encrypted:
      '{name} er beskyttet med adgangskode. Fjern adgangskoden, og upload den igen.',
    office_has_no_text:
      '{name} indeholder ingen læsbar tekst eller celler. Upload en tekstversion eller en PDF med markerbar tekst.',
    office_too_complex: '{name} er for kompleks til at blive læst. Opdel den i mindre filer.',
    office_extraction_timeout: 'Det tog for lang tid at læse {name}. Opdel den i mindre filer.',
    file_text_too_large:
      '{name} er gemt til download. Upload en kopi på under 50 MiB, så Poneva kan læse den.',
    pdf_has_no_text:
      '{name} indeholder ingen markerbar tekst og kan derfor være et scannet billede. Vedhæft en kopi på højst 4 MB direkte i en chatbesked, eller upload en tekstversion.',
    pdf_encrypted: '{name} er beskyttet med adgangskode. Fjern adgangskoden, og upload den igen.',
    pdf_unreadable: '{name} kunne ikke læses som PDF.',
    pdf_too_many_pages: '{name} har mere end 1.000 sider. Opdel den i mindre PDF-filer.',
    pdf_too_complex: '{name} er for kompleks til at blive læst. Opdel den i mindre PDF-filer.',
    file_too_complex:
      '{name} er for dybt indlejret eller for langsom at kontrollere. Forenkl strukturen, eller opdel den i mindre filer.',
    pdf_extraction_timeout:
      'Det tog for lang tid at læse {name}. Opdel den i mindre PDF-filer, og prøv igen.',
    file_too_large: '{name} er større end 500 MB.',
    invalid_workspace_input:
      '{name} kunne ikke gemmes. Kontrollér, at filen matcher sin type (f.eks. gyldig JSON).',
    workspace_storage_full:
      'Din virksomheds Bibliotek har nået sin lagergrænse, så filen blev ikke gemt.',
    workspace_capacity_reached:
      'Din virksomheds Bibliotek har nået grænsen for antal filer, så filen blev ikke gemt.',
    workspace_upload_busy: 'Andre uploads behandles stadig. Prøv igen om lidt.',
    workspace_storage_unavailable:
      'Filuploads er i øjeblikket ikke tilgængelige. Kontakt Poneva support.',
    company_admin_required: 'Kun virksomhedsejere kan føje filer til Biblioteket.',
    uploadGeneric: 'Vi kunne ikke uploade {name}. Prøv igen.',
    uploadTooLarge: '{name} er større end 500 MB.',
    attachmentWorkspaceChanged: 'Dit arbejdsområde er ændret. Vælg billedet igen.',
    attachmentSignInImage: 'Log ind for at vedhæfte et billede.',
    attachmentImageSize: 'Vælg et billede på under 16 MiB, eller fjern det for at fortsætte.',
    attachmentImageUnavailable: 'Billedet er ikke tilgængeligt. Fjern det, eller vælg det igen.',
    attachmentUploadImage: 'Billedet kunne ikke uploades. Fjern det, eller vælg et andet billede.',
    attachmentFileChanged: 'Dit arbejdsområde er ændret. Vedhæft filen igen.',
    attachmentSignInFile: 'Log ind for at uploade en virksomhedsfil.',
    attachmentEmpty: '{name} er tom.',
    attachmentFileTypes: 'Vælg en PDF-, tekst-, Excel-, Word- eller PowerPoint-fil.',
    attachmentUploadFile: 'Vi kunne ikke uploade denne fil.',
    attachmentReadable: 'Fjern den mislykkede vedhæftning, og upload en læsbar kopi.',
    repairWorkspaceChanged: 'Dit arbejdsområde er ændret. Genindlæs chatten, før du redigerer.',
    repairCannotSave: 'Denne vedhæftning kan ikke gemmes. Fjern den, og vælg filen igen.',
    repairOldUnavailable:
      'Denne gamle vedhæftning er ikke tilgængelig. Fjern den, eller vælg filen igen.',
    repairUpload: 'Filen kunne ikke uploades. Fjern den, eller vælg den igen.',
    repairUnavailable: 'Filen er ikke tilgængelig. Fjern den, eller vælg den igen.',
    repairMessageUnavailable: 'Denne besked er ikke tilgængelig. Genindlæs chatten.',
    repairChatChanged: 'Denne chat er ændret. Genindlæs den, før du gemmer din redigering.',
    turnUnavailable: 'Denne omgang er ikke tilgængelig. Genindlæs chatten, og send en ny besked.',
    imagePreparation: 'Dette billede kunne ikke klargøres. Fjern det, og vælg et andet billede.',
    documentSize: '{name} er større end 4 MB.',
    projectFileJoinFailed:
      '{name} er i dine filer, men kunne ikke føjes til projektet. Tilføj den fra projektet.',
  },
  nl: {
    uploadRule:
      'Gebruik Excel (.xlsx), Word (.docx), PowerPoint (.pptx), PDF, CSV, TSV, tekst, JSON, transcripties, audio of video tot 500 MB. Sla oudere Office-bestanden eerst op als .xlsx, .docx of .pptx.',
    emptyFile: '{name} is leeg. Kies een bestand met inhoud.',
    uploadPart: 'Een bestandsdeel kon niet worden geüpload. Probeer het opnieuw.',
    uploadEtag: 'De opslagservice heeft een bestandsdeel niet bevestigd. Probeer het opnieuw.',
    uploadGrant: 'De uploadsessie bevat geen opslagmachtiging.',
    uploadParts: 'De uploadsessie heeft onvolledige machtigingen voor bestandsdelen geretourneerd.',
    browserChecksum:
      'Deze browser kan een grote upload niet verifiëren. Gebruik een recente browser.',
    history401: 'Meld je opnieuw aan om je gesprekken te doorzoeken.',
    history403: 'Je hebt een bedrijfsuitnodiging nodig om gesprekken te doorzoeken.',
    historyUnavailable: 'Gesprekken doorzoeken is niet beschikbaar. Probeer het opnieuw.',
    historyUnreadable:
      'Het doorzoeken van gesprekken gaf een onleesbare reactie. Probeer het opnieuw.',
    search401: 'Meld je opnieuw aan om je werkruimte te doorzoeken.',
    searchUnavailable: 'Werkruimte doorzoeken is niet beschikbaar. Probeer het opnieuw.',
    searchUnreadable:
      'Het doorzoeken van de werkruimte gaf een onleesbare reactie. Probeer het opnieuw.',
    skillsUnavailable: 'Vaardigheden zijn niet beschikbaar. Probeer het opnieuw.',
    skillsUnreadable: 'Vaardigheden gaven een onleesbare reactie. Probeer het opnieuw.',
    csvColumns:
      'Dit bestand heeft meer dan 200 kolommen. Download het origineel om het te bekijken.',
    csvField:
      'Een waarde is te groot om als voorbeeld weer te geven. Download het origineel om het te bekijken.',
    csvQuote:
      'Deze CSV bevat een onvolledige waarde tussen aanhalingstekens. Download het origineel of upload een gecorrigeerd bestand.',
    meetingRecording: 'Deze vergadering heeft nog geen bewaarde opname.',
    uploadInterrupted: 'De upload is onderbroken. Probeer het opnieuw.',
    uploadRefused: 'De opslagservice heeft het bestand geweigerd. Probeer het opnieuw.',
    downloadDeck: 'We konden het PowerPoint-bestand niet maken. Probeer het opnieuw.',
    downloadFile: 'We konden dit bestand niet downloaden. Probeer het opnieuw.',
    integrationsUnavailable: 'We konden de integraties niet laden. Probeer het opnieuw.',
    inboxActionUnavailable: 'Deze actie is niet beschikbaar.',
    inboxUnavailable: 'We konden je Postvak IN niet laden.',
    referencesUnavailable:
      'Sommige geselecteerde verwijzingen zijn niet beschikbaar. Controleer hun toegang voordat je doorgaat.',
    fileVerification: 'Bestandsverificatie mislukt.',
    uploadChecksum:
      'Deze browser kan een grote upload niet verifiëren. Gebruik een recente browser.',
    agentUnreadable: 'Poneva heeft een antwoord gegeven dat deze pagina niet kon lezen.',
    office_unreadable:
      '{name} kon niet worden gelezen. Open het bestand in Office en sla een nieuwe .xlsx-, .docx- of .pptx-kopie op.',
    office_encrypted:
      '{name} is beveiligd met een wachtwoord. Verwijder het wachtwoord en upload het bestand opnieuw.',
    office_has_no_text:
      '{name} bevat geen leesbare tekst of cellen. Upload een tekstversie of een pdf met selecteerbare tekst.',
    office_too_complex:
      '{name} is te complex om te lezen. Splits het bestand op in kleinere bestanden.',
    office_extraction_timeout:
      'Het duurde te lang om {name} te lezen. Splits het bestand op in kleinere bestanden.',
    file_text_too_large:
      '{name} is opgeslagen om te downloaden. Upload een kopie van minder dan 50 MiB zodat Poneva het kan lezen.',
    pdf_has_no_text:
      '{name} bevat geen selecteerbare tekst en is mogelijk een gescande afbeelding. Voeg een kopie van maximaal 4 MB rechtstreeks toe aan een chatbericht of upload een tekstversie.',
    pdf_encrypted:
      '{name} is beveiligd met een wachtwoord. Verwijder het wachtwoord en upload het bestand opnieuw.',
    pdf_unreadable: '{name} kon niet als pdf worden gelezen.',
    pdf_too_many_pages:
      "{name} bevat meer dan 1.000 pagina's. Splits het bestand op in kleinere pdf's.",
    pdf_too_complex: "{name} is te complex om te lezen. Splits het bestand op in kleinere pdf's.",
    file_too_complex:
      '{name} is te diep genest of het controleren ervan duurt te lang. Vereenvoudig de structuur of splits het bestand op in kleinere bestanden.',
    pdf_extraction_timeout:
      "Het duurde te lang om {name} te lezen. Splits het bestand op in kleinere pdf's en probeer het opnieuw.",
    file_too_large: '{name} is groter dan 500 MB.',
    invalid_workspace_input:
      '{name} kon niet worden opgeslagen. Controleer of het bestand overeenkomt met het bestandstype (bijvoorbeeld geldige JSON).',
    workspace_storage_full:
      'De Library van je bedrijf heeft de opslaglimiet bereikt. Dit bestand is daarom niet opgeslagen.',
    workspace_capacity_reached:
      'De Library van je bedrijf heeft de limiet voor het aantal bestanden bereikt. Dit bestand is daarom niet opgeslagen.',
    workspace_upload_busy:
      'Andere uploads worden nog verwerkt. Probeer het over een moment opnieuw.',
    workspace_storage_unavailable:
      'Bestanden uploaden is momenteel niet beschikbaar. Neem contact op met Poneva Support.',
    company_admin_required: 'Alleen bedrijfseigenaren kunnen bestanden aan de Library toevoegen.',
    uploadGeneric: 'We konden {name} niet uploaden. Probeer het opnieuw.',
    uploadTooLarge: '{name} is groter dan 500 MB.',
    attachmentWorkspaceChanged: 'Je werkruimte is gewijzigd. Kies de afbeelding opnieuw.',
    attachmentSignInImage: 'Meld je aan om een afbeelding toe te voegen.',
    attachmentImageSize:
      'Kies een afbeelding kleiner dan 16 MiB of verwijder de afbeelding om door te gaan.',
    attachmentImageUnavailable:
      'De afbeelding is niet beschikbaar. Verwijder de afbeelding of kies deze opnieuw.',
    attachmentUploadImage:
      'De afbeelding kon niet worden geüpload. Verwijder de afbeelding of kies een andere afbeelding.',
    attachmentFileChanged: 'Je werkruimte is gewijzigd. Voeg het bestand opnieuw toe.',
    attachmentSignInFile: 'Meld je aan om een bedrijfsbestand te uploaden.',
    attachmentEmpty: '{name} is leeg.',
    attachmentFileTypes: 'Kies een PDF-, tekst-, Excel-, Word- of PowerPoint-bestand.',
    attachmentUploadFile: 'We konden dit bestand niet uploaden.',
    attachmentReadable: 'Verwijder de mislukte bijlage en upload een leesbare kopie.',
    repairWorkspaceChanged:
      'Je werkruimte is gewijzigd. Laad de chat opnieuw voordat je deze bewerkt.',
    repairCannotSave:
      'Deze bijlage kan niet worden opgeslagen. Verwijder de bijlage en kies het bestand opnieuw.',
    repairOldUnavailable:
      'Deze oude bijlage is niet beschikbaar. Verwijder de bijlage of kies het bestand opnieuw.',
    repairUpload:
      'Het bestand kon niet worden geüpload. Verwijder het bestand of kies het opnieuw.',
    repairUnavailable:
      'Het bestand is niet beschikbaar. Verwijder het bestand of kies het opnieuw.',
    repairMessageUnavailable: 'Dit bericht is niet beschikbaar. Laad de chat opnieuw.',
    repairChatChanged:
      'Deze chat is gewijzigd. Laad de chat opnieuw voordat je je bewerking opslaat.',
    turnUnavailable:
      'Deze beurt is niet beschikbaar. Laad de chat opnieuw en stuur een nieuw bericht.',
    imagePreparation:
      'Deze afbeelding kon niet worden voorbereid. Verwijder de afbeelding en kies een andere afbeelding.',
    documentSize: '{name} is groter dan 4 MB.',
    projectFileJoinFailed:
      '{name} staat in je bestanden, maar kon niet aan het project worden toegevoegd. Voeg het toe vanuit het project.',
  },
  et: {
    uploadRule:
      'Kasutage Exceli (.xlsx), Wordi (.docx), PowerPointi (.pptx), PDF-, CSV-, TSV- või tekstifaile, JSON-faile, transkriptsioone ning heli- või videofaile mahuga kuni 500 MB. Salvestage vanemad Office’i failid esmalt vormingus .xlsx, .docx või .pptx.',
    emptyFile: '{name} on tühi. Vali sisuga fail.',
    uploadPart: 'Failiosa üleslaadimine ebaõnnestus. Proovi uuesti.',
    uploadEtag: 'Salvestusteenus ei kinnitanud failiosa. Proovi uuesti.',
    uploadGrant: 'Üleslaadimisseansil puudub salvestusluba.',
    uploadParts: 'Üleslaadimisseanss tagastas puudulikud osade load.',
    browserChecksum:
      'See brauser ei saa suurt üleslaadimist kontrollida. Kasuta ajakohast brauserit.',
    history401: 'Vestluste otsimiseks logi uuesti sisse.',
    history403: 'Vestluste otsimiseks on vaja ettevõtte kutset.',
    historyUnavailable: 'Vestluste otsing pole saadaval. Proovi uuesti.',
    historyUnreadable: 'Vestluste otsing tagastas loetamatu vastuse. Proovi uuesti.',
    search401: 'Tööruumist otsimiseks logi uuesti sisse.',
    searchUnavailable: 'Tööruumiotsing pole saadaval. Proovi uuesti.',
    searchUnreadable: 'Tööruumiotsing tagastas loetamatu vastuse. Proovi uuesti.',
    skillsUnavailable: 'Oskused pole saadaval. Proovi uuesti.',
    skillsUnreadable: 'Oskused tagastasid loetamatu vastuse. Proovi uuesti.',
    csvColumns: 'Selles failis on üle 200 veeru. Faili kontrollimiseks laadi alla algne fail.',
    csvField: 'Väärtus on eelvaate jaoks liiga suur. Faili kontrollimiseks laadi alla algne fail.',
    csvQuote:
      'Selles CSV-failis on lõpetamata jutumärkides väärtus. Laadi alla algne fail või üles parandatud fail.',
    meetingRecording: 'Sellel koosolekul pole veel säilitatud salvestist.',
    uploadInterrupted: 'Üleslaadimine katkes. Proovige uuesti.',
    uploadRefused: 'Salvestusteenus lükkas faili tagasi. Proovige uuesti.',
    downloadDeck: 'PowerPointi faili loomine ebaõnnestus. Proovige uuesti.',
    downloadFile: 'Selle faili allalaadimine ebaõnnestus. Proovige uuesti.',
    integrationsUnavailable: 'Integratsioonide laadimine ebaõnnestus. Proovige uuesti.',
    inboxActionUnavailable: 'See toiming pole saadaval.',
    inboxUnavailable: 'Postkasti laadimine ebaõnnestus.',
    referencesUnavailable:
      'Mõned valitud viited pole saadaval. Kontrollige enne jätkamist nende juurdepääsuõigusi.',
    fileVerification: 'Faili kontrollimine ebaõnnestus.',
    uploadChecksum:
      'See brauser ei saa suurt üleslaadimist kontrollida. Kasutage ajakohast brauserit.',
    agentUnreadable: 'Poneva tagastas vastuse, mida see leht ei suutnud lugeda.',
    office_unreadable:
      '{name} ei saanud lugeda. Avage see Office’is ja salvestage uus .xlsx-, .docx- või .pptx-koopia.',
    office_encrypted: '{name} on parooliga kaitstud. Eemaldage parool ja laadige fail uuesti üles.',
    office_has_no_text:
      '{name} ei sisalda loetavat teksti ega lahtreid. Laadige üles tekstiversioon või valitava tekstiga PDF.',
    office_too_complex: '{name} on lugemiseks liiga keerukas. Jagage see väiksemateks failideks.',
    office_extraction_timeout:
      '{name} lugemine võttis liiga kaua aega. Jagage see väiksemateks failideks.',
    file_text_too_large:
      '{name} salvestati allalaadimiseks. Laadige üles alla 50 MiB suurune koopia, et Poneva saaks seda lugeda.',
    pdf_has_no_text:
      '{name} ei sisalda valitavat teksti, seega võib see olla skannitud pilt. Lisage vestlussõnumile otse kuni 4 MB suurune koopia või laadige üles tekstiversioon.',
    pdf_encrypted: '{name} on parooliga kaitstud. Eemaldage parool ja laadige fail uuesti üles.',
    pdf_unreadable: '{name} ei saanud PDF-failina lugeda.',
    pdf_too_many_pages:
      '{name} sisaldab üle 1 000 lehekülje. Jagage see väiksemateks PDF-failideks.',
    pdf_too_complex: '{name} on lugemiseks liiga keerukas. Jagage see väiksemateks PDF-failideks.',
    file_too_complex:
      '{name} on liiga sügavalt pesastatud või selle kontrollimine võtab liiga kaua aega. Lihtsustage selle struktuuri või jagage see väiksemateks failideks.',
    pdf_extraction_timeout:
      '{name} lugemine võttis liiga kaua aega. Jagage see väiksemateks PDF-failideks ja proovige uuesti.',
    file_too_large: '{name} on suurem kui 500 MB.',
    invalid_workspace_input:
      '{name} ei saanud salvestada. Kontrollige, et fail vastaks oma tüübile (näiteks oleks kehtiv JSON).',
    workspace_storage_full:
      'Teie ettevõtte teegi salvestusruumi limiit on täis, seega seda faili ei salvestatud.',
    workspace_capacity_reached:
      'Teie ettevõtte teegi failide arvupiir on täis, seega seda faili ei salvestatud.',
    workspace_upload_busy: 'Teisi üleslaadimisi töödeldakse veel. Proovige hetke pärast uuesti.',
    workspace_storage_unavailable:
      'Failide üleslaadimine pole praegu saadaval. Võtke ühendust Poneva toega.',
    company_admin_required: 'Faile saavad teeki lisada ainult ettevõtte omanikud.',
    uploadGeneric: 'Me ei saanud faili {name} üles laadida. Proovige uuesti.',
    uploadTooLarge: '{name} on suurem kui 500 MB.',
    attachmentWorkspaceChanged: 'Sinu tööruum on muutunud. Vali pilt uuesti.',
    attachmentSignInImage: 'Pildi lisamiseks logi sisse.',
    attachmentImageSize: 'Vali alla 16 MiB suurune pilt või eemalda see jätkamiseks.',
    attachmentImageUnavailable: 'Pilt pole saadaval. Eemalda see või vali uuesti.',
    attachmentUploadImage: 'Pilti ei saanud üles laadida. Eemalda see või vali mõni teine pilt.',
    attachmentFileChanged: 'Sinu tööruum on muutunud. Lisa fail uuesti.',
    attachmentSignInFile: 'Tööfaili üleslaadimiseks logi sisse.',
    attachmentEmpty: '{name} on tühi.',
    attachmentFileTypes: 'Vali PDF-, teksti-, Exceli-, Wordi- või PowerPointi fail.',
    attachmentUploadFile: 'Seda faili ei saanud üles laadida.',
    attachmentReadable: 'Eemalda nurjunud manus ja laadi üles loetav koopia.',
    repairWorkspaceChanged: 'Sinu tööruum on muutunud. Laadi vestlus enne muutmist uuesti.',
    repairCannotSave: 'Seda manust ei saa salvestada. Eemalda see ja vali fail uuesti.',
    repairOldUnavailable: 'See vana manus pole saadaval. Eemalda see või vali fail uuesti.',
    repairUpload: 'Faili ei saanud üles laadida. Eemalda see või vali uuesti.',
    repairUnavailable: 'Fail pole saadaval. Eemalda see või vali uuesti.',
    repairMessageUnavailable: 'See sõnum pole saadaval. Laadi vestlus uuesti.',
    repairChatChanged: 'See vestlus on muutunud. Laadi see enne muudatuse salvestamist uuesti.',
    turnUnavailable: 'See voor pole saadaval. Laadi vestlus uuesti ja saada uus sõnum.',
    imagePreparation: 'Seda pilti ei saanud ette valmistada. Eemalda see ja vali mõni teine pilt.',
    documentSize: '{name} on suurem kui 4 MB.',
    projectFileJoinFailed:
      '{name} on sinu failides, kuid seda ei õnnestunud projekti lisada. Lisa see projektist.',
  },
  fi: {
    uploadRule:
      'Käytä Excel-tiedostoja (.xlsx), Word-tiedostoja (.docx), PowerPoint-tiedostoja (.pptx), PDF-, CSV- ja TSV-tiedostoja, tekstitiedostoja, JSON-tiedostoja, litterointeja sekä ääni- tai videotiedostoja, joiden koko on enintään 500 Mt. Tallenna vanhemmat Office-tiedostot ensin .xlsx-, .docx- tai .pptx-muotoon.',
    emptyFile: '{name} on tyhjä. Valitse tiedosto, jossa on sisältöä.',
    uploadPart: 'Tiedoston osaa ei voitu ladata. Yritä uudelleen.',
    uploadEtag: 'Tallennuspalvelu ei vahvistanut tiedoston osaa. Yritä uudelleen.',
    uploadGrant: 'Latausistunnolta puuttuu tallennusoikeus.',
    uploadParts: 'Latausistunto palautti puutteelliset osien oikeudet.',
    browserChecksum: 'Tämä selain ei voi vahvistaa suurta latausta. Käytä ajantasaista selainta.',
    history401: 'Kirjaudu uudelleen hakeaksesi keskusteluistasi.',
    history403: 'Keskustelujen hakeminen edellyttää yrityksen kutsua.',
    historyUnavailable: 'Keskusteluhaku ei ole käytettävissä. Yritä uudelleen.',
    historyUnreadable: 'Keskusteluhaku palautti lukukelvottoman vastauksen. Yritä uudelleen.',
    search401: 'Kirjaudu uudelleen hakeaksesi työtilastasi.',
    searchUnavailable: 'Työtilahaku ei ole käytettävissä. Yritä uudelleen.',
    searchUnreadable: 'Työtilahaku palautti lukukelvottoman vastauksen. Yritä uudelleen.',
    skillsUnavailable: 'Taidot eivät ole käytettävissä. Yritä uudelleen.',
    skillsUnreadable: 'Taidot palauttivat lukukelvottoman vastauksen. Yritä uudelleen.',
    csvColumns:
      'Tiedostossa on yli 200 saraketta. Lataa alkuperäinen tiedosto tarkastellaksesi sitä.',
    csvField:
      'Arvo on liian suuri esikatseltavaksi. Lataa alkuperäinen tiedosto tarkastellaksesi sitä.',
    csvQuote:
      'Tässä CSV-tiedostossa on keskeneräinen lainausmerkeillä rajattu arvo. Lataa alkuperäinen tiedosto tai lataa korjattu tiedosto.',
    meetingRecording: 'Tästä kokouksesta ei ole vielä säilytettyä tallennetta.',
    uploadInterrupted: 'Lähetys keskeytyi. Yritä uudelleen.',
    uploadRefused: 'Tallennuspalvelu hylkäsi tiedoston. Yritä uudelleen.',
    downloadDeck: 'PowerPoint-tiedoston luominen ei onnistunut. Yritä uudelleen.',
    downloadFile: 'Tiedoston lataaminen ei onnistunut. Yritä uudelleen.',
    integrationsUnavailable: 'Integraatioita ei voitu ladata. Yritä uudelleen.',
    inboxActionUnavailable: 'Tämä toiminto ei ole käytettävissä.',
    inboxUnavailable: 'Saapuneita viestejäsi ei voitu ladata.',
    referencesUnavailable:
      'Jotkin valituista viitteistä eivät ole käytettävissä. Tarkista niiden käyttöoikeudet ennen jatkamista.',
    fileVerification: 'Tiedoston vahvistus epäonnistui.',
    uploadChecksum: 'Tämä selain ei voi tarkistaa suurta lähetystä. Käytä ajantasaista selainta.',
    agentUnreadable: 'Ponevan palauttamaa vastausta ei voitu lukea tällä sivulla.',
    office_unreadable:
      'Tiedostoa {name} ei voitu lukea. Avaa se Officessa ja tallenna uusi .xlsx-, .docx- tai .pptx-kopio.',
    office_encrypted:
      'Tiedosto {name} on suojattu salasanalla. Poista salasana ja lataa tiedosto uudelleen.',
    office_has_no_text:
      'Tiedostossa {name} ei ole luettavaa tekstiä tai soluja. Lataa tekstiversio tai PDF, jonka tekstin voi valita.',
    office_too_complex:
      'Tiedosto {name} on liian monimutkainen luettavaksi. Jaa se pienemmiksi tiedostoiksi.',
    office_extraction_timeout:
      'Tiedoston {name} lukeminen kesti liian kauan. Jaa se pienemmiksi tiedostoiksi.',
    file_text_too_large:
      'Tiedosto {name} on tallennettu ladattavaksi. Lataa alle 50 MiB:n kokoinen kopio, jotta Poneva voi lukea sen.',
    pdf_has_no_text:
      'Tiedostossa {name} ei ole valittavaa tekstiä, joten se saattaa olla skannattu kuva. Liitä enintään 4 MB:n kokoinen kopio suoraan chat-viestiin tai lataa tekstiversio.',
    pdf_encrypted:
      'Tiedosto {name} on suojattu salasanalla. Poista salasana ja lataa tiedosto uudelleen.',
    pdf_unreadable: 'Tiedostoa {name} ei voitu lukea PDF-tiedostona.',
    pdf_too_many_pages:
      'Tiedostossa {name} on yli 1 000 sivua. Jaa se pienemmiksi PDF-tiedostoiksi.',
    pdf_too_complex:
      'Tiedosto {name} on liian monimutkainen luettavaksi. Jaa se pienemmiksi PDF-tiedostoiksi.',
    file_too_complex:
      'Tiedosto {name} on liian syvästi sisäkkäinen tai sen tarkistaminen kestää liian kauan. Yksinkertaista sen rakennetta tai jaa se pienemmiksi tiedostoiksi.',
    pdf_extraction_timeout:
      'Tiedoston {name} lukeminen kesti liian kauan. Jaa se pienemmiksi PDF-tiedostoiksi ja yritä uudelleen.',
    file_too_large: 'Tiedosto {name} on suurempi kuin 500 MB.',
    invalid_workspace_input:
      'Tiedostoa {name} ei voitu tallentaa. Tarkista, että tiedosto vastaa tyyppiään (esimerkiksi että JSON on kelvollinen).',
    workspace_storage_full:
      'Yrityksesi Library on saavuttanut tallennustilarajan, joten tätä tiedostoa ei tallennettu.',
    workspace_capacity_reached:
      'Yrityksesi Library on saavuttanut tiedostokapasiteettinsa, joten tätä tiedostoa ei tallennettu.',
    workspace_upload_busy:
      'Muita latauksia käsitellään parhaillaan. Yritä uudelleen hetken kuluttua.',
    workspace_storage_unavailable:
      'Tiedostojen lataaminen ei ole tällä hetkellä käytettävissä. Ota yhteyttä Ponevan tukeen.',
    company_admin_required: 'Vain yrityksen omistajat voivat lisätä tiedostoja Libraryyn.',
    uploadGeneric: 'Tiedostoa {name} ei voitu ladata. Yritä uudelleen.',
    uploadTooLarge: 'Tiedosto {name} on suurempi kuin 500 MB.',
    attachmentWorkspaceChanged: 'Työtilasi vaihtui. Valitse kuva uudelleen.',
    attachmentSignInImage: 'Kirjaudu sisään liittääksesi kuvan.',
    attachmentImageSize: 'Valitse alle 16 MiB:n kokoinen kuva tai poista kuva jatkaaksesi.',
    attachmentImageUnavailable: 'Kuva ei ole käytettävissä. Poista se tai valitse se uudelleen.',
    attachmentUploadImage: 'Kuvan lataaminen epäonnistui. Poista se tai valitse toinen kuva.',
    attachmentFileChanged: 'Työtilasi vaihtui. Liitä tiedosto uudelleen.',
    attachmentSignInFile: 'Kirjaudu sisään ladataksesi yritystiedoston.',
    attachmentEmpty: '{name} on tyhjä.',
    attachmentFileTypes: 'Valitse PDF-, teksti-, Excel-, Word- tai PowerPoint-tiedosto.',
    attachmentUploadFile: 'Tiedoston lataaminen epäonnistui.',
    attachmentReadable: 'Poista epäonnistunut liite ja lataa luettava kopio.',
    repairWorkspaceChanged: 'Työtilasi vaihtui. Lataa keskustelu uudelleen ennen muokkaamista.',
    repairCannotSave: 'Tätä liitettä ei voi tallentaa. Poista se ja valitse tiedosto uudelleen.',
    repairOldUnavailable:
      'Tämä vanha liite ei ole käytettävissä. Poista se tai valitse tiedosto uudelleen.',
    repairUpload: 'Tiedoston lataaminen epäonnistui. Poista se tai valitse se uudelleen.',
    repairUnavailable: 'Tiedosto ei ole käytettävissä. Poista se tai valitse se uudelleen.',
    repairMessageUnavailable: 'Tämä viesti ei ole käytettävissä. Lataa keskustelu uudelleen.',
    repairChatChanged:
      'Tämä keskustelu muuttui. Lataa se uudelleen ennen muokkauksen tallentamista.',
    turnUnavailable:
      'Tämä viestivuoro ei ole käytettävissä. Lataa keskustelu uudelleen ja lähetä uusi viesti.',
    imagePreparation: 'Kuvan valmistelu epäonnistui. Poista se ja valitse toinen kuva.',
    documentSize: '{name} on suurempi kuin 4 Mt.',
    projectFileJoinFailed:
      '{name} on tiedostoissasi, mutta sitä ei voitu lisätä projektiin. Lisää se projektista.',
  },
  fr: {
    uploadRule:
      'Utilisez des fichiers Excel (.xlsx), Word (.docx), PowerPoint (.pptx), PDF, CSV, TSV, texte, JSON, des transcriptions, des fichiers audio ou vidéo de 500 Mo maximum. Enregistrez d’abord les anciens fichiers Office au format .xlsx, .docx ou .pptx.',
    emptyFile: '{name} est vide. Choisissez un fichier contenant des données.',
    uploadPart: 'Une partie du fichier n’a pas pu être importée. Réessayez.',
    uploadEtag: 'Le service de stockage n’a pas confirmé une partie du fichier. Réessayez.',
    uploadGrant: 'L’autorisation de stockage de la session d’importation est manquante.',
    uploadParts:
      'La session d’importation a renvoyé des autorisations incomplètes pour certaines parties.',
    browserChecksum:
      'Ce navigateur ne peut pas vérifier un importation volumineux. Utilisez un navigateur récent.',
    history401: 'Reconnectez-vous pour rechercher dans vos conversations.',
    history403: 'Une invitation de l’entreprise est requise pour rechercher des conversations.',
    historyUnavailable: 'La recherche dans les conversations est indisponible. Réessayez.',
    historyUnreadable:
      'La recherche dans les conversations a renvoyé une réponse illisible. Réessayez.',
    search401: 'Reconnectez-vous pour effectuer une recherche dans votre espace de travail.',
    searchUnavailable: 'La recherche dans l’espace de travail est indisponible. Réessayez.',
    searchUnreadable:
      'La recherche dans l’espace de travail a renvoyé une réponse illisible. Réessayez.',
    skillsUnavailable: 'Les compétences sont indisponibles. Réessayez.',
    skillsUnreadable: 'Les compétences ont renvoyé une réponse illisible. Réessayez.',
    csvColumns: 'Ce fichier contient plus de 200 colonnes. Téléchargez l’original pour l’examiner.',
    csvField:
      'Une valeur est trop volumineuse pour être prévisualisée. Téléchargez l’original pour l’examiner.',
    csvQuote:
      'Ce fichier CSV contient une valeur entre guillemets non terminée. Téléchargez l’original ou importez une version corrigée.',
    meetingRecording: 'Cette réunion ne dispose pas encore d’un enregistrement conservé.',
    uploadInterrupted: 'Le chargement a été interrompu. Réessayez.',
    uploadRefused: 'Le service de stockage a refusé le fichier. Réessayez.',
    downloadDeck: 'Nous n’avons pas pu créer le fichier PowerPoint. Réessayez.',
    downloadFile: 'Nous n’avons pas pu télécharger ce fichier. Réessayez.',
    integrationsUnavailable: 'Nous n’avons pas pu charger les intégrations. Réessayez.',
    inboxActionUnavailable: 'Cette action n’est pas disponible.',
    inboxUnavailable: 'Nous n’avons pas pu charger votre boîte de réception.',
    referencesUnavailable:
      'Certaines références sélectionnées ne sont pas disponibles. Vérifiez leurs accès avant de continuer.',
    fileVerification: 'La vérification du fichier a échoué.',
    uploadChecksum:
      'Ce navigateur ne peut pas vérifier un chargement volumineux. Utilisez un navigateur à jour.',
    agentUnreadable: 'Poneva a renvoyé une réponse que cette page n’a pas pu lire.',
    office_unreadable:
      '{name} n’a pas pu être lu. Ouvrez-le dans Office et enregistrez une nouvelle copie au format .xlsx, .docx ou .pptx.',
    office_encrypted:
      '{name} est protégé par un mot de passe. Supprimez le mot de passe, puis importez-le à nouveau.',
    office_has_no_text:
      '{name} ne contient aucun texte ni aucune cellule lisible. Importez une version texte ou un PDF avec du texte sélectionnable.',
    office_too_complex: '{name} est trop complexe à lire. Divisez-le en fichiers plus petits.',
    office_extraction_timeout:
      'La lecture de {name} a pris trop de temps. Divisez-le en fichiers plus petits.',
    file_text_too_large:
      '{name} est disponible au téléchargement. Importez une copie de moins de 50 MiB pour que Poneva puisse la lire.',
    pdf_has_no_text:
      '{name} ne contient aucun texte sélectionnable et est peut-être une image numérisée. Joignez directement une copie de 4 Mo maximum à un message ou importez une version texte.',
    pdf_encrypted:
      '{name} est protégé par un mot de passe. Supprimez le mot de passe, puis importez-le à nouveau.',
    pdf_unreadable: '{name} n’a pas pu être lu comme un PDF.',
    pdf_too_many_pages:
      '{name} contient plus de 1 000 pages. Divisez-le en fichiers PDF plus petits.',
    pdf_too_complex: '{name} est trop complexe à lire. Divisez-le en fichiers PDF plus petits.',
    file_too_complex:
      '{name} est trop profondément imbriqué ou trop lent à vérifier. Simplifiez sa structure ou divisez-le en fichiers plus petits.',
    pdf_extraction_timeout:
      'La lecture de {name} a pris trop de temps. Divisez-le en fichiers PDF plus petits, puis réessayez.',
    file_too_large: '{name} dépasse 500 Mo.',
    invalid_workspace_input:
      '{name} n’a pas pu être enregistré. Vérifiez que le fichier correspond à son type, par exemple qu’il s’agit d’un fichier JSON valide.',
    workspace_storage_full:
      'La Bibliothèque de votre entreprise a atteint sa limite de stockage. Ce fichier n’a donc pas été enregistré.',
    workspace_capacity_reached:
      'La Bibliothèque de votre entreprise a atteint sa capacité maximale de fichiers. Ce fichier n’a donc pas été enregistré.',
    workspace_upload_busy: 'D’autres importations sont encore en cours. Réessayez dans un instant.',
    workspace_storage_unavailable:
      'L’importation de fichiers est actuellement indisponible. Contactez l’assistance Poneva.',
    company_admin_required:
      'Seuls les propriétaires de l’entreprise peuvent ajouter des fichiers à la Bibliothèque.',
    uploadGeneric: 'Nous n’avons pas pu importer {name}. Veuillez réessayer.',
    uploadTooLarge: '{name} dépasse 500 Mo.',
    attachmentWorkspaceChanged: 'Votre espace de travail a changé. Sélectionnez à nouveau l’image.',
    attachmentSignInImage: 'Connectez-vous pour joindre une image.',
    attachmentImageSize:
      'Sélectionnez une image de moins de 16 Mio ou supprimez-la pour continuer.',
    attachmentImageUnavailable:
      'L’image n’est pas disponible. Supprimez-la ou sélectionnez-la à nouveau.',
    attachmentUploadImage:
      'Impossible d’importer l’image. Supprimez-la ou sélectionnez une autre image.',
    attachmentFileChanged: 'Votre espace de travail a changé. Joignez à nouveau le fichier.',
    attachmentSignInFile: 'Connectez-vous pour importer un fichier professionnel.',
    attachmentEmpty: '{name} est vide.',
    attachmentFileTypes: 'Sélectionnez un fichier PDF, texte, Excel, Word ou PowerPoint.',
    attachmentUploadFile: 'Impossible d’importer ce fichier.',
    attachmentReadable: 'Supprimez la pièce jointe défaillante et importez une copie lisible.',
    repairWorkspaceChanged:
      'Votre espace de travail a changé. Rechargez la conversation avant de la modifier.',
    repairCannotSave:
      'Impossible d’enregistrer cette pièce jointe. Supprimez-la et sélectionnez à nouveau le fichier.',
    repairOldUnavailable:
      'Cette ancienne pièce jointe n’est pas disponible. Supprimez-la ou sélectionnez à nouveau le fichier.',
    repairUpload: 'Impossible d’importer le fichier. Supprimez-le ou sélectionnez-le à nouveau.',
    repairUnavailable:
      'Le fichier n’est pas disponible. Supprimez-le ou sélectionnez-le à nouveau.',
    repairMessageUnavailable: 'Ce message n’est pas disponible. Rechargez la conversation.',
    repairChatChanged:
      'Cette conversation a changé. Rechargez-la avant d’enregistrer votre modification.',
    turnUnavailable:
      'Ce tour n’est pas disponible. Rechargez la conversation et envoyez un nouveau message.',
    imagePreparation:
      'Impossible de préparer cette image. Supprimez-la et sélectionnez-en une autre.',
    documentSize: '{name} fait plus de 4 Mo.',
    projectFileJoinFailed:
      '{name} est dans vos fichiers, mais n’a pas pu être ajouté au projet. Ajoutez-le depuis le projet.',
  },
  de: {
    uploadRule:
      'Verwenden Sie Excel (.xlsx), Word (.docx), PowerPoint (.pptx), PDF, CSV, TSV, Textdateien, JSON, Transkripte sowie Audio- oder Videodateien mit bis zu 500 MB. Speichern Sie ältere Office-Dateien zuerst als .xlsx, .docx oder .pptx.',
    emptyFile: '{name} ist leer. Wählen Sie eine Datei mit Inhalt aus.',
    uploadPart: 'Ein Dateiteil konnte nicht hochgeladen werden. Versuchen Sie es erneut.',
    uploadEtag: 'Der Speicherdienst hat einen Dateiteil nicht bestätigt. Versuchen Sie es erneut.',
    uploadGrant: 'Der Upload-Sitzung fehlt die Speicherberechtigung.',
    uploadParts:
      'Die Upload-Sitzung hat unvollständige Berechtigungen für Dateiteile zurückgegeben.',
    browserChecksum:
      'Dieser Browser kann keinen großen Upload überprüfen. Verwenden Sie einen aktuellen Browser.',
    history401: 'Melden Sie sich erneut an, um Ihre Unterhaltungen zu durchsuchen.',
    history403: 'Zum Durchsuchen von Unterhaltungen ist eine Unternehmenseinladung erforderlich.',
    historyUnavailable: 'Die Unterhaltungssuche ist nicht verfügbar. Versuchen Sie es erneut.',
    historyUnreadable:
      'Die Unterhaltungssuche hat eine nicht lesbare Antwort zurückgegeben. Versuchen Sie es erneut.',
    search401: 'Melden Sie sich erneut an, um Ihren Workspace zu durchsuchen.',
    searchUnavailable: 'Die Workspace-Suche ist nicht verfügbar. Versuchen Sie es erneut.',
    searchUnreadable:
      'Die Workspace-Suche hat eine nicht lesbare Antwort zurückgegeben. Versuchen Sie es erneut.',
    skillsUnavailable: 'Skills sind nicht verfügbar. Versuchen Sie es erneut.',
    skillsUnreadable:
      'Skills haben eine nicht lesbare Antwort zurückgegeben. Versuchen Sie es erneut.',
    csvColumns:
      'Diese Datei enthält mehr als 200 Spalten. Laden Sie das Original herunter, um sie zu prüfen.',
    csvField:
      'Ein Wert ist zu groß für die Vorschau. Laden Sie das Original herunter, um ihn zu prüfen.',
    csvQuote:
      'Diese CSV-Datei enthält einen nicht abgeschlossenen Wert in Anführungszeichen. Laden Sie das Original herunter oder laden Sie eine korrigierte Datei hoch.',
    meetingRecording: 'Für dieses Meeting gibt es noch keine gespeicherte Aufzeichnung.',
    uploadInterrupted: 'Der Upload wurde unterbrochen. Versuchen Sie es erneut.',
    uploadRefused: 'Der Speicherdienst hat die Datei abgelehnt. Versuchen Sie es erneut.',
    downloadDeck: 'Die PowerPoint-Datei konnte nicht erstellt werden. Versuchen Sie es erneut.',
    downloadFile: 'Diese Datei konnte nicht heruntergeladen werden. Versuchen Sie es erneut.',
    integrationsUnavailable: 'Integrationen konnten nicht geladen werden. Versuchen Sie es erneut.',
    inboxActionUnavailable: 'Diese Aktion ist nicht verfügbar.',
    inboxUnavailable: 'Der Posteingang konnte nicht geladen werden.',
    referencesUnavailable:
      'Einige ausgewählte Referenzen sind nicht verfügbar. Prüfen Sie deren Zugriffsrechte, bevor Sie fortfahren.',
    fileVerification: 'Die Dateiprüfung ist fehlgeschlagen.',
    uploadChecksum:
      'Dieser Browser kann einen großen Upload nicht verifizieren. Verwenden Sie einen aktuellen Browser.',
    agentUnreadable: 'Poneva hat eine Antwort zurückgegeben, die diese Seite nicht lesen konnte.',
    office_unreadable:
      '{name} konnte nicht gelesen werden. Öffnen Sie die Datei in Office und speichern Sie eine neue .xlsx-, .docx- oder .pptx-Kopie.',
    office_encrypted:
      '{name} ist passwortgeschützt. Entfernen Sie das Passwort und laden Sie die Datei erneut hoch.',
    office_has_no_text:
      '{name} enthält keinen lesbaren Text bzw. keine lesbaren Zellen. Laden Sie eine Textversion oder ein PDF mit auswählbarem Text hoch.',
    office_too_complex:
      '{name} ist zu komplex zum Lesen. Teilen Sie die Datei in kleinere Dateien auf.',
    office_extraction_timeout:
      '{name} konnte nicht rechtzeitig gelesen werden. Teilen Sie die Datei in kleinere Dateien auf.',
    file_text_too_large:
      '{name} wurde zum Herunterladen gespeichert. Laden Sie für Poneva eine Kopie mit weniger als 50 MiB hoch.',
    pdf_has_no_text:
      '{name} enthält keinen auswählbaren Text und ist möglicherweise ein gescanntes Bild. Hängen Sie eine Kopie mit bis zu 4 MB direkt an eine Chatnachricht an oder laden Sie eine Textversion hoch.',
    pdf_encrypted:
      '{name} ist passwortgeschützt. Entfernen Sie das Passwort und laden Sie die Datei erneut hoch.',
    pdf_unreadable: '{name} konnte nicht als PDF gelesen werden.',
    pdf_too_many_pages:
      '{name} enthält mehr als 1.000 Seiten. Teilen Sie die Datei in kleinere PDFs auf.',
    pdf_too_complex: '{name} ist zu komplex zum Lesen. Teilen Sie die Datei in kleinere PDFs auf.',
    file_too_complex:
      '{name} ist zu tief verschachtelt oder zu langsam zu prüfen. Vereinfachen Sie die Struktur oder teilen Sie die Datei in kleinere Dateien auf.',
    pdf_extraction_timeout:
      '{name} konnte nicht rechtzeitig gelesen werden. Teilen Sie die Datei in kleinere PDFs auf und versuchen Sie es erneut.',
    file_too_large: '{name} ist größer als 500 MB.',
    invalid_workspace_input:
      '{name} konnte nicht gespeichert werden. Überprüfen Sie, ob der Dateityp zum Inhalt passt (z. B. gültiges JSON).',
    workspace_storage_full:
      'Die Bibliothek Ihres Unternehmens hat ihr Speicherlimit erreicht. Diese Datei wurde daher nicht gespeichert.',
    workspace_capacity_reached:
      'Die Bibliothek Ihres Unternehmens hat ihre Dateikapazität erreicht. Diese Datei wurde daher nicht gespeichert.',
    workspace_upload_busy:
      'Andere Uploads werden noch verarbeitet. Versuchen Sie es gleich noch einmal.',
    workspace_storage_unavailable:
      'Datei-Uploads sind derzeit nicht verfügbar. Wenden Sie sich bitte an den Poneva-Support.',
    company_admin_required: 'Nur Unternehmenseigentümer können Dateien zur Bibliothek hinzufügen.',
    uploadGeneric: '{name} konnte nicht hochgeladen werden. Versuchen Sie es bitte erneut.',
    uploadTooLarge: '{name} ist größer als 500 MB.',
    attachmentWorkspaceChanged:
      'Ihr Arbeitsbereich wurde geändert. Wählen Sie das Bild erneut aus.',
    attachmentSignInImage: 'Melden Sie sich an, um ein Bild anzuhängen.',
    attachmentImageSize:
      'Wählen Sie ein Bild unter 16 MiB aus oder entfernen Sie es, um fortzufahren.',
    attachmentImageUnavailable:
      'Das Bild ist nicht verfügbar. Entfernen Sie es oder wählen Sie es erneut aus.',
    attachmentUploadImage:
      'Das Bild konnte nicht hochgeladen werden. Entfernen Sie es oder wählen Sie ein anderes Bild aus.',
    attachmentFileChanged: 'Ihr Arbeitsbereich wurde geändert. Hängen Sie die Datei erneut an.',
    attachmentSignInFile: 'Melden Sie sich an, um eine Unternehmensdatei hochzuladen.',
    attachmentEmpty: '{name} ist leer.',
    attachmentFileTypes: 'Wählen Sie eine PDF-, Text-, Excel-, Word- oder PowerPoint-Datei aus.',
    attachmentUploadFile: 'Diese Datei konnte nicht hochgeladen werden.',
    attachmentReadable:
      'Entfernen Sie den fehlgeschlagenen Anhang und laden Sie eine lesbare Kopie hoch.',
    repairWorkspaceChanged:
      'Ihr Arbeitsbereich wurde geändert. Laden Sie den Chat neu, bevor Sie ihn bearbeiten.',
    repairCannotSave:
      'Dieser Anhang kann nicht gespeichert werden. Entfernen Sie ihn und wählen Sie die Datei erneut aus.',
    repairOldUnavailable:
      'Dieser alte Anhang ist nicht verfügbar. Entfernen Sie ihn oder wählen Sie die Datei erneut aus.',
    repairUpload:
      'Die Datei konnte nicht hochgeladen werden. Entfernen Sie sie oder wählen Sie sie erneut aus.',
    repairUnavailable:
      'Die Datei ist nicht verfügbar. Entfernen Sie sie oder wählen Sie sie erneut aus.',
    repairMessageUnavailable: 'Diese Nachricht ist nicht verfügbar. Laden Sie den Chat neu.',
    repairChatChanged:
      'Dieser Chat wurde geändert. Laden Sie ihn neu, bevor Sie Ihre Bearbeitung speichern.',
    turnUnavailable:
      'Dieser Gesprächsschritt ist nicht verfügbar. Laden Sie den Chat neu und senden Sie eine neue Nachricht.',
    imagePreparation:
      'Dieses Bild konnte nicht vorbereitet werden. Entfernen Sie es oder wählen Sie ein anderes Bild aus.',
    documentSize: '{name} ist größer als 4 MB.',
    projectFileJoinFailed:
      '{name} befindet sich in Ihren Dateien, konnte aber nicht zum Projekt hinzugefügt werden. Fügen Sie die Datei über das Projekt hinzu.',
  },
  el: {
    uploadRule:
      'Χρησιμοποιήστε αρχεία Excel (.xlsx), Word (.docx), PowerPoint (.pptx), PDF, CSV, TSV, κειμένου, JSON, απομαγνητοφωνήσεις, ήχου ή βίντεο έως 500 MB. Αποθηκεύστε πρώτα τα παλαιότερα αρχεία του Office ως .xlsx, .docx ή .pptx.',
    emptyFile: 'Το {name} είναι κενό. Επιλέξτε ένα αρχείο με περιεχόμενο.',
    uploadPart: 'Δεν ήταν δυνατή η μεταφόρτωση ενός τμήματος του αρχείου. Δοκιμάστε ξανά.',
    uploadEtag: 'Η υπηρεσία αποθήκευσης δεν επιβεβαίωσε ένα τμήμα του αρχείου. Δοκιμάστε ξανά.',
    uploadGrant: 'Από τη συνεδρία μεταφόρτωσης λείπει η άδεια αποθήκευσης.',
    uploadParts: 'Η συνεδρία μεταφόρτωσης επέστρεψε ελλιπείς άδειες τμημάτων.',
    browserChecksum:
      'Αυτό το πρόγραμμα περιήγησης δεν μπορεί να επαληθεύσει μια μεγάλη μεταφόρτωση. Χρησιμοποιήστε ένα ενημερωμένο πρόγραμμα περιήγησης.',
    history401: 'Συνδεθείτε ξανά για να αναζητήσετε τις συνομιλίες σας.',
    history403: 'Απαιτείται πρόσκληση από την εταιρεία για την αναζήτηση συνομιλιών.',
    historyUnavailable: 'Η αναζήτηση συνομιλιών δεν είναι διαθέσιμη. Δοκιμάστε ξανά.',
    historyUnreadable:
      'Η αναζήτηση συνομιλιών επέστρεψε μια μη αναγνώσιμη απόκριση. Δοκιμάστε ξανά.',
    search401: 'Συνδεθείτε ξανά για να κάνετε αναζήτηση στον χώρο εργασίας σας.',
    searchUnavailable: 'Η αναζήτηση στον χώρο εργασίας δεν είναι διαθέσιμη. Δοκιμάστε ξανά.',
    searchUnreadable:
      'Η αναζήτηση στον χώρο εργασίας επέστρεψε μια μη αναγνώσιμη απόκριση. Δοκιμάστε ξανά.',
    skillsUnavailable: 'Οι Δεξιότητες δεν είναι διαθέσιμες. Δοκιμάστε ξανά.',
    skillsUnreadable: 'Οι Δεξιότητες επέστρεψαν μια μη αναγνώσιμη απόκριση. Δοκιμάστε ξανά.',
    csvColumns:
      'Αυτό το αρχείο έχει περισσότερες από 200 στήλες. Κατεβάστε το αρχικό για να το ελέγξετε.',
    csvField:
      'Μια τιμή είναι πολύ μεγάλη για προεπισκόπηση. Κατεβάστε το αρχικό για να την ελέγξετε.',
    csvQuote:
      'Αυτό το CSV έχει μια μη ολοκληρωμένη τιμή σε εισαγωγικά. Κατεβάστε το αρχικό ή μεταφορτώστε ένα διορθωμένο αρχείο.',
    meetingRecording: 'Αυτή η σύσκεψη δεν έχει ακόμη διατηρημένη ηχογράφηση.',
    uploadInterrupted: 'Η μεταφόρτωση διακόπηκε. Δοκιμάστε ξανά.',
    uploadRefused: 'Η υπηρεσία αποθήκευσης απέρριψε το αρχείο. Δοκιμάστε ξανά.',
    downloadDeck: 'Δεν ήταν δυνατή η δημιουργία του αρχείου PowerPoint. Δοκιμάστε ξανά.',
    downloadFile: 'Δεν ήταν δυνατή η λήψη αυτού του αρχείου. Δοκιμάστε ξανά.',
    integrationsUnavailable: 'Δεν ήταν δυνατή η φόρτωση των ενσωματώσεων. Δοκιμάστε ξανά.',
    inboxActionUnavailable: 'Αυτή η ενέργεια δεν είναι διαθέσιμη.',
    inboxUnavailable: 'Δεν ήταν δυνατή η φόρτωση των εισερχομένων σας.',
    referencesUnavailable:
      'Ορισμένες επιλεγμένες αναφορές δεν είναι διαθέσιμες. Ελέγξτε την πρόσβασή σας πριν συνεχίσετε.',
    fileVerification: 'Η επαλήθευση του αρχείου απέτυχε.',
    uploadChecksum:
      'Αυτό το πρόγραμμα περιήγησης δεν μπορεί να επαληθεύσει μια μεγάλη μεταφόρτωση. Χρησιμοποιήστε ένα ενημερωμένο πρόγραμμα περιήγησης.',
    agentUnreadable: 'Το Poneva επέστρεψε μια απάντηση που αυτή η σελίδα δεν μπορούσε να διαβάσει.',
    office_unreadable:
      'Δεν ήταν δυνατή η ανάγνωση του {name}. Ανοίξτε το στο Office και αποθηκεύστε ένα νέο αντίγραφο .xlsx, .docx ή .pptx.',
    office_encrypted:
      'Το {name} προστατεύεται με κωδικό πρόσβασης. Αφαιρέστε τον κωδικό πρόσβασης και ανεβάστε το ξανά.',
    office_has_no_text:
      'Το {name} δεν περιέχει αναγνώσιμο κείμενο ή κελιά. Ανεβάστε μια έκδοση κειμένου ή ένα PDF με επιλέξιμο κείμενο.',
    office_too_complex:
      'Το {name} είναι υπερβολικά σύνθετο για ανάγνωση. Χωρίστε το σε μικρότερα αρχεία.',
    office_extraction_timeout:
      'Η ανάγνωση του {name} διήρκεσε υπερβολικά. Χωρίστε το σε μικρότερα αρχεία.',
    file_text_too_large:
      'Το {name} αποθηκεύτηκε για λήψη. Ανεβάστε ένα αντίγραφο μικρότερο από 50 MiB για να το διαβάσει το Poneva.',
    pdf_has_no_text:
      'Το {name} δεν περιέχει επιλέξιμο κείμενο, επομένως μπορεί να είναι σαρωμένη εικόνα. Επισυνάψτε ένα αντίγραφο έως 4 MB απευθείας σε μήνυμα συνομιλίας ή ανεβάστε μια έκδοση κειμένου.',
    pdf_encrypted:
      'Το {name} προστατεύεται με κωδικό πρόσβασης. Αφαιρέστε τον κωδικό πρόσβασης και ανεβάστε το ξανά.',
    pdf_unreadable: 'Δεν ήταν δυνατή η ανάγνωση του {name} ως PDF.',
    pdf_too_many_pages:
      'Το {name} περιέχει περισσότερες από 1.000 σελίδες. Χωρίστε το σε μικρότερα PDF.',
    pdf_too_complex:
      'Το {name} είναι υπερβολικά σύνθετο για ανάγνωση. Χωρίστε το σε μικρότερα PDF.',
    file_too_complex:
      'Το {name} έχει υπερβολικά βαθιά ένθεση ή είναι πολύ αργό στον έλεγχο. Απλοποιήστε τη δομή του ή χωρίστε το σε μικρότερα αρχεία.',
    pdf_extraction_timeout:
      'Η ανάγνωση του {name} διήρκεσε υπερβολικά. Χωρίστε το σε μικρότερα PDF και δοκιμάστε ξανά.',
    file_too_large: 'Το {name} είναι μεγαλύτερο από 500 MB.',
    invalid_workspace_input:
      'Δεν ήταν δυνατή η αποθήκευση του {name}. Ελέγξτε ότι το αρχείο αντιστοιχεί στον τύπο του (για παράδειγμα, έγκυρο JSON).',
    workspace_storage_full:
      'Η Βιβλιοθήκη της εταιρείας σας έχει φτάσει το όριο αποθηκευτικού χώρου, επομένως αυτό το αρχείο δεν αποθηκεύτηκε.',
    workspace_capacity_reached:
      'Η Βιβλιοθήκη της εταιρείας σας έχει φτάσει το όριο χωρητικότητας αρχείων, επομένως αυτό το αρχείο δεν αποθηκεύτηκε.',
    workspace_upload_busy:
      'Η επεξεργασία άλλων μεταφορτώσεων δεν έχει ολοκληρωθεί. Δοκιμάστε ξανά σε λίγο.',
    workspace_storage_unavailable:
      'Οι μεταφορτώσεις αρχείων δεν είναι προσωρινά διαθέσιμες. Επικοινωνήστε με την υποστήριξη του Poneva.',
    company_admin_required:
      'Μόνο οι ιδιοκτήτες της εταιρείας μπορούν να προσθέτουν αρχεία στη Βιβλιοθήκη.',
    uploadGeneric: 'Δεν ήταν δυνατή η μεταφόρτωση του {name}. Δοκιμάστε ξανά.',
    uploadTooLarge: 'Το {name} είναι μεγαλύτερο από 500 MB.',
    attachmentWorkspaceChanged: 'Ο χώρος εργασίας σας άλλαξε. Επιλέξτε ξανά την εικόνα.',
    attachmentSignInImage: 'Συνδεθείτε για να επισυνάψετε μια εικόνα.',
    attachmentImageSize:
      'Επιλέξτε μια εικόνα μικρότερη από 16 MiB ή αφαιρέστε την για να συνεχίσετε.',
    attachmentImageUnavailable: 'Η εικόνα δεν είναι διαθέσιμη. Αφαιρέστε την ή επιλέξτε την ξανά.',
    attachmentUploadImage:
      'Δεν ήταν δυνατή η μεταφόρτωση της εικόνας. Αφαιρέστε την ή επιλέξτε μια άλλη εικόνα.',
    attachmentFileChanged: 'Ο χώρος εργασίας σας άλλαξε. Επισυνάψτε ξανά το αρχείο.',
    attachmentSignInFile: 'Συνδεθείτε για να μεταφορτώσετε ένα εταιρικό αρχείο.',
    attachmentEmpty: 'Το {name} είναι κενό.',
    attachmentFileTypes: 'Επιλέξτε ένα αρχείο PDF, κειμένου, Excel, Word ή PowerPoint.',
    attachmentUploadFile: 'Δεν ήταν δυνατή η μεταφόρτωση αυτού του αρχείου.',
    attachmentReadable:
      'Αφαιρέστε το αποτυχημένο συνημμένο και μεταφορτώστε ένα αναγνώσιμο αντίγραφο.',
    repairWorkspaceChanged:
      'Ο χώρος εργασίας σας άλλαξε. Φορτώστε ξανά τη συνομιλία πριν από την επεξεργασία.',
    repairCannotSave:
      'Δεν είναι δυνατή η αποθήκευση αυτού του συνημμένου. Αφαιρέστε το και επιλέξτε ξανά το αρχείο.',
    repairOldUnavailable:
      'Αυτό το παλιό συνημμένο δεν είναι διαθέσιμο. Αφαιρέστε το ή επιλέξτε ξανά το αρχείο.',
    repairUpload: 'Δεν ήταν δυνατή η μεταφόρτωση του αρχείου. Αφαιρέστε το ή επιλέξτε το ξανά.',
    repairUnavailable: 'Το αρχείο δεν είναι διαθέσιμο. Αφαιρέστε το ή επιλέξτε το ξανά.',
    repairMessageUnavailable: 'Αυτό το μήνυμα δεν είναι διαθέσιμο. Φορτώστε ξανά τη συνομιλία.',
    repairChatChanged:
      'Αυτή η συνομιλία άλλαξε. Φορτώστε την ξανά πριν αποθηκεύσετε την επεξεργασία σας.',
    turnUnavailable:
      'Αυτός ο γύρος δεν είναι διαθέσιμος. Φορτώστε ξανά τη συνομιλία και στείλτε νέο μήνυμα.',
    imagePreparation:
      'Δεν ήταν δυνατή η προετοιμασία αυτής της εικόνας. Αφαιρέστε την και επιλέξτε μια άλλη εικόνα.',
    documentSize: 'Το {name} είναι μεγαλύτερο από 4 MB.',
    projectFileJoinFailed:
      'Το {name} βρίσκεται στα αρχεία σας, αλλά δεν μπόρεσε να προστεθεί στο έργο. Προσθέστε το από το έργο.',
  },
  hu: {
    uploadRule:
      'Excel-fájlokat (.xlsx), Word-fájlokat (.docx), PowerPoint-fájlokat (.pptx), PDF-, CSV- és TSV-fájlokat, szöveget, JSON-fájlokat, átiratokat, hang- vagy videofájlokat tölthet fel legfeljebb 500 MB méretben. A régebbi Office-fájlokat először mentse .xlsx, .docx vagy .pptx formátumban.',
    emptyFile: 'A(z) {name} üres. Válasszon tartalommal rendelkező fájlt.',
    uploadPart: 'Egy fájlrész feltöltése nem sikerült. Próbálja újra.',
    uploadEtag: 'A tárhelyszolgáltatás nem erősítette meg a fájlrészt. Próbálja újra.',
    uploadGrant: 'A feltöltési munkamenethez nem tartozik tárhely-hozzáférés.',
    uploadParts: 'A feltöltési munkamenet hiányos fájlrész-hozzáféréseket adott vissza.',
    browserChecksum:
      'Ez a böngésző nem tudja ellenőrizni a nagy méretű feltöltést. Használjon naprakész böngészőt.',
    history401: 'Jelentkezzen be újra a beszélgetések kereséséhez.',
    history403: 'A beszélgetések kereséséhez vállalati meghívó szükséges.',
    historyUnavailable: 'A beszélgetéskeresés nem érhető el. Próbálja újra.',
    historyUnreadable: 'A beszélgetéskeresés olvashatatlan választ adott vissza. Próbálja újra.',
    search401: 'Jelentkezzen be újra a munkaterület kereséséhez.',
    searchUnavailable: 'A munkaterület keresése nem érhető el. Próbálja újra.',
    searchUnreadable: 'A munkaterület keresése olvashatatlan választ adott vissza. Próbálja újra.',
    skillsUnavailable: 'A készségek nem érhetők el. Próbálja újra.',
    skillsUnreadable: 'A készségek olvashatatlan választ adtak vissza. Próbálja újra.',
    csvColumns:
      'Ez a fájl több mint 200 oszlopot tartalmaz. Az eredeti letöltésével megtekintheti.',
    csvField: 'Egy érték túl nagy az előnézethez. Az eredeti letöltésével megtekintheti.',
    csvQuote:
      'Ez a CSV-fájl befejezetlen idézőjeles értéket tartalmaz. Töltse le az eredetit, vagy töltsön fel javított fájlt.',
    meetingRecording: 'Ehhez a megbeszéléshez még nincs megőrzött felvétel.',
    uploadInterrupted: 'A feltöltés megszakadt. Próbálja újra.',
    uploadRefused: 'A tárhelyszolgáltatás elutasította a fájlt. Próbálja újra.',
    downloadDeck: 'Nem sikerült létrehozni a PowerPoint-fájlt. Próbálja újra.',
    downloadFile: 'Nem sikerült letölteni ezt a fájlt. Próbálja újra.',
    integrationsUnavailable: 'Nem sikerült betölteni az integrációkat. Próbálja újra.',
    inboxActionUnavailable: 'Ez a művelet nem érhető el.',
    inboxUnavailable: 'Nem sikerült betölteni a beérkező leveleket.',
    referencesUnavailable:
      'Néhány kiválasztott hivatkozás nem érhető el. Folytatás előtt ellenőrizze a hozzáférésüket.',
    fileVerification: 'A fájl ellenőrzése sikertelen.',
    uploadChecksum:
      'Ez a böngésző nem tud ellenőrizni egy nagy méretű feltöltést. Használjon naprakész böngészőt.',
    agentUnreadable: 'A Poneva olyan választ adott, amelyet ez az oldal nem tudott beolvasni.',
    office_unreadable:
      'A(z) {name} nem olvasható. Nyissa meg az Office-ban, és mentsen egy új .xlsx-, .docx- vagy .pptx-példányt.',
    office_encrypted: 'A(z) {name} jelszóval védett. Távolítsa el a jelszót, majd töltse fel újra.',
    office_has_no_text:
      'A(z) {name} nem tartalmaz olvasható szöveget vagy cellákat. Töltsön fel szöveges változatot vagy kijelölhető szöveget tartalmazó PDF-et.',
    office_too_complex: 'A(z) {name} túl összetett az olvasáshoz. Bontsa kisebb fájlokra.',
    office_extraction_timeout: 'A(z) {name} olvasása túl sokáig tartott. Bontsa kisebb fájlokra.',
    file_text_too_large:
      'A(z) {name} letölthetőként lett mentve. Töltsön fel egy 50 MiB-nál kisebb példányt, hogy a Poneva elolvashassa.',
    pdf_has_no_text:
      'A(z) {name} nem tartalmaz kijelölhető szöveget, ezért lehet, hogy beolvasott kép. Csatoljon közvetlenül egy legfeljebb 4 MB-os példányt egy csevegőüzenethez, vagy töltsön fel szöveges változatot.',
    pdf_encrypted: 'A(z) {name} jelszóval védett. Távolítsa el a jelszót, majd töltse fel újra.',
    pdf_unreadable: 'A(z) {name} PDF-ként nem olvasható.',
    pdf_too_many_pages: 'A(z) {name} több mint 1000 oldalt tartalmaz. Bontsa kisebb PDF-ekre.',
    pdf_too_complex: 'A(z) {name} túl összetett az olvasáshoz. Bontsa kisebb PDF-ekre.',
    file_too_complex:
      'A(z) {name} túl mélyen beágyazott vagy túl lassan ellenőrizhető. Egyszerűsítse a struktúráját, vagy bontsa kisebb fájlokra.',
    pdf_extraction_timeout:
      'A(z) {name} olvasása túl sokáig tartott. Bontsa kisebb PDF-ekre, majd próbálja újra.',
    file_too_large: 'A(z) {name} nagyobb 500 MB-nál.',
    invalid_workspace_input:
      'A(z) {name} nem menthető. Ellenőrizze, hogy a fájl megfelel-e a típusának (például érvényes JSON-e).',
    workspace_storage_full:
      'A vállalati könyvtár elérte a tárhelykorlátját, ezért ez a fájl nem lett mentve.',
    workspace_capacity_reached:
      'A vállalati könyvtár elérte a fájlkapacitását, ezért ez a fájl nem lett mentve.',
    workspace_upload_busy:
      'Más feltöltések feldolgozása még folyamatban van. Próbálja újra egy pillanat múlva.',
    workspace_storage_unavailable:
      'A fájlfeltöltés jelenleg nem érhető el. Forduljon a Poneva ügyfélszolgálatához.',
    company_admin_required: 'Csak a vállalat tulajdonosai adhatnak hozzá fájlokat a könyvtárhoz.',
    uploadGeneric: 'A(z) {name} feltöltése nem sikerült. Próbálja újra.',
    uploadTooLarge: 'A(z) {name} nagyobb 500 MB-nál.',
    attachmentWorkspaceChanged: 'Megváltozott a munkaterülete. Válassza ki újra a képet.',
    attachmentSignInImage: 'Jelentkezzen be kép csatolásához.',
    attachmentImageSize:
      '16 MiB-nál kisebb képet válasszon, vagy távolítsa el a képet a folytatáshoz.',
    attachmentImageUnavailable: 'A kép nem érhető el. Távolítsa el, vagy válassza ki újra.',
    attachmentUploadImage:
      'A képet nem sikerült feltölteni. Távolítsa el, vagy válasszon másik képet.',
    attachmentFileChanged: 'Megváltozott a munkaterülete. Csatolja újra a fájlt.',
    attachmentSignInFile: 'Jelentkezzen be üzleti fájl feltöltéséhez.',
    attachmentEmpty: 'A(z) {name} üres.',
    attachmentFileTypes: 'PDF-, szöveges, Excel-, Word- vagy PowerPoint-fájlt válasszon.',
    attachmentUploadFile: 'Ezt a fájlt nem sikerült feltölteni.',
    attachmentReadable:
      'Távolítsa el a sikertelen csatolmányt, és töltsön fel olvasható másolatot.',
    repairWorkspaceChanged:
      'Megváltozott a munkaterülete. Szerkesztés előtt töltse újra a beszélgetést.',
    repairCannotSave: 'Ez a csatolmány nem menthető. Távolítsa el, és válassza ki újra a fájlt.',
    repairOldUnavailable:
      'Ez a régi csatolmány nem érhető el. Távolítsa el, vagy válassza ki újra a fájlt.',
    repairUpload: 'A fájlt nem sikerült feltölteni. Távolítsa el, vagy válassza ki újra.',
    repairUnavailable: 'A fájl nem érhető el. Távolítsa el, vagy válassza ki újra.',
    repairMessageUnavailable: 'Ez az üzenet nem érhető el. Töltse újra a beszélgetést.',
    repairChatChanged: 'A beszélgetés megváltozott. A szerkesztés mentése előtt töltse újra.',
    turnUnavailable:
      'Ez a beszélgetési kör nem érhető el. Töltse újra a beszélgetést, és küldjön új üzenetet.',
    imagePreparation:
      'Ezt a képet nem sikerült előkészíteni. Távolítsa el, és válasszon másik képet.',
    documentSize: 'A(z) {name} mérete meghaladja a 4 MB-ot.',
    projectFileJoinFailed:
      'A(z) {name} szerepel a fájljai között, de nem sikerült hozzáadni a projekthez. Adja hozzá a projektből.',
  },
  ga: {
    uploadRule:
      'Bain úsáid as Excel (.xlsx), Word (.docx), PowerPoint (.pptx), PDF, CSV, TSV, téacs, JSON, tras-scríbhinní, fuaim nó físeán suas le 500 MB. Sábháil comhaid Office níos sine mar .xlsx, .docx nó .pptx ar dtús.',
    emptyFile: 'Tá {name} folamh. Roghnaigh comhad ina bhfuil ábhar.',
    uploadPart: 'Níorbh fhéidir cuid den chomhad a uaslódáil. Bain triail eile as.',
    uploadEtag: 'Níor dheimhnigh an tseirbhís stórála cuid den chomhad. Bain triail eile as.',
    uploadGrant: 'Tá deontas stórála an tseisiúin uaslódála ar iarraidh.',
    uploadParts: 'Thug an seisiún uaslódála deontais neamhiomlána do na codanna.',
    browserChecksum:
      'Ní féidir leis an mbrabhsálaí seo uaslódáil mhór a fhíorú. Bain úsáid as brabhsálaí reatha.',
    history401: 'Sínigh isteach arís chun do chomhráite a chuardach.',
    history403: 'Tá cuireadh cuideachta riachtanach chun comhráite a chuardach.',
    historyUnavailable: 'Níl cuardach comhráite ar fáil. Bain triail eile as.',
    historyUnreadable:
      'Fuarthas freagra nach raibh inléite ó chuardach na gcomhráite. Bain triail eile as.',
    search401: 'Sínigh isteach arís chun do spás oibre a chuardach.',
    searchUnavailable: 'Níl cuardach an spáis oibre ar fáil. Bain triail eile as.',
    searchUnreadable:
      'Fuarthas freagra nach raibh inléite ó chuardach an spáis oibre. Bain triail eile as.',
    skillsUnavailable: 'Níl Scileanna ar fáil. Bain triail eile as.',
    skillsUnreadable: 'Thug Scileanna freagra nach raibh inléite. Bain triail eile as.',
    csvColumns: 'Tá níos mó ná 200 colún sa chomhad seo. Íoslódáil an bunleagan chun é a scrúdú.',
    csvField:
      'Tá luach rómhór le réamhamharc a dhéanamh air. Íoslódáil an bunleagan chun é a scrúdú.',
    csvQuote:
      'Tá luach athfhriotail neamhchríochnaithe sa CSV seo. Íoslódáil an bunleagan nó uaslódáil comhad ceartaithe.',
    meetingRecording: 'Níl taifeadadh coinnithe den chruinniú seo ann fós.',
    uploadInterrupted: 'Cuireadh isteach ar an uaslódáil. Bain triail eile as.',
    uploadRefused: 'Dhiúltaigh an tseirbhís stórála don chomhad. Bain triail eile as.',
    downloadDeck: 'Níorbh fhéidir linn an comhad PowerPoint a chruthú. Bain triail eile as.',
    downloadFile: 'Níorbh fhéidir linn an comhad seo a íoslódáil. Bain triail eile as.',
    integrationsUnavailable: 'Níorbh fhéidir linn na comhtháthuithe a luchtú. Bain triail eile as.',
    inboxActionUnavailable: 'Níl an gníomh seo ar fáil.',
    inboxUnavailable: 'Níorbh fhéidir linn do Bhosca Isteach a luchtú.',
    referencesUnavailable:
      'Níl cuid de na tagairtí roghnaithe ar fáil. Seiceáil a rochtain sula leanann tú ar aghaidh.',
    fileVerification: 'Theip ar fhíorú an chomhaid.',
    uploadChecksum:
      'Ní féidir leis an mbrabhsálaí seo uaslódáil mhór a fhíorú. Úsáid brabhsálaí reatha.',
    agentUnreadable: 'D’fhill Poneva freagra nach raibh an leathanach seo in ann a léamh.',
    office_unreadable:
      'Níorbh fhéidir {name} a léamh. Oscail in Office é agus sábháil cóip nua .xlsx, .docx nó .pptx.',
    office_encrypted: 'Tá {name} cosanta ag pasfhocal. Bain an pasfhocal agus uaslódáil arís é.',
    office_has_no_text:
      'Níl téacs ná cealla inléite ar bith in {name}. Uaslódáil leagan téacs, nó PDF le téacs is féidir a roghnú.',
    office_too_complex: 'Tá {name} róchasta le léamh. Roinn i gcomhaid níos lú é.',
    office_extraction_timeout: 'Thóg sé rófhada {name} a léamh. Roinn i gcomhaid níos lú é.',
    file_text_too_large:
      'Tá {name} sábháilte le híoslódáil. Uaslódáil cóip faoi bhun 50 MiB lena léamh ag Poneva.',
    pdf_has_no_text:
      'Níl téacs is féidir a roghnú in {name}, mar sin d’fhéadfadh gur íomhá scanta atá ann. Ceangail cóip suas le 4 MB go díreach le teachtaireacht chomhrá, nó uaslódáil leagan téacs.',
    pdf_encrypted: 'Tá {name} cosanta ag pasfhocal. Bain an pasfhocal agus uaslódáil arís é.',
    pdf_unreadable: 'Níorbh fhéidir {name} a léamh mar PDF.',
    pdf_too_many_pages: 'Tá níos mó ná 1,000 leathanach in {name}. Roinn i gcomhaid PDF níos lú é.',
    pdf_too_complex: 'Tá {name} róchasta le léamh. Roinn i gcomhaid PDF níos lú é.',
    file_too_complex:
      'Tá {name} neadaithe ró dhomhain nó ró-mhall le seiceáil. Simpligh a struchtúr nó roinn i gcomhaid níos lú é.',
    pdf_extraction_timeout:
      'Thóg sé rófhada {name} a léamh. Roinn i gcomhaid PDF níos lú é agus bain triail eile as.',
    file_too_large: 'Tá {name} níos mó ná 500 MB.',
    invalid_workspace_input:
      'Níorbh fhéidir {name} a shábháil. Seiceáil go bhfuil an comhad comhoiriúnach lena chineál (JSON bailí, mar shampla).',
    workspace_storage_full:
      'Tá teorainn stórála Leabharlann do chuideachta bainte amach, mar sin níor sábháladh an comhad seo.',
    workspace_capacity_reached:
      'Tá teorainn chomhaid Leabharlann do chuideachta bainte amach, mar sin níor sábháladh an comhad seo.',
    workspace_upload_busy:
      'Tá uaslódálacha eile fós á bpróiseáil. Bain triail eile as i gceann nóiméid.',
    workspace_storage_unavailable:
      'Níl uaslódálacha comhad ar fáil faoi láthair. Déan teagmháil le tacaíocht Poneva.',
    company_admin_required:
      'Ní féidir ach le húinéirí na cuideachta comhaid a chur leis an Leabharlann.',
    uploadGeneric: 'Níorbh fhéidir {name} a uaslódáil. Bain triail eile as.',
    uploadTooLarge: 'Tá {name} níos mó ná 500 MB.',
    attachmentWorkspaceChanged: "D'athraigh do spás oibre. Roghnaigh an íomhá arís.",
    attachmentSignInImage: 'Sínigh isteach chun íomhá a cheangal.',
    attachmentImageSize:
      'Roghnaigh íomhá atá faoi bhun 16 MiB, nó bain di chun leanúint ar aghaidh.',
    attachmentImageUnavailable: 'Níl an íomhá ar fáil. Bain di nó roghnaigh arís í.',
    attachmentUploadImage: 'Níorbh fhéidir an íomhá a uaslódáil. Bain di nó roghnaigh íomhá eile.',
    attachmentFileChanged: "D'athraigh do spás oibre. Ceangail an comhad arís.",
    attachmentSignInFile: 'Sínigh isteach chun comhad gnó a uaslódáil.',
    attachmentEmpty: 'Tá {name} folamh.',
    attachmentFileTypes: 'Roghnaigh comhad PDF, téacs, Excel, Word nó PowerPoint.',
    attachmentUploadFile: 'Níorbh fhéidir an comhad seo a uaslódáil.',
    attachmentReadable: 'Bain an ceangaltán ar theip air agus uaslódáil cóip inléite.',
    repairWorkspaceChanged:
      "D'athraigh do spás oibre. Athlódáil an comhrá sula gcuirfidh tú in eagar é.",
    repairCannotSave:
      'Ní féidir an ceangaltán seo a shábháil. Bain de agus roghnaigh an comhad arís.',
    repairOldUnavailable:
      'Níl an seancheangaltán seo ar fáil. Bain de nó roghnaigh an comhad arís.',
    repairUpload: 'Níorbh fhéidir an comhad a uaslódáil. Bain de nó roghnaigh arís é.',
    repairUnavailable: 'Níl an comhad ar fáil. Bain de nó roghnaigh arís é.',
    repairMessageUnavailable: 'Níl an teachtaireacht seo ar fáil. Athlódáil an comhrá.',
    repairChatChanged: "D'athraigh an comhrá seo. Athlódáil é sula sábhálfaidh tú d'eagar.",
    turnUnavailable: 'Níl an babhta seo ar fáil. Athlódáil an comhrá agus seol teachtaireacht nua.',
    imagePreparation: 'Níorbh fhéidir an íomhá seo a ullmhú. Bain di agus roghnaigh íomhá eile.',
    documentSize: 'Tá {name} níos mó ná 4 MB.',
    projectFileJoinFailed:
      'Tá {name} i do chuid comhad, ach níorbh fhéidir é a chur leis an tionscadal. Cuir leis ón tionscadal é.',
  },
  it: {
    uploadRule:
      'Usa file Excel (.xlsx), Word (.docx), PowerPoint (.pptx), PDF, CSV, TSV, di testo, JSON, trascrizioni, audio o video fino a 500 MB. Salva prima i file Office meno recenti come .xlsx, .docx o .pptx.',
    emptyFile: '{name} è vuoto. Scegli un file con contenuti.',
    uploadPart: 'Non è stato possibile caricare una parte del file. Riprova.',
    uploadEtag: 'Il servizio di archiviazione non ha confermato una parte del file. Riprova.',
    uploadGrant: "Nella sessione di caricamento manca l'autorizzazione per l'archiviazione.",
    uploadParts: 'La sessione di caricamento ha restituito autorizzazioni incomplete per le parti.',
    browserChecksum:
      'Questo browser non può verificare un caricamento di grandi dimensioni. Usa un browser aggiornato.',
    history401: 'Accedi di nuovo per cercare nelle tue conversazioni.',
    history403: 'Per cercare nelle conversazioni è necessario un invito aziendale.',
    historyUnavailable: 'La ricerca nelle conversazioni non è disponibile. Riprova.',
    historyUnreadable:
      'La ricerca nelle conversazioni ha restituito una risposta illeggibile. Riprova.',
    search401: 'Accedi di nuovo per cercare nel tuo spazio di lavoro.',
    searchUnavailable: 'La ricerca nello spazio di lavoro non è disponibile. Riprova.',
    searchUnreadable:
      'La ricerca nello spazio di lavoro ha restituito una risposta illeggibile. Riprova.',
    skillsUnavailable: 'Le competenze non sono disponibili. Riprova.',
    skillsUnreadable: 'Le competenze hanno restituito una risposta illeggibile. Riprova.',
    csvColumns: "Questo file contiene più di 200 colonne. Scarica l'originale per esaminarlo.",
    csvField: "Un valore è troppo grande per l'anteprima. Scarica l'originale per esaminarlo.",
    csvQuote:
      "Questo CSV contiene un valore tra virgolette non terminato. Scarica l'originale o carica un file corretto.",
    meetingRecording: 'Questa riunione non ha ancora una registrazione conservata.',
    uploadInterrupted: 'Il caricamento è stato interrotto. Riprova.',
    uploadRefused: 'Il servizio di archiviazione ha rifiutato il file. Riprova.',
    downloadDeck: 'Non è stato possibile creare il file PowerPoint. Riprova.',
    downloadFile: 'Non è stato possibile scaricare questo file. Riprova.',
    integrationsUnavailable: 'Non è stato possibile caricare le integrazioni. Riprova.',
    inboxActionUnavailable: 'Questa azione non è disponibile.',
    inboxUnavailable: 'Non è stato possibile caricare la posta in arrivo.',
    referencesUnavailable:
      'Alcuni riferimenti selezionati non sono disponibili. Verifica il loro accesso prima di continuare.',
    fileVerification: 'Verifica del file non riuscita.',
    uploadChecksum:
      'Questo browser non può verificare un caricamento di grandi dimensioni. Usa un browser aggiornato.',
    agentUnreadable: 'Poneva ha restituito una risposta che questa pagina non ha potuto leggere.',
    office_unreadable:
      'Non è stato possibile leggere {name}. Aprilo in Office e salva una nuova copia .xlsx, .docx o .pptx.',
    office_encrypted: '{name} è protetto da password. Rimuovi la password e caricalo di nuovo.',
    office_has_no_text:
      '{name} non contiene testo o celle leggibili. Carica una versione testuale oppure un PDF con testo selezionabile.',
    office_too_complex: '{name} è troppo complesso da leggere. Dividilo in file più piccoli.',
    office_extraction_timeout:
      'La lettura di {name} ha richiesto troppo tempo. Dividilo in file più piccoli.',
    file_text_too_large:
      '{name} è stato salvato per il download. Carica una copia inferiore a 50 MiB affinché Poneva possa leggerlo.',
    pdf_has_no_text:
      "{name} non contiene testo selezionabile, quindi potrebbe essere un'immagine scansionata. Allega una copia fino a 4 MB direttamente a un messaggio della chat oppure carica una versione testuale.",
    pdf_encrypted: '{name} è protetto da password. Rimuovi la password e caricalo di nuovo.',
    pdf_unreadable: 'Non è stato possibile leggere {name} come PDF.',
    pdf_too_many_pages: '{name} contiene più di 1.000 pagine. Dividilo in PDF più piccoli.',
    pdf_too_complex: '{name} è troppo complesso da leggere. Dividilo in PDF più piccoli.',
    file_too_complex:
      '{name} è annidato troppo in profondità o è troppo lento da controllare. Semplificane la struttura oppure dividilo in file più piccoli.',
    pdf_extraction_timeout:
      'La lettura di {name} ha richiesto troppo tempo. Dividilo in PDF più piccoli e riprova.',
    file_too_large: '{name} supera i 500 MB.',
    invalid_workspace_input:
      'Non è stato possibile salvare {name}. Verifica che il file corrisponda al tipo dichiarato (ad esempio, che sia un JSON valido).',
    workspace_storage_full:
      'La Libreria della tua azienda ha raggiunto il limite di spazio, quindi questo file non è stato salvato.',
    workspace_capacity_reached:
      'La Libreria della tua azienda ha raggiunto il limite di file, quindi questo file non è stato salvato.',
    workspace_upload_busy: 'Altri caricamenti sono ancora in corso. Riprova tra poco.',
    workspace_storage_unavailable:
      "I caricamenti di file non sono al momento disponibili. Contatta l'assistenza di Poneva.",
    company_admin_required:
      "Solo i proprietari dell'azienda possono aggiungere file alla Libreria.",
    uploadGeneric: 'Non è stato possibile caricare {name}. Riprova.',
    uploadTooLarge: '{name} supera i 500 MB.',
    attachmentWorkspaceChanged: "Il tuo spazio di lavoro è cambiato. Scegli di nuovo l'immagine.",
    attachmentSignInImage: "Accedi per allegare un'immagine.",
    attachmentImageSize:
      "Scegli un'immagine di dimensioni inferiori a 16 MiB oppure rimuovila per continuare.",
    attachmentImageUnavailable: "L'immagine non è disponibile. Rimuovila o sceglila di nuovo.",
    attachmentUploadImage:
      "Non è stato possibile caricare l'immagine. Rimuovila o scegline un'altra.",
    attachmentFileChanged: 'Il tuo spazio di lavoro è cambiato. Allega di nuovo il file.',
    attachmentSignInFile: 'Accedi per caricare un file aziendale.',
    attachmentEmpty: '{name} è vuoto.',
    attachmentFileTypes: 'Scegli un file PDF, di testo, Excel, Word o PowerPoint.',
    attachmentUploadFile: 'Non è stato possibile caricare questo file.',
    attachmentReadable: "Rimuovi l'allegato non riuscito e carica una copia leggibile.",
    repairWorkspaceChanged:
      'Il tuo spazio di lavoro è cambiato. Ricarica la chat prima di modificare.',
    repairCannotSave:
      'Non è possibile salvare questo allegato. Rimuovilo e scegli di nuovo il file.',
    repairOldUnavailable:
      'Questo vecchio allegato non è disponibile. Rimuovilo o scegli di nuovo il file.',
    repairUpload: 'Non è stato possibile caricare il file. Rimuovilo o sceglilo di nuovo.',
    repairUnavailable: 'Il file non è disponibile. Rimuovilo o sceglilo di nuovo.',
    repairMessageUnavailable: 'Questo messaggio non è disponibile. Ricarica la chat.',
    repairChatChanged: 'Questa chat è cambiata. Ricaricala prima di salvare la modifica.',
    turnUnavailable: 'Questo turno non è disponibile. Ricarica la chat e invia un nuovo messaggio.',
    imagePreparation:
      "Non è stato possibile preparare questa immagine. Rimuovila e scegline un'altra.",
    documentSize: '{name} è più grande di 4 MB.',
    projectFileJoinFailed:
      '{name} è nei tuoi file, ma non è stato possibile aggiungerlo al progetto. Aggiungilo dal progetto.',
  },
  lv: {
    uploadRule:
      'Izmantojiet Excel (.xlsx), Word (.docx), PowerPoint (.pptx), PDF, CSV, TSV, teksta failus, JSON, transkriptus, audio vai video failus līdz 500 MB. Vispirms saglabājiet vecākus Office failus kā .xlsx, .docx vai .pptx.',
    emptyFile: '{name} ir tukšs. Izvēlieties failu ar saturu.',
    uploadPart: 'Neizdevās augšupielādēt faila daļu. Mēģiniet vēlreiz.',
    uploadEtag: 'Krātuves pakalpojums neapstiprināja faila daļu. Mēģiniet vēlreiz.',
    uploadGrant: 'Augšupielādes sesijai trūkst krātuves piešķīruma.',
    uploadParts: 'Augšupielādes sesija atgrieza nepilnīgus daļu piešķīrumus.',
    browserChecksum:
      'Šī pārlūkprogramma nevar pārbaudīt lielu augšupielādi. Izmantojiet jaunāko pārlūkprogrammas versiju.',
    history401: 'Pierakstieties vēlreiz, lai meklētu savās sarunās.',
    history403: 'Lai meklētu sarunās, nepieciešams uzņēmuma uzaicinājums.',
    historyUnavailable: 'Sarunu meklēšana nav pieejama. Mēģiniet vēlreiz.',
    historyUnreadable: 'Sarunu meklēšana atgrieza nelasāmu atbildi. Mēģiniet vēlreiz.',
    search401: 'Pierakstieties vēlreiz, lai meklētu savā darbvietā.',
    searchUnavailable: 'Darbvietas meklēšana nav pieejama. Mēģiniet vēlreiz.',
    searchUnreadable: 'Darbvietas meklēšana atgrieza nelasāmu atbildi. Mēģiniet vēlreiz.',
    skillsUnavailable: 'Prasmes nav pieejamas. Mēģiniet vēlreiz.',
    skillsUnreadable: 'Prasmju funkcija atgrieza nelasāmu atbildi. Mēģiniet vēlreiz.',
    csvColumns:
      'Šajā failā ir vairāk nekā 200 kolonnu. Lejupielādējiet oriģinālu, lai to pārbaudītu.',
    csvField:
      'Vērtība ir pārāk liela priekšskatīšanai. Lejupielādējiet oriģinālu, lai to pārbaudītu.',
    csvQuote:
      'Šajā CSV failā ir nepabeigta vērtība pēdiņās. Lejupielādējiet oriģinālu vai augšupielādējiet izlabotu failu.',
    meetingRecording: 'Šai sapulcei vēl nav saglabāta ieraksta.',
    uploadInterrupted: 'Augšupielāde tika pārtraukta. Mēģiniet vēlreiz.',
    uploadRefused: 'Krātuves pakalpojums noraidīja failu. Mēģiniet vēlreiz.',
    downloadDeck: 'Neizdevās izveidot PowerPoint failu. Lūdzu, mēģiniet vēlreiz.',
    downloadFile: 'Neizdevās lejupielādēt šo failu. Lūdzu, mēģiniet vēlreiz.',
    integrationsUnavailable: 'Neizdevās ielādēt integrācijas. Lūdzu, mēģiniet vēlreiz.',
    inboxActionUnavailable: 'Šī darbība nav pieejama.',
    inboxUnavailable: 'Neizdevās ielādēt jūsu iesūtni.',
    referencesUnavailable:
      'Dažas atlasītās atsauces nav pieejamas. Pirms turpināšanas pārbaudiet piekļuvi tām.',
    fileVerification: 'Faila pārbaude neizdevās.',
    uploadChecksum:
      'Šī pārlūkprogramma nevar pārbaudīt lielu augšupielādi. Izmantojiet jaunāku pārlūkprogrammu.',
    agentUnreadable: 'Poneva sniedza atbildi, ko šī lapa nevarēja nolasīt.',
    office_unreadable:
      'Neizdevās nolasīt {name}. Atveriet to programmā Office un saglabājiet jaunu .xlsx, .docx vai .pptx kopiju.',
    office_encrypted:
      '{name} ir aizsargāts ar paroli. Noņemiet paroli un augšupielādējiet to vēlreiz.',
    office_has_no_text:
      '{name} nav nolasāma teksta vai šūnu. Augšupielādējiet teksta versiju vai PDF failu ar atlasāmu tekstu.',
    office_too_complex: '{name} ir pārāk sarežģīts, lai to nolasītu. Sadaliet to mazākos failos.',
    office_extraction_timeout:
      '{name} nolasīšana aizņēma pārāk daudz laika. Sadaliet to mazākos failos.',
    file_text_too_large:
      '{name} ir saglabāts lejupielādei. Augšupielādējiet kopiju, kas ir mazāka par 50 MiB, lai Poneva varētu to nolasīt.',
    pdf_has_no_text:
      '{name} nav atlasāma teksta, tāpēc tas var būt skenēts attēls. Pievienojiet kopiju līdz 4 MB tieši tērzēšanas ziņai vai augšupielādējiet teksta versiju.',
    pdf_encrypted:
      '{name} ir aizsargāts ar paroli. Noņemiet paroli un augšupielādējiet to vēlreiz.',
    pdf_unreadable: 'Neizdevās nolasīt {name} kā PDF failu.',
    pdf_too_many_pages: '{name} ir vairāk nekā 1 000 lappušu. Sadaliet to mazākos PDF failos.',
    pdf_too_complex: '{name} ir pārāk sarežģīts, lai to nolasītu. Sadaliet to mazākos PDF failos.',
    file_too_complex:
      '{name} ir pārāk dziļi ligzdots vai tā pārbaude aizņem pārāk daudz laika. Vienkāršojiet tā struktūru vai sadaliet to mazākos failos.',
    pdf_extraction_timeout:
      '{name} nolasīšana aizņēma pārāk daudz laika. Sadaliet to mazākos PDF failos un mēģiniet vēlreiz.',
    file_too_large: '{name} ir lielāks par 500 MB.',
    invalid_workspace_input:
      'Neizdevās saglabāt {name}. Pārbaudiet, vai fails atbilst savam tipam (piemēram, vai tas ir derīgs JSON).',
    workspace_storage_full:
      'Jūsu uzņēmuma bibliotēka ir sasniegusi krātuves limitu, tāpēc šis fails netika saglabāts.',
    workspace_capacity_reached:
      'Jūsu uzņēmuma bibliotēka ir sasniegusi failu skaita limitu, tāpēc šis fails netika saglabāts.',
    workspace_upload_busy:
      'Citi augšupielādes procesi vēl tiek apstrādāti. Mēģiniet vēlreiz pēc brīža.',
    workspace_storage_unavailable:
      'Failu augšupielāde pašlaik nav pieejama. Sazinieties ar Poneva atbalsta dienestu.',
    company_admin_required: 'Tikai uzņēmuma īpašnieki var pievienot failus bibliotēkai.',
    uploadGeneric: 'Neizdevās augšupielādēt {name}. Mēģiniet vēlreiz.',
    uploadTooLarge: '{name} ir lielāks par 500 MB.',
    attachmentWorkspaceChanged: 'Jūsu darbvieta ir mainīta. Izvēlieties attēlu vēlreiz.',
    attachmentSignInImage: 'Piesakieties, lai pievienotu attēlu.',
    attachmentImageSize:
      'Izvēlieties attēlu, kas mazāks par 16 MiB, vai noņemiet to, lai turpinātu.',
    attachmentImageUnavailable: 'Attēls nav pieejams. Noņemiet to vai izvēlieties vēlreiz.',
    attachmentUploadImage:
      'Attēlu neizdevās augšupielādēt. Noņemiet to vai izvēlieties citu attēlu.',
    attachmentFileChanged: 'Jūsu darbvieta ir mainīta. Pievienojiet failu vēlreiz.',
    attachmentSignInFile: 'Piesakieties, lai augšupielādētu darba failu.',
    attachmentEmpty: '{name} ir tukšs.',
    attachmentFileTypes: 'Izvēlieties PDF, teksta, Excel, Word vai PowerPoint failu.',
    attachmentUploadFile: 'Šo failu neizdevās augšupielādēt.',
    attachmentReadable: 'Noņemiet neizdevušos pielikumu un augšupielādējiet lasāmu kopiju.',
    repairWorkspaceChanged:
      'Jūsu darbvieta ir mainīta. Pirms rediģēšanas atkārtoti ielādējiet tērzēšanu.',
    repairCannotSave: 'Šo pielikumu nevar saglabāt. Noņemiet to un izvēlieties failu vēlreiz.',
    repairOldUnavailable:
      'Šis vecais pielikums nav pieejams. Noņemiet to vai izvēlieties failu vēlreiz.',
    repairUpload: 'Failu neizdevās augšupielādēt. Noņemiet to vai izvēlieties vēlreiz.',
    repairUnavailable: 'Fails nav pieejams. Noņemiet to vai izvēlieties vēlreiz.',
    repairMessageUnavailable: 'Šis ziņojums nav pieejams. Atkārtoti ielādējiet tērzēšanu.',
    repairChatChanged:
      'Šī tērzēšana ir mainīta. Pirms labojuma saglabāšanas atkārtoti ielādējiet to.',
    turnUnavailable:
      'Šis gājiens nav pieejams. Atkārtoti ielādējiet tērzēšanu un nosūtiet jaunu ziņojumu.',
    imagePreparation: 'Šo attēlu neizdevās sagatavot. Noņemiet to un izvēlieties citu attēlu.',
    documentSize: '{name} ir lielāks par 4 MB.',
    projectFileJoinFailed:
      '{name} ir jūsu failos, bet to neizdevās pievienot projektam. Pievienojiet to no projekta.',
  },
  lt: {
    uploadRule:
      'Naudokite „Excel“ (.xlsx), „Word“ (.docx), „PowerPoint“ (.pptx), PDF, CSV, TSV, tekstinius failus, JSON, stenogramas, garso arba vaizdo įrašus iki 500 MB. Senesnius „Office“ failus pirmiausia išsaugokite kaip .xlsx, .docx arba .pptx.',
    emptyFile: '{name} yra tuščias. Pasirinkite failą su turiniu.',
    uploadPart: 'Nepavyko įkelti failo dalies. Bandykite dar kartą.',
    uploadEtag: 'Saugyklos paslauga nepatvirtino failo dalies. Bandykite dar kartą.',
    uploadGrant: 'Įkėlimo seansui trūksta saugyklos leidimo.',
    uploadParts: 'Įkėlimo seansas grąžino ne visas dalių prieigos teises.',
    browserChecksum: 'Ši naršyklė negali patikrinti didelio įkėlimo. Naudokite naujausią naršyklę.',
    history401: 'Prisijunkite dar kartą, kad galėtumėte ieškoti pokalbiuose.',
    history403: 'Norint ieškoti pokalbiuose, reikia įmonės kvietimo.',
    historyUnavailable: 'Pokalbių paieška nepasiekiama. Bandykite dar kartą.',
    historyUnreadable: 'Pokalbių paieška grąžino neperskaitomą atsakymą. Bandykite dar kartą.',
    search401: 'Prisijunkite dar kartą, kad galėtumėte ieškoti savo darbo srityje.',
    searchUnavailable: 'Darbo srities paieška nepasiekiama. Bandykite dar kartą.',
    searchUnreadable: 'Darbo srities paieška grąžino neperskaitomą atsakymą. Bandykite dar kartą.',
    skillsUnavailable: 'Įgūdžiai nepasiekiami. Bandykite dar kartą.',
    skillsUnreadable: 'Įgūdžiai grąžino neperskaitomą atsakymą. Bandykite dar kartą.',
    csvColumns:
      'Šiame faile yra daugiau nei 200 stulpelių. Atsisiųskite originalą, kad galėtumėte jį peržiūrėti.',
    csvField: 'Reikšmė per didelė peržiūrai. Atsisiųskite originalą, kad galėtumėte ją peržiūrėti.',
    csvQuote:
      'Šiame CSV faile yra neužbaigta reikšmė kabutėse. Atsisiųskite originalą arba įkelkite pataisytą failą.',
    meetingRecording: 'Šis susitikimas dar neturi išsaugoto įrašo.',
    uploadInterrupted: 'Įkėlimas buvo nutrauktas. Bandykite dar kartą.',
    uploadRefused: 'Saugyklos paslauga atmetė failą. Bandykite dar kartą.',
    downloadDeck: 'Nepavyko sukurti „PowerPoint“ failo. Bandykite dar kartą.',
    downloadFile: 'Nepavyko atsisiųsti šio failo. Bandykite dar kartą.',
    integrationsUnavailable: 'Nepavyko įkelti integracijų. Bandykite dar kartą.',
    inboxActionUnavailable: 'Šis veiksmas nepasiekiamas.',
    inboxUnavailable: 'Nepavyko įkelti jūsų gautųjų.',
    referencesUnavailable:
      'Kai kurios pasirinktos nuorodos nepasiekiamos. Prieš tęsdami patikrinkite jų prieigą.',
    fileVerification: 'Nepavyko patvirtinti failo.',
    uploadChecksum:
      'Ši naršyklė negali patikrinti didelio failo įkėlimo. Naudokite naujausią naršyklę.',
    agentUnreadable: 'Poneva pateikė atsakymą, kurio šis puslapis negalėjo perskaityti.',
    office_unreadable:
      'Nepavyko nuskaityti failo „{name}“. Atidarykite jį naudodami Office ir išsaugokite naują .xlsx, .docx arba .pptx kopiją.',
    office_encrypted:
      'Failas „{name}“ apsaugotas slaptažodžiu. Pašalinkite slaptažodį ir įkelkite failą dar kartą.',
    office_has_no_text:
      'Faile „{name}“ nėra nuskaitomo teksto ar langelių. Įkelkite tekstinę versiją arba PDF failą su pasirenkamu tekstu.',
    office_too_complex:
      'Failas „{name}“ yra per sudėtingas nuskaityti. Padalykite jį į mažesnius failus.',
    office_extraction_timeout:
      'Failo „{name}“ nuskaitymas užtruko per ilgai. Padalykite jį į mažesnius failus.',
    file_text_too_large:
      'Failas „{name}“ išsaugotas atsisiųsti. Įkelkite mažesnę nei 50 MiB kopiją, kad Poneva galėtų jį nuskaityti.',
    pdf_has_no_text:
      'Faile „{name}“ nėra pasirenkamo teksto, todėl tai gali būti nuskaitytas vaizdas. Pridėkite iki 4 MB dydžio kopiją tiesiai prie pokalbio žinutės arba įkelkite tekstinę versiją.',
    pdf_encrypted:
      'Failas „{name}“ apsaugotas slaptažodžiu. Pašalinkite slaptažodį ir įkelkite failą dar kartą.',
    pdf_unreadable: 'Nepavyko nuskaityti failo „{name}“ kaip PDF.',
    pdf_too_many_pages:
      'Faile „{name}“ yra daugiau nei 1 000 puslapių. Padalykite jį į mažesnius PDF failus.',
    pdf_too_complex:
      'Failas „{name}“ yra per sudėtingas nuskaityti. Padalykite jį į mažesnius PDF failus.',
    file_too_complex:
      'Failas „{name}“ yra per daug įdėtas arba jį patikrinti užtrunka per ilgai. Supaprastinkite jo struktūrą arba padalykite į mažesnius failus.',
    pdf_extraction_timeout:
      'Failo „{name}“ nuskaitymas užtruko per ilgai. Padalykite jį į mažesnius PDF failus ir bandykite dar kartą.',
    file_too_large: 'Failas „{name}“ yra didesnis nei 500 MB.',
    invalid_workspace_input:
      'Nepavyko išsaugoti failo „{name}“. Patikrinkite, ar failas atitinka savo tipą (pavyzdžiui, ar tai tinkamas JSON).',
    workspace_storage_full:
      'Jūsų įmonės biblioteka pasiekė saugyklos limitą, todėl šis failas nebuvo išsaugotas.',
    workspace_capacity_reached:
      'Jūsų įmonės biblioteka pasiekė failų skaičiaus limitą, todėl šis failas nebuvo išsaugotas.',
    workspace_upload_busy: 'Kiti failai vis dar įkeliami. Pabandykite dar kartą po akimirkos.',
    workspace_storage_unavailable:
      'Failų įkėlimas šiuo metu nepasiekiamas. Susisiekite su Poneva pagalbos tarnyba.',
    company_admin_required: 'Tik įmonės savininkai gali pridėti failų į biblioteką.',
    uploadGeneric: 'Nepavyko įkelti failo „{name}“. Bandykite dar kartą.',
    uploadTooLarge: 'Failas „{name}“ yra didesnis nei 500 MB.',
    attachmentWorkspaceChanged: 'Jūsų darbo sritis pasikeitė. Dar kartą pasirinkite vaizdą.',
    attachmentSignInImage: 'Prisijunkite, kad galėtumėte pridėti vaizdą.',
    attachmentImageSize:
      'Pasirinkite mažesnį nei 16 MiB vaizdą arba pašalinkite jį, kad galėtumėte tęsti.',
    attachmentImageUnavailable: 'Vaizdas nepasiekiamas. Pašalinkite jį arba pasirinkite dar kartą.',
    attachmentUploadImage: 'Vaizdo nepavyko įkelti. Pašalinkite jį arba pasirinkite kitą vaizdą.',
    attachmentFileChanged: 'Jūsų darbo sritis pasikeitė. Dar kartą pridėkite failą.',
    attachmentSignInFile: 'Prisijunkite, kad galėtumėte įkelti darbo failą.',
    attachmentEmpty: '{name} yra tuščias.',
    attachmentFileTypes: 'Pasirinkite PDF, tekstinį, „Excel“, „Word“ arba „PowerPoint“ failą.',
    attachmentUploadFile: 'Šio failo nepavyko įkelti.',
    attachmentReadable:
      'Pašalinkite nepavykusį pridėti failą ir įkelkite failą, kurį galima perskaityti.',
    repairWorkspaceChanged:
      'Jūsų darbo sritis pasikeitė. Prieš redaguodami iš naujo įkelkite pokalbį.',
    repairCannotSave:
      'Šio priedo negalima išsaugoti. Pašalinkite jį ir dar kartą pasirinkite failą.',
    repairOldUnavailable:
      'Šis senas priedas nepasiekiamas. Pašalinkite jį arba dar kartą pasirinkite failą.',
    repairUpload: 'Failo nepavyko įkelti. Pašalinkite jį arba pasirinkite dar kartą.',
    repairUnavailable: 'Failas nepasiekiamas. Pašalinkite jį arba pasirinkite dar kartą.',
    repairMessageUnavailable: 'Šis pranešimas nepasiekiamas. Iš naujo įkelkite pokalbį.',
    repairChatChanged:
      'Šis pokalbis pasikeitė. Prieš išsaugodami pakeitimą iš naujo įkelkite pokalbį.',
    turnUnavailable:
      'Šis pokalbio žingsnis nepasiekiamas. Iš naujo įkelkite pokalbį ir išsiųskite naują pranešimą.',
    imagePreparation: 'Šio vaizdo nepavyko paruošti. Pašalinkite jį ir pasirinkite kitą vaizdą.',
    documentSize: '{name} yra didesnis nei 4 MB.',
    projectFileJoinFailed:
      '{name} yra jūsų failuose, bet jo nepavyko pridėti prie projekto. Pridėkite jį iš projekto.',
  },
  mt: {
    uploadRule:
      'Uża Excel (.xlsx), Word (.docx), PowerPoint (.pptx), PDF, CSV, TSV, fajls ta’ test, JSON, traskrizzjonijiet, awdjo jew vidjo sa 500 MB. Issejvja l-fajls tal-Office l-antiki bħala .xlsx, .docx jew .pptx l-ewwel.',
    emptyFile: '{name} huwa vojt. Agħżel fajl li fih kontenut.',
    uploadPart: "Parti tal-fajl ma setgħetx tittella'. Erġa' pprova.",
    uploadEtag: "Is-servizz tal-ħażna ma kkonfermax parti tal-fajl. Erġa' pprova.",
    uploadGrant: "Is-sessjoni tat-tlugħ m'għandhiex l-awtorizzazzjoni tal-ħażna tagħha.",
    uploadParts:
      'Is-sessjoni tat-tlugħ irritornat awtorizzazzjonijiet mhux kompluti għall-partijiet.',
    browserChecksum: 'Dan il-browser ma jistax jivverifika tlugħ kbir. Uża browser aġġornat.',
    history401: 'Idħol mill-ġdid biex tfittex fil-konversazzjonijiet tiegħek.',
    history403: 'Hija meħtieġa stedina tal-kumpanija biex tfittex fil-konversazzjonijiet.',
    historyUnavailable: "It-tfittxija fil-konversazzjonijiet mhijiex disponibbli. Erġa' pprova.",
    historyUnreadable:
      "It-tfittxija fil-konversazzjonijiet irritornat tweġiba li ma tistax tinqara. Erġa' pprova.",
    search401: 'Idħol mill-ġdid biex tfittex fl-ispazju tax-xogħol tiegħek.',
    searchUnavailable: "It-tfittxija fl-ispazju tax-xogħol mhijiex disponibbli. Erġa' pprova.",
    searchUnreadable:
      "It-tfittxija fl-ispazju tax-xogħol irritornat tweġiba li ma tistax tinqara. Erġa' pprova.",
    skillsUnavailable: "Il-ħiliet mhumiex disponibbli. Erġa' pprova.",
    skillsUnreadable: "Il-ħiliet irritornaw tweġiba li ma tistax tinqara. Erġa' pprova.",
    csvColumns: 'Dan il-fajl għandu aktar minn 200 kolonna. Niżżel l-oriġinali biex tispezzjonah.',
    csvField:
      'Valur huwa kbir wisq biex jintwera bħala previżjoni. Niżżel l-oriġinali biex tispezzjonah.',
    csvQuote:
      "Dan is-CSV għandu valur bejn kwotazzjonijiet li ma tlestiex. Niżżel l-oriġinali jew tella' fajl ikkoreġut.",
    meetingRecording: 'Din il-laqgħa għad m’għandhiex reġistrazzjoni maħżuna.',
    uploadInterrupted: 'It-tlugħ ġie interrott. Erġa’ pprova.',
    uploadRefused: 'Is-servizz tal-ħażna rrifjuta l-fajl. Erġa’ pprova.',
    downloadDeck: 'Ma stajniex noħolqu l-fajl PowerPoint. Erġa’ pprova.',
    downloadFile: 'Ma stajniex inniżżlu dan il-fajl. Erġa’ pprova.',
    integrationsUnavailable: 'Ma stajniex nitgħabbew l-integrazzjonijiet. Erġa’ pprova.',
    inboxActionUnavailable: 'Din l-azzjoni mhijiex disponibbli.',
    inboxUnavailable: 'Ma stajniex nitgħabbew l-Inbox tiegħek.',
    referencesUnavailable:
      'Uħud mir-referenzi magħżula mhumiex disponibbli. Iċċekkja l-aċċess għalihom qabel tkompli.',
    fileVerification: 'Il-verifika tal-fajl falliet.',
    uploadChecksum: 'Dan il-browser ma jistax jivverifika tlugħ kbir. Uża browser aġġornat.',
    agentUnreadable: 'Poneva rritorna tweġiba li din il-paġna ma setgħetx taqra.',
    office_unreadable:
      '{name} ma setax jinqara. Iftaħ f’Office u ssejvja kopja ġdida .xlsx, .docx jew .pptx.',
    office_encrypted: '{name} huwa protett b’password. Neħħi l-password u erġa’ tellgħu.',
    office_has_no_text:
      '{name} m’għandux test jew ċelloli li jistgħu jinqraw. Tella’ verżjoni testwali, jew PDF b’test li jista’ jintgħażel.',
    office_too_complex: '{name} huwa kumpless wisq biex jinqara. Aqsamu f’fajls iżgħar.',
    office_extraction_timeout: '{name} dam wisq biex jinqara. Aqsamu f’fajls iżgħar.',
    file_text_too_large:
      '{name} ġie ssejvjat biex jitniżżel. Tella’ kopja ta’ inqas minn 50 MiB biex Poneva jaqrah.',
    pdf_has_no_text:
      '{name} m’għandux test li jista’ jintgħażel, għalhekk jista’ jkun immaġni skennjata. Ehmeż kopja ta’ mhux aktar minn 4 MB direttament ma’ messaġġ taċ-chat, jew tella’ verżjoni testwali.',
    pdf_encrypted: '{name} huwa protett b’password. Neħħi l-password u erġa’ tellgħu.',
    pdf_unreadable: '{name} ma setax jinqara bħala PDF.',
    pdf_too_many_pages: '{name} għandu aktar minn 1,000 paġna. Aqsamu f’PDFs iżgħar.',
    pdf_too_complex: '{name} huwa kumpless wisq biex jinqara. Aqsamu f’PDFs iżgħar.',
    file_too_complex:
      '{name} għandu struttura mdaħħla fil-fond wisq jew jieħu wisq ħin biex jiġi kkontrollat. Issimplifika l-istruttura tiegħu jew aqsamu f’fajls iżgħar.',
    pdf_extraction_timeout: '{name} dam wisq biex jinqara. Aqsamu f’PDFs iżgħar u erġa’ pprova.',
    file_too_large: '{name} huwa akbar minn 500 MB.',
    invalid_workspace_input:
      '{name} ma setax jiġi ssejvjat. Iċċekkja li l-fajl jaqbilx mat-tip tiegħu (pereżempju JSON validu).',
    workspace_storage_full:
      'Il-Librerija tal-kumpanija tiegħek laħqet il-limitu tal-ħażna tagħha, għalhekk dan il-fajl ma ġiex ssejvjat.',
    workspace_capacity_reached:
      'Il-Librerija tal-kumpanija tiegħek laħqet il-kapaċità tagħha ta’ fajls, għalhekk dan il-fajl ma ġiex ssejvjat.',
    workspace_upload_busy:
      'Għad hemm uploads oħra li qed jiġu pproċessati. Erġa’ pprova ftit wara.',
    workspace_storage_unavailable:
      'L-uploads tal-fajls bħalissa mhumiex disponibbli. Ikkuntattja l-appoġġ ta’ Poneva.',
    company_admin_required: 'Is-sidien tal-kumpanija biss jistgħu jżidu fajls mal-Librerija.',
    uploadGeneric: 'Ma stajniex intellgħu {name}. Erġa’ pprova.',
    uploadTooLarge: '{name} huwa akbar minn 500 MB.',
    attachmentWorkspaceChanged: 'L-ispazju tax-xogħol tiegħek inbidel. Agħżel l-immaġni mill-ġdid.',
    attachmentSignInImage: 'Idħol biex tehmeż immaġni.',
    attachmentImageSize: 'Agħżel immaġni iżgħar minn 16 MiB, jew neħħiha biex tkompli.',
    attachmentImageUnavailable: 'L-immaġni mhijiex disponibbli. Neħħiha jew agħżelha mill-ġdid.',
    attachmentUploadImage: 'L-immaġni ma setgħetx tittella’. Neħħiha jew agħżel immaġni oħra.',
    attachmentFileChanged: 'L-ispazju tax-xogħol tiegħek inbidel. Erġa’ ehmeż il-fajl.',
    attachmentSignInFile: 'Idħol biex ittella’ fajl tan-negozju.',
    attachmentEmpty: '{name} huwa vojt.',
    attachmentFileTypes: 'Agħżel fajl PDF, test, Excel, Word jew PowerPoint.',
    attachmentUploadFile: 'Ma stajniex intellgħu dan il-fajl.',
    attachmentReadable: 'Neħħi l-anness li ma rnexxiex u tella’ kopja li tista’ tinqara.',
    repairWorkspaceChanged:
      'L-ispazju tax-xogħol tiegħek inbidel. Erġa’ għabbi ċ-chat qabel teditja.',
    repairCannotSave: 'Dan l-anness ma jistax jiġi ssejvjat. Neħħih u agħżel il-fajl mill-ġdid.',
    repairOldUnavailable:
      'Dan l-anness antik mhuwiex disponibbli. Neħħih jew agħżel il-fajl mill-ġdid.',
    repairUpload: 'Il-fajl ma setax jittella’. Neħħih jew agħżlu mill-ġdid.',
    repairUnavailable: 'Il-fajl mhuwiex disponibbli. Neħħih jew agħżlu mill-ġdid.',
    repairMessageUnavailable: 'Dan il-messaġġ mhuwiex disponibbli. Erġa’ għabbi ċ-chat.',
    repairChatChanged: 'Dan iċ-chat inbidel. Erġa’ għabbu qabel issalva l-editjar tiegħek.',
    turnUnavailable: 'Dan it-turn mhuwiex disponibbli. Erġa’ għabbi ċ-chat u ibgħat messaġġ ġdid.',
    imagePreparation: 'Din l-immaġni ma setgħetx titħejja. Neħħiha u agħżel immaġni oħra.',
    documentSize: '{name} huwa akbar minn 4 MB.',
    projectFileJoinFailed:
      '{name} qiegħed fil-fajls tiegħek, imma ma setax jiżdied mal-proġett. Żidu mill-proġett.',
  },
  pl: {
    uploadRule:
      'Używaj plików Excel (.xlsx), Word (.docx), PowerPoint (.pptx), PDF, CSV, TSV, tekstowych, JSON, transkrypcji, audio lub wideo o rozmiarze do 500 MB. Najpierw zapisz starsze pliki pakietu Office jako .xlsx, .docx lub .pptx.',
    emptyFile: '{name} jest pusty. Wybierz plik zawierający treść.',
    uploadPart: 'Nie udało się przesłać części pliku. Spróbuj ponownie.',
    uploadEtag: 'Usługa przechowywania nie potwierdziła części pliku. Spróbuj ponownie.',
    uploadGrant: 'W sesji przesyłania brakuje uprawnienia do korzystania z magazynu.',
    uploadParts: 'Sesja przesyłania zwróciła niekompletne uprawnienia do części pliku.',
    browserChecksum:
      'Ta przeglądarka nie może zweryfikować dużego pliku. Użyj aktualnej przeglądarki.',
    history401: 'Zaloguj się ponownie, aby wyszukiwać w rozmowach.',
    history403: 'Aby wyszukiwać w rozmowach, wymagane jest zaproszenie do firmy.',
    historyUnavailable: 'Wyszukiwanie w rozmowach jest niedostępne. Spróbuj ponownie.',
    historyUnreadable: 'Wyszukiwanie w rozmowach zwróciło nieczytelną odpowiedź. Spróbuj ponownie.',
    search401: 'Zaloguj się ponownie, aby wyszukiwać w obszarze roboczym.',
    searchUnavailable: 'Wyszukiwanie w obszarze roboczym jest niedostępne. Spróbuj ponownie.',
    searchUnreadable:
      'Wyszukiwanie w obszarze roboczym zwróciło nieczytelną odpowiedź. Spróbuj ponownie.',
    skillsUnavailable: 'Umiejętności są niedostępne. Spróbuj ponownie.',
    skillsUnreadable: 'Umiejętności zwróciły nieczytelną odpowiedź. Spróbuj ponownie.',
    csvColumns: 'Ten plik zawiera więcej niż 200 kolumn. Pobierz oryginał, aby go sprawdzić.',
    csvField:
      'Jedna z wartości jest zbyt duża, aby wyświetlić jej podgląd. Pobierz oryginał, aby ją sprawdzić.',
    csvQuote:
      'Ten plik CSV zawiera niedokończoną wartość w cudzysłowie. Pobierz oryginał lub prześlij poprawiony plik.',
    meetingRecording: 'To spotkanie nie ma jeszcze zachowanego nagrania.',
    uploadInterrupted: 'Przesyłanie zostało przerwane. Spróbuj ponownie.',
    uploadRefused: 'Usługa pamięci masowej odrzuciła plik. Spróbuj ponownie.',
    downloadDeck: 'Nie udało się utworzyć pliku PowerPoint. Spróbuj ponownie.',
    downloadFile: 'Nie udało się pobrać tego pliku. Spróbuj ponownie.',
    integrationsUnavailable: 'Nie udało się załadować integracji. Spróbuj ponownie.',
    inboxActionUnavailable: 'Ta czynność jest niedostępna.',
    inboxUnavailable: 'Nie udało się załadować Twojej skrzynki odbiorczej.',
    referencesUnavailable:
      'Niektóre wybrane odwołania są niedostępne. Sprawdź do nich dostęp przed kontynuowaniem.',
    fileVerification: 'Weryfikacja pliku nie powiodła się.',
    uploadChecksum:
      'Ta przeglądarka nie może zweryfikować dużego przesyłanego pliku. Użyj aktualnej przeglądarki.',
    agentUnreadable: 'Poneva zwróciła odpowiedź, której ta strona nie mogła odczytać.',
    office_unreadable:
      'Nie udało się odczytać pliku {name}. Otwórz go w pakiecie Office i zapisz nową kopię w formacie .xlsx, .docx lub .pptx.',
    office_encrypted: 'Plik {name} jest chroniony hasłem. Usuń hasło i prześlij go ponownie.',
    office_has_no_text:
      'Plik {name} nie zawiera tekstu ani komórek, które można odczytać. Prześlij wersję tekstową lub PDF z zaznaczalnym tekstem.',
    office_too_complex:
      'Plik {name} jest zbyt złożony, aby go odczytać. Podziel go na mniejsze pliki.',
    office_extraction_timeout:
      'Odczytanie pliku {name} trwało zbyt długo. Podziel go na mniejsze pliki.',
    file_text_too_large:
      'Plik {name} został zapisany do pobrania. Prześlij kopię mniejszą niż 50 MiB, aby Poneva mogła go odczytać.',
    pdf_has_no_text:
      'Plik {name} nie zawiera zaznaczalnego tekstu, więc może być zeskanowanym obrazem. Dołącz kopię o rozmiarze do 4 MB bezpośrednio do wiadomości na czacie lub prześlij wersję tekstową.',
    pdf_encrypted: 'Plik {name} jest chroniony hasłem. Usuń hasło i prześlij go ponownie.',
    pdf_unreadable: 'Nie udało się odczytać pliku {name} jako PDF.',
    pdf_too_many_pages: 'Plik {name} ma więcej niż 1000 stron. Podziel go na mniejsze pliki PDF.',
    pdf_too_complex:
      'Plik {name} jest zbyt złożony, aby go odczytać. Podziel go na mniejsze pliki PDF.',
    file_too_complex:
      'Plik {name} ma zbyt głęboko zagnieżdżoną strukturę lub jego sprawdzenie trwa zbyt długo. Uprość jego strukturę albo podziel go na mniejsze pliki.',
    pdf_extraction_timeout:
      'Odczytanie pliku {name} trwało zbyt długo. Podziel go na mniejsze pliki PDF i spróbuj ponownie.',
    file_too_large: 'Plik {name} jest większy niż 500 MB.',
    invalid_workspace_input:
      'Nie udało się zapisać pliku {name}. Sprawdź, czy plik odpowiada swojemu typowi (na przykład jest prawidłowym plikiem JSON).',
    workspace_storage_full:
      'Firmowa Biblioteka osiągnęła limit miejsca, dlatego ten plik nie został zapisany.',
    workspace_capacity_reached:
      'Firmowa Biblioteka osiągnęła limit liczby plików, dlatego ten plik nie został zapisany.',
    workspace_upload_busy: 'Inne pliki są nadal przetwarzane. Spróbuj ponownie za chwilę.',
    workspace_storage_unavailable:
      'Przesyłanie plików jest obecnie niedostępne. Skontaktuj się z pomocą techniczną Poneva.',
    company_admin_required: 'Tylko właściciele firmy mogą dodawać pliki do Biblioteki.',
    uploadGeneric: 'Nie udało się przesłać pliku {name}. Spróbuj ponownie.',
    uploadTooLarge: 'Plik {name} jest większy niż 500 MB.',
    attachmentWorkspaceChanged: 'Twój obszar roboczy uległ zmianie. Wybierz obraz ponownie.',
    attachmentSignInImage: 'Zaloguj się, aby dołączyć obraz.',
    attachmentImageSize: 'Wybierz obraz mniejszy niż 16 MiB lub usuń go, aby kontynuować.',
    attachmentImageUnavailable: 'Obraz jest niedostępny. Usuń go lub wybierz ponownie.',
    attachmentUploadImage: 'Nie udało się przesłać obrazu. Usuń go lub wybierz inny obraz.',
    attachmentFileChanged: 'Twój obszar roboczy uległ zmianie. Dołącz plik ponownie.',
    attachmentSignInFile: 'Zaloguj się, aby przesłać plik firmowy.',
    attachmentEmpty: '{name} jest pusty.',
    attachmentFileTypes: 'Wybierz plik PDF, tekstowy, Excel, Word lub PowerPoint.',
    attachmentUploadFile: 'Nie udało się przesłać tego pliku.',
    attachmentReadable: 'Usuń nieudany załącznik i prześlij czytelną kopię.',
    repairWorkspaceChanged: 'Twój obszar roboczy uległ zmianie. Odśwież czat przed edycją.',
    repairCannotSave: 'Nie można zapisać tego załącznika. Usuń go i ponownie wybierz plik.',
    repairOldUnavailable:
      'Ten stary załącznik jest niedostępny. Usuń go lub wybierz plik ponownie.',
    repairUpload: 'Nie udało się przesłać pliku. Usuń go lub wybierz ponownie.',
    repairUnavailable: 'Plik jest niedostępny. Usuń go lub wybierz ponownie.',
    repairMessageUnavailable: 'Ta wiadomość jest niedostępna. Odśwież czat.',
    repairChatChanged: 'Ten czat uległ zmianie. Odśwież go przed zapisaniem edycji.',
    turnUnavailable: 'Ta tura jest niedostępna. Odśwież czat i wyślij nową wiadomość.',
    imagePreparation: 'Nie udało się przygotować tego obrazu. Usuń go i wybierz inny obraz.',
    documentSize: '{name} ma ponad 4 MB.',
    projectFileJoinFailed:
      '{name} znajduje się w Twoich plikach, ale nie udało się go dodać do projektu. Dodaj go z poziomu projektu.',
  },
  pt: {
    uploadRule:
      'Use arquivos Excel (.xlsx), Word (.docx), PowerPoint (.pptx), PDF, CSV, TSV, texto, JSON, transcrições, áudio ou vídeo de até 500 MB. Salve primeiro os arquivos antigos do Office como .xlsx, .docx ou .pptx.',
    emptyFile: '{name} está vazio. Escolha um arquivo com conteúdo.',
    uploadPart: 'Não foi possível fazer upload de uma parte do arquivo. Tente novamente.',
    uploadEtag: 'O serviço de armazenamento não confirmou uma parte do arquivo. Tente novamente.',
    uploadGrant: 'A sessão de upload não tem a autorização de armazenamento necessária.',
    uploadParts: 'A sessão de upload retornou autorizações incompletas para as partes.',
    browserChecksum:
      'Este navegador não consegue verificar um upload grande. Use um navegador atualizado.',
    history401: 'Faça login novamente para pesquisar suas conversas.',
    history403: 'É necessário um convite da empresa para pesquisar conversas.',
    historyUnavailable: 'A pesquisa de conversas está indisponível. Tente novamente.',
    historyUnreadable: 'A pesquisa de conversas retornou uma resposta ilegível. Tente novamente.',
    search401: 'Faça login novamente para pesquisar no seu espaço de trabalho.',
    searchUnavailable: 'A pesquisa no espaço de trabalho está indisponível. Tente novamente.',
    searchUnreadable:
      'A pesquisa no espaço de trabalho retornou uma resposta ilegível. Tente novamente.',
    skillsUnavailable: 'As habilidades estão indisponíveis. Tente novamente.',
    skillsUnreadable: 'As habilidades retornaram uma resposta ilegível. Tente novamente.',
    csvColumns: 'Este arquivo tem mais de 200 colunas. Baixe o original para inspecioná-lo.',
    csvField: 'Um valor é grande demais para ser visualizado. Baixe o original para inspecioná-lo.',
    csvQuote:
      'Este CSV tem um valor entre aspas não finalizado. Baixe o original ou faça upload de um arquivo corrigido.',
    meetingRecording: 'Esta reunião ainda não tem uma gravação retida.',
    uploadInterrupted: 'O upload foi interrompido. Tente novamente.',
    uploadRefused: 'O serviço de armazenamento recusou o arquivo. Tente novamente.',
    downloadDeck: 'Não foi possível criar o arquivo do PowerPoint. Tente novamente.',
    downloadFile: 'Não foi possível baixar este arquivo. Tente novamente.',
    integrationsUnavailable: 'Não foi possível carregar as integrações. Tente novamente.',
    inboxActionUnavailable: 'Esta ação está indisponível.',
    inboxUnavailable: 'Não foi possível carregar sua Caixa de entrada.',
    referencesUnavailable:
      'Algumas referências selecionadas estão indisponíveis. Verifique o acesso a elas antes de continuar.',
    fileVerification: 'Falha na verificação do arquivo.',
    uploadChecksum:
      'Este navegador não pode verificar um upload grande. Use um navegador atualizado.',
    agentUnreadable: 'O Poneva retornou uma resposta que esta página não conseguiu ler.',
    office_unreadable:
      'Não foi possível ler {name}. Abra-o no Office e salve uma nova cópia .xlsx, .docx ou .pptx.',
    office_encrypted: '{name} é protegido por senha. Remova a senha e faça o upload novamente.',
    office_has_no_text:
      '{name} não contém texto ou células legíveis. Faça o upload de uma versão com texto ou de um PDF com texto selecionável.',
    office_too_complex: '{name} é complexo demais para ser lido. Divida-o em arquivos menores.',
    office_extraction_timeout: '{name} demorou demais para ser lido. Divida-o em arquivos menores.',
    file_text_too_large:
      '{name} foi salvo para download. Faça o upload de uma cópia com menos de 50 MiB para que o Poneva possa lê-lo.',
    pdf_has_no_text:
      '{name} não contém texto selecionável e pode ser uma imagem digitalizada. Anexe uma cópia de até 4 MB diretamente a uma mensagem de chat ou faça o upload de uma versão com texto.',
    pdf_encrypted: '{name} é protegido por senha. Remova a senha e faça o upload novamente.',
    pdf_unreadable: 'Não foi possível ler {name} como PDF.',
    pdf_too_many_pages: '{name} tem mais de 1.000 páginas. Divida-o em PDFs menores.',
    pdf_too_complex: '{name} é complexo demais para ser lido. Divida-o em PDFs menores.',
    file_too_complex:
      '{name} tem uma estrutura aninhada demais ou demora muito para ser verificado. Simplifique sua estrutura ou divida-o em arquivos menores.',
    pdf_extraction_timeout:
      '{name} demorou demais para ser lido. Divida-o em PDFs menores e tente novamente.',
    file_too_large: '{name} é maior que 500 MB.',
    invalid_workspace_input:
      'Não foi possível salvar {name}. Verifique se o arquivo corresponde ao seu tipo (por exemplo, se é um JSON válido).',
    workspace_storage_full:
      'A Library da sua empresa atingiu o limite de armazenamento, então este arquivo não foi salvo.',
    workspace_capacity_reached:
      'A Library da sua empresa atingiu o limite de arquivos, então este arquivo não foi salvo.',
    workspace_upload_busy:
      'Outros uploads ainda estão sendo processados. Tente novamente em instantes.',
    workspace_storage_unavailable:
      'No momento, não é possível fazer upload de arquivos. Entre em contato com o suporte do Poneva.',
    company_admin_required:
      'Somente os proprietários da empresa podem adicionar arquivos à Library.',
    uploadGeneric: 'Não foi possível fazer o upload de {name}. Tente novamente.',
    uploadTooLarge: '{name} é maior que 500 MB.',
    attachmentWorkspaceChanged: 'Seu espaço de trabalho mudou. Escolha a imagem novamente.',
    attachmentSignInImage: 'Inicie sessão para anexar uma imagem.',
    attachmentImageSize: 'Escolha uma imagem com menos de 16 MiB ou remova-a para continuar.',
    attachmentImageUnavailable: 'A imagem não está disponível. Remova-a ou escolha-a novamente.',
    attachmentUploadImage: 'Não foi possível carregar a imagem. Remova-a ou escolha outra imagem.',
    attachmentFileChanged: 'Seu espaço de trabalho mudou. Anexe o arquivo novamente.',
    attachmentSignInFile: 'Inicie sessão para carregar um arquivo comercial.',
    attachmentEmpty: '{name} está vazio.',
    attachmentFileTypes: 'Escolha um arquivo PDF, de texto, Excel, Word ou PowerPoint.',
    attachmentUploadFile: 'Não foi possível carregar este arquivo.',
    attachmentReadable: 'Remova o anexo com falha e carregue uma cópia legível.',
    repairWorkspaceChanged: 'Seu espaço de trabalho mudou. Recarregue o chat antes de editar.',
    repairCannotSave: 'Não é possível salvar este anexo. Remova-o e escolha o arquivo novamente.',
    repairOldUnavailable:
      'Este anexo antigo não está disponível. Remova-o ou escolha o arquivo novamente.',
    repairUpload: 'Não foi possível carregar o arquivo. Remova-o ou escolha-o novamente.',
    repairUnavailable: 'O arquivo não está disponível. Remova-o ou escolha-o novamente.',
    repairMessageUnavailable: 'Esta mensagem não está disponível. Recarregue o chat.',
    repairChatChanged: 'Este chat mudou. Recarregue-o antes de salvar sua edição.',
    turnUnavailable:
      'Esta interação não está disponível. Recarregue o chat e envie uma nova mensagem.',
    imagePreparation: 'Não foi possível preparar esta imagem. Remova-a e escolha outra imagem.',
    documentSize: '{name} é maior que 4 MB.',
    projectFileJoinFailed:
      '{name} está nos seus arquivos, mas não foi possível adicioná-lo ao projeto. Adicione-o a partir do projeto.',
  },
  ro: {
    uploadRule:
      'Folosiți fișiere Excel (.xlsx), Word (.docx), PowerPoint (.pptx), PDF, CSV, TSV, text, JSON, transcrieri, audio sau video de până la 500 MB. Salvați mai întâi fișierele Office mai vechi ca .xlsx, .docx sau .pptx.',
    emptyFile: '{name} este gol. Alege un fișier cu conținut.',
    uploadPart: 'O parte a fișierului nu a putut fi încărcată. Încearcă din nou.',
    uploadEtag: 'Serviciul de stocare nu a confirmat o parte a fișierului. Încearcă din nou.',
    uploadGrant: 'Sesiunii de încărcare îi lipsește autorizația de stocare.',
    uploadParts: 'Sesiunea de încărcare a returnat autorizații incomplete pentru părți.',
    browserChecksum:
      'Acest browser nu poate verifica o încărcare de dimensiuni mari. Folosește un browser actualizat.',
    history401: 'Autentifică-te din nou pentru a căuta în conversațiile tale.',
    history403: 'Este necesară o invitație din partea companiei pentru a căuta conversații.',
    historyUnavailable: 'Căutarea conversațiilor nu este disponibilă. Încearcă din nou.',
    historyUnreadable:
      'Căutarea conversațiilor a returnat un răspuns imposibil de citit. Încearcă din nou.',
    search401: 'Autentifică-te din nou pentru a căuta în spațiul tău de lucru.',
    searchUnavailable: 'Căutarea în spațiul de lucru nu este disponibilă. Încearcă din nou.',
    searchUnreadable:
      'Căutarea în spațiul de lucru a returnat un răspuns imposibil de citit. Încearcă din nou.',
    skillsUnavailable: 'Competențele nu sunt disponibile. Încearcă din nou.',
    skillsUnreadable: 'Competențele au returnat un răspuns imposibil de citit. Încearcă din nou.',
    csvColumns:
      'Acest fișier are mai mult de 200 de coloane. Descarcă originalul pentru a-l inspecta.',
    csvField:
      'O valoare este prea mare pentru previzualizare. Descarcă originalul pentru a o inspecta.',
    csvQuote:
      'Acest CSV are o valoare între ghilimele neterminată. Descarcă originalul sau încarcă un fișier corectat.',
    meetingRecording: 'Această întâlnire nu are încă o înregistrare păstrată.',
    uploadInterrupted: 'Încărcarea a fost întreruptă. Încearcă din nou.',
    uploadRefused: 'Serviciul de stocare a refuzat fișierul. Încearcă din nou.',
    downloadDeck: 'Nu am putut crea fișierul PowerPoint. Încearcă din nou.',
    downloadFile: 'Nu am putut descărca acest fișier. Încearcă din nou.',
    integrationsUnavailable: 'Nu am putut încărca integrările. Încearcă din nou.',
    inboxActionUnavailable: 'Această acțiune nu este disponibilă.',
    inboxUnavailable: 'Nu am putut încărca mesajele primite.',
    referencesUnavailable:
      'Unele referințe selectate nu sunt disponibile. Verifică accesul la acestea înainte de a continua.',
    fileVerification: 'Verificarea fișierului a eșuat.',
    uploadChecksum:
      'Acest browser nu poate verifica o încărcare de dimensiune mare. Folosește un browser actualizat.',
    agentUnreadable: 'Poneva a returnat un răspuns pe care această pagină nu l-a putut citi.',
    office_unreadable:
      '{name} nu a putut fi citit. Deschideți-l în Office și salvați o copie nouă în format .xlsx, .docx sau .pptx.',
    office_encrypted: '{name} este protejat prin parolă. Eliminați parola și încărcați-l din nou.',
    office_has_no_text:
      '{name} nu conține text sau celule care pot fi citite. Încărcați o versiune text sau un PDF cu text selectabil.',
    office_too_complex:
      '{name} este prea complex pentru a fi citit. Împărțiți-l în fișiere mai mici.',
    office_extraction_timeout: 'Citirea {name} a durat prea mult. Împărțiți-l în fișiere mai mici.',
    file_text_too_large:
      '{name} a fost salvat pentru descărcare. Încărcați o copie de cel mult 50 MiB pentru ca Poneva să o poată citi.',
    pdf_has_no_text:
      '{name} nu conține text selectabil, deci este posibil să fie o imagine scanată. Atașați o copie de cel mult 4 MB direct într-un mesaj de chat sau încărcați o versiune text.',
    pdf_encrypted: '{name} este protejat prin parolă. Eliminați parola și încărcați-l din nou.',
    pdf_unreadable: '{name} nu a putut fi citit ca PDF.',
    pdf_too_many_pages:
      '{name} are mai mult de 1.000 de pagini. Împărțiți-l în fișiere PDF mai mici.',
    pdf_too_complex:
      '{name} este prea complex pentru a fi citit. Împărțiți-l în fișiere PDF mai mici.',
    file_too_complex:
      '{name} are prea multe niveluri de imbricare sau verificarea durează prea mult. Simplificați-i structura sau împărțiți-l în fișiere mai mici.',
    pdf_extraction_timeout:
      'Citirea {name} a durat prea mult. Împărțiți-l în fișiere PDF mai mici și încercați din nou.',
    file_too_large: '{name} este mai mare de 500 MB.',
    invalid_workspace_input:
      '{name} nu a putut fi salvat. Verificați dacă fișierul corespunde tipului său (de exemplu, dacă este un JSON valid).',
    workspace_storage_full:
      'Biblioteca companiei a atins limita de stocare, așa că acest fișier nu a fost salvat.',
    workspace_capacity_reached:
      'Biblioteca companiei a atins numărul maxim de fișiere, așa că acest fișier nu a fost salvat.',
    workspace_upload_busy:
      'Alte încărcări sunt încă în curs de procesare. Încercați din nou peste puțin timp.',
    workspace_storage_unavailable:
      'Încărcarea fișierelor nu este disponibilă momentan. Contactați serviciul de asistență Poneva.',
    company_admin_required: 'Doar proprietarii companiei pot adăuga fișiere în Bibliotecă.',
    uploadGeneric: 'Nu am putut încărca {name}. Încercați din nou.',
    uploadTooLarge: '{name} este mai mare de 500 MB.',
    attachmentWorkspaceChanged: 'Spațiul de lucru s-a schimbat. Alegeți din nou imaginea.',
    attachmentSignInImage: 'Conectați-vă pentru a atașa o imagine.',
    attachmentImageSize:
      'Alegeți o imagine mai mică de 16 MiB sau eliminați imaginea pentru a continua.',
    attachmentImageUnavailable: 'Imaginea nu este disponibilă. Eliminați-o sau alegeți-o din nou.',
    attachmentUploadImage:
      'Imaginea nu a putut fi încărcată. Eliminați-o sau alegeți altă imagine.',
    attachmentFileChanged: 'Spațiul de lucru s-a schimbat. Atașați din nou fișierul.',
    attachmentSignInFile: 'Conectați-vă pentru a încărca un fișier de business.',
    attachmentEmpty: '{name} este gol.',
    attachmentFileTypes: 'Alegeți un fișier PDF, text, Excel, Word sau PowerPoint.',
    attachmentUploadFile: 'Nu am putut încărca acest fișier.',
    attachmentReadable: 'Eliminați atașamentul eșuat și încărcați o copie care poate fi citită.',
    repairWorkspaceChanged:
      'Spațiul de lucru s-a schimbat. Reîncărcați conversația înainte de editare.',
    repairCannotSave:
      'Acest atașament nu poate fi salvat. Eliminați-l și alegeți din nou fișierul.',
    repairOldUnavailable:
      'Acest atașament vechi nu este disponibil. Eliminați-l sau alegeți din nou fișierul.',
    repairUpload: 'Fișierul nu a putut fi încărcat. Eliminați-l sau alegeți-l din nou.',
    repairUnavailable: 'Fișierul nu este disponibil. Eliminați-l sau alegeți-l din nou.',
    repairMessageUnavailable: 'Acest mesaj nu este disponibil. Reîncărcați conversația.',
    repairChatChanged:
      'Această conversație s-a schimbat. Reîncărcați-o înainte de a salva editarea.',
    turnUnavailable:
      'Această etapă nu este disponibilă. Reîncărcați conversația și trimiteți un mesaj nou.',
    imagePreparation:
      'Această imagine nu a putut fi pregătită. Eliminați-o și alegeți altă imagine.',
    documentSize: '{name} este mai mare de 4 MB.',
    projectFileJoinFailed:
      '{name} este în fișierele tale, dar nu a putut fi adăugat la proiect. Adaugă-l din proiect.',
  },
  sk: {
    uploadRule:
      'Použite súbory Excel (.xlsx), Word (.docx), PowerPoint (.pptx), PDF, CSV, TSV, textové súbory, JSON, prepisy, zvukové alebo videá s veľkosťou do 500 MB. Staršie súbory balíka Office najprv uložte ako .xlsx, .docx alebo .pptx.',
    emptyFile: '{name} je prázdny. Vyberte súbor s obsahom.',
    uploadPart: 'Časť súboru sa nepodarilo nahrať. Skúste to znova.',
    uploadEtag: 'Úložná služba nepotvrdila časť súboru. Skúste to znova.',
    uploadGrant: 'Relácii nahrávania chýba povolenie úložnej služby.',
    uploadParts: 'Relácia nahrávania vrátila neúplné povolenia pre časti súboru.',
    browserChecksum:
      'Tento prehliadač nedokáže overiť veľké nahrávanie. Použite aktuálny prehliadač.',
    history401: 'Znova sa prihláste a vyhľadajte svoje konverzácie.',
    history403: 'Na vyhľadávanie konverzácií je potrebná pozvánka do spoločnosti.',
    historyUnavailable: 'Vyhľadávanie konverzácií nie je k dispozícii. Skúste to znova.',
    historyUnreadable: 'Vyhľadávanie konverzácií vrátilo nečitateľnú odpoveď. Skúste to znova.',
    search401: 'Znova sa prihláste a vyhľadajte vo svojom pracovnom priestore.',
    searchUnavailable: 'Vyhľadávanie v pracovnom priestore nie je k dispozícii. Skúste to znova.',
    searchUnreadable:
      'Vyhľadávanie v pracovnom priestore vrátilo nečitateľnú odpoveď. Skúste to znova.',
    skillsUnavailable: 'Zručnosti nie sú k dispozícii. Skúste to znova.',
    skillsUnreadable: 'Zručnosti vrátili nečitateľnú odpoveď. Skúste to znova.',
    csvColumns:
      'Tento súbor obsahuje viac ako 200 stĺpcov. Stiahnite si originál a skontrolujte ho.',
    csvField:
      'Hodnota je príliš veľká na zobrazenie v náhľade. Stiahnite si originál a skontrolujte ju.',
    csvQuote:
      'Tento súbor CSV obsahuje neukončenú hodnotu v úvodzovkách. Stiahnite si originál alebo nahrajte opravený súbor.',
    meetingRecording: 'K tomuto stretnutiu zatiaľ nie je k dispozícii uchovaná nahrávka.',
    uploadInterrupted: 'Nahrávanie bolo prerušené. Skúste to znova.',
    uploadRefused: 'Úložná služba odmietla súbor. Skúste to znova.',
    downloadDeck: 'Prezentáciu PowerPoint sa nepodarilo vytvoriť. Skúste to znova.',
    downloadFile: 'Tento súbor sa nepodarilo stiahnuť. Skúste to znova.',
    integrationsUnavailable: 'Integrácie sa nepodarilo načítať. Skúste to znova.',
    inboxActionUnavailable: 'Táto akcia nie je k dispozícii.',
    inboxUnavailable: 'Doručenú poštu sa nepodarilo načítať.',
    referencesUnavailable:
      'Niektoré vybrané referencie nie sú k dispozícii. Pred pokračovaním skontrolujte svoj prístup.',
    fileVerification: 'Overenie súboru zlyhalo.',
    uploadChecksum:
      'Tento prehliadač nedokáže overiť veľké nahrávanie. Použite aktuálny prehliadač.',
    agentUnreadable: 'Poneva vrátila odpoveď, ktorú táto stránka nedokázala prečítať.',
    office_unreadable:
      '{name} sa nepodarilo prečítať. Otvorte ho v Office a uložte novú kópiu vo formáte .xlsx, .docx alebo .pptx.',
    office_encrypted: '{name} je chránený heslom. Odstráňte heslo a nahrajte ho znova.',
    office_has_no_text:
      '{name} neobsahuje čitateľný text ani bunky. Nahrajte textovú verziu alebo PDF s textom, ktorý možno označiť.',
    office_too_complex: '{name} je príliš zložitý na prečítanie. Rozdeľte ho na menšie súbory.',
    office_extraction_timeout:
      'Čítanie súboru {name} trvalo príliš dlho. Rozdeľte ho na menšie súbory.',
    file_text_too_large:
      '{name} bol uložený na stiahnutie. Nahrajte kópiu menšiu ako 50 MiB, aby ho Poneva mohla prečítať.',
    pdf_has_no_text:
      '{name} neobsahuje text, ktorý možno označiť, takže môže ísť o naskenovaný obrázok. Priložte kópiu s veľkosťou do 4 MB priamo do správy v chate alebo nahrajte textovú verziu.',
    pdf_encrypted: '{name} je chránený heslom. Odstráňte heslo a nahrajte ho znova.',
    pdf_unreadable: '{name} sa nepodarilo prečítať ako PDF.',
    pdf_too_many_pages: '{name} má viac ako 1 000 strán. Rozdeľte ho na menšie súbory PDF.',
    pdf_too_complex: '{name} je príliš zložitý na prečítanie. Rozdeľte ho na menšie súbory PDF.',
    file_too_complex:
      '{name} má príliš vnorenú štruktúru alebo jeho kontrola trvá príliš dlho. Zjednodušte jeho štruktúru alebo ho rozdeľte na menšie súbory.',
    pdf_extraction_timeout:
      'Čítanie súboru {name} trvalo príliš dlho. Rozdeľte ho na menšie súbory PDF a skúste znova.',
    file_too_large: '{name} je väčší ako 500 MB.',
    invalid_workspace_input:
      '{name} sa nepodarilo uložiť. Skontrolujte, či súbor zodpovedá svojmu typu (napríklad či ide o platný JSON).',
    workspace_storage_full:
      'Firemná Knižnica dosiahla limit úložiska, preto sa tento súbor neuložil.',
    workspace_capacity_reached:
      'Firemná Knižnica dosiahla limit počtu súborov, preto sa tento súbor neuložil.',
    workspace_upload_busy: 'Ostatné nahrávania sa stále spracúvajú. Skúste to znova o chvíľu.',
    workspace_storage_unavailable:
      'Nahrávanie súborov je momentálne nedostupné. Kontaktujte podporu Poneva.',
    company_admin_required: 'Súbory do Knižnice môžu pridávať iba vlastníci spoločnosti.',
    uploadGeneric: 'Súbor {name} sa nepodarilo nahrať. Skúste to znova.',
    uploadTooLarge: '{name} je väčší ako 500 MB.',
    attachmentWorkspaceChanged: 'Váš pracovný priestor sa zmenil. Vyberte obrázok znova.',
    attachmentSignInImage: 'Prihláste sa a priložte obrázok.',
    attachmentImageSize: 'Vyberte obrázok s veľkosťou do 16 MiB alebo ho odstráňte a pokračujte.',
    attachmentImageUnavailable: 'Obrázok nie je k dispozícii. Odstráňte ho alebo ho vyberte znova.',
    attachmentUploadImage: 'Obrázok sa nepodarilo nahrať. Odstráňte ho alebo vyberte iný obrázok.',
    attachmentFileChanged: 'Váš pracovný priestor sa zmenil. Priložte súbor znova.',
    attachmentSignInFile: 'Prihláste sa a nahrajte firemný súbor.',
    attachmentEmpty: '{name} je prázdny.',
    attachmentFileTypes:
      'Vyberte súbor vo formáte PDF, textový súbor alebo súbor Excel, Word či PowerPoint.',
    attachmentUploadFile: 'Tento súbor sa nepodarilo nahrať.',
    attachmentReadable: 'Odstráňte neúspešne priložený súbor a nahrajte čitateľnú kópiu.',
    repairWorkspaceChanged: 'Váš pracovný priestor sa zmenil. Pred úpravou znova načítajte chat.',
    repairCannotSave: 'Túto prílohu nie je možné uložiť. Odstráňte ju a vyberte súbor znova.',
    repairOldUnavailable:
      'Táto stará príloha nie je k dispozícii. Odstráňte ju alebo vyberte súbor znova.',
    repairUpload: 'Súbor sa nepodarilo nahrať. Odstráňte ho alebo ho vyberte znova.',
    repairUnavailable: 'Súbor nie je k dispozícii. Odstráňte ho alebo ho vyberte znova.',
    repairMessageUnavailable: 'Táto správa nie je k dispozícii. Znova načítajte chat.',
    repairChatChanged: 'Tento chat sa zmenil. Pred uložením úpravy ho znova načítajte.',
    turnUnavailable: 'Toto kolo nie je k dispozícii. Znova načítajte chat a odošlite novú správu.',
    imagePreparation: 'Tento obrázok sa nepodarilo pripraviť. Odstráňte ho a vyberte iný obrázok.',
    documentSize: '{name} je väčší než 4 MB.',
    projectFileJoinFailed:
      '{name} je vo vašich súboroch, ale nepodarilo sa ho pridať do projektu. Pridajte ho z projektu.',
  },
  sl: {
    uploadRule:
      'Uporabite Excel (.xlsx), Word (.docx), PowerPoint (.pptx), PDF, CSV, TSV, besedilne datoteke, JSON, prepise, zvočne ali videodatoteke do 500 MB. Starejše Officeove datoteke najprej shranite kot .xlsx, .docx ali .pptx.',
    emptyFile: '{name} je prazna. Izberite datoteko z vsebino.',
    uploadPart: 'Dela datoteke ni bilo mogoče naložiti. Poskusite znova.',
    uploadEtag: 'Storitev za shranjevanje ni potrdila dela datoteke. Poskusite znova.',
    uploadGrant: 'Seji nalaganja manjka dovoljenje za shranjevanje.',
    uploadParts: 'Seja nalaganja je vrnila nepopolna dovoljenja za dele datoteke.',
    browserChecksum:
      'Ta brskalnik ne more preveriti velikega prenosa. Uporabite posodobljen brskalnik.',
    history401: 'Znova se prijavite za iskanje po pogovorih.',
    history403: 'Za iskanje po pogovorih je potrebno povabilo podjetja.',
    historyUnavailable: 'Iskanje po pogovorih ni na voljo. Poskusite znova.',
    historyUnreadable: 'Iskanje po pogovorih je vrnilo neberljiv odgovor. Poskusite znova.',
    search401: 'Znova se prijavite za iskanje po delovnem prostoru.',
    searchUnavailable: 'Iskanje po delovnem prostoru ni na voljo. Poskusite znova.',
    searchUnreadable: 'Iskanje po delovnem prostoru je vrnilo neberljiv odgovor. Poskusite znova.',
    skillsUnavailable: 'Veščine niso na voljo. Poskusite znova.',
    skillsUnreadable: 'Veščine so vrnile neberljiv odgovor. Poskusite znova.',
    csvColumns: 'Ta datoteka ima več kot 200 stolpcev. Prenesite izvirnik, da si jih ogledate.',
    csvField: 'Vrednost je prevelika za predogled. Prenesite izvirnik, da si jo ogledate.',
    csvQuote:
      'Ta CSV vsebuje nedokončano vrednost v narekovajih. Prenesite izvirnik ali naložite popravljeno datoteko.',
    meetingRecording: 'Ta sestanek še nima shranjenega posnetka.',
    uploadInterrupted: 'Nalaganje je bilo prekinjeno. Poskusite znova.',
    uploadRefused: 'Storitev za shranjevanje je datoteko zavrnila. Poskusite znova.',
    downloadDeck: 'PowerPointove datoteke ni bilo mogoče ustvariti. Poskusite znova.',
    downloadFile: 'Te datoteke ni bilo mogoče prenesti. Poskusite znova.',
    integrationsUnavailable: 'Integracij ni bilo mogoče naložiti. Poskusite znova.',
    inboxActionUnavailable: 'To dejanje ni na voljo.',
    inboxUnavailable: 'Prejetih sporočil ni bilo mogoče naložiti.',
    referencesUnavailable:
      'Nekateri izbrani sklici niso na voljo. Pred nadaljevanjem preverite svoj dostop do njih.',
    fileVerification: 'Preverjanje datoteke ni uspelo.',
    uploadChecksum:
      'Ta brskalnik ne more preveriti velikega nalaganja. Uporabite posodobljen brskalnik.',
    agentUnreadable: 'Poneva je vrnil odgovor, ki ga ta stran ni mogla prebrati.',
    office_unreadable:
      '{name} ni bilo mogoče prebrati. Odprite ga v programu Office in shranite novo kopijo v obliki .xlsx, .docx ali .pptx.',
    office_encrypted: '{name} je zaščiten z geslom. Odstranite geslo in ga znova naložite.',
    office_has_no_text:
      '{name} nima berljivega besedila ali celic. Naložite različico z besedilom ali PDF z besedilom, ki ga je mogoče izbrati.',
    office_too_complex: '{name} je preveč zapleten za branje. Razdelite ga na manjše datoteke.',
    office_extraction_timeout:
      '{name} je bilo treba brati predolgo. Razdelite ga na manjše datoteke.',
    file_text_too_large:
      '{name} je shranjen za prenos. Naložite kopijo, manjšo od 50 MiB, da ga bo Poneva lahko prebrala.',
    pdf_has_no_text:
      '{name} nima besedila, ki bi ga bilo mogoče izbrati, zato je morda optično prebrana slika. Priložite kopijo, veliko največ 4 MB, neposredno sporočilu klepeta ali naložite različico z besedilom.',
    pdf_encrypted: '{name} je zaščiten z geslom. Odstranite geslo in ga znova naložite.',
    pdf_unreadable: '{name} ni bilo mogoče prebrati kot PDF.',
    pdf_too_many_pages: '{name} ima več kot 1.000 strani. Razdelite ga na manjše datoteke PDF.',
    pdf_too_complex: '{name} je preveč zapleten za branje. Razdelite ga na manjše datoteke PDF.',
    file_too_complex:
      '{name} je pregloboko ugnezden ali pa je njegovo preverjanje prepočasno. Poenostavite njegovo strukturo ali ga razdelite na manjše datoteke.',
    pdf_extraction_timeout:
      '{name} je bilo treba brati predolgo. Razdelite ga na manjše datoteke PDF in poskusite znova.',
    file_too_large: '{name} je večji od 500 MB.',
    invalid_workspace_input:
      '{name} ni bilo mogoče shraniti. Preverite, ali se vrsta datoteke ujema z njeno vsebino (na primer veljaven JSON).',
    workspace_storage_full:
      'Knjižnica vašega podjetja je dosegla omejitev prostora za shranjevanje, zato ta datoteka ni bila shranjena.',
    workspace_capacity_reached:
      'Knjižnica vašega podjetja je dosegla omejitev števila datotek, zato ta datoteka ni bila shranjena.',
    workspace_upload_busy: 'Drugi prenosi so še v obdelavi. Poskusite znova čez trenutek.',
    workspace_storage_unavailable:
      'Nalaganje datotek trenutno ni na voljo. Obrnite se na podporo Poneva.',
    company_admin_required: 'Datoteke lahko v Knjižnico dodajo samo lastniki podjetja.',
    uploadGeneric: 'Datoteke {name} ni bilo mogoče naložiti. Poskusite znova.',
    uploadTooLarge: '{name} je večji od 500 MB.',
    attachmentWorkspaceChanged: 'Vaš delovni prostor se je spremenil. Znova izberite sliko.',
    attachmentSignInImage: 'Prijavite se, da lahko priložite sliko.',
    attachmentImageSize: 'Izberite sliko, manjšo od 16 MiB, ali jo odstranite za nadaljevanje.',
    attachmentImageUnavailable: 'Slika ni na voljo. Odstranite jo ali jo znova izberite.',
    attachmentUploadImage: 'Slike ni bilo mogoče naložiti. Odstranite jo ali izberite drugo sliko.',
    attachmentFileChanged: 'Vaš delovni prostor se je spremenil. Znova priložite datoteko.',
    attachmentSignInFile: 'Prijavite se, da lahko naložite poslovno datoteko.',
    attachmentEmpty: '{name} je prazna.',
    attachmentFileTypes:
      'Izberite datoteko PDF, besedilno datoteko ali datoteko Excel, Word oziroma PowerPoint.',
    attachmentUploadFile: 'Te datoteke ni bilo mogoče naložiti.',
    attachmentReadable: 'Odstranite neuspešno priloženo datoteko in naložite berljivo kopijo.',
    repairWorkspaceChanged:
      'Vaš delovni prostor se je spremenil. Pred urejanjem znova naložite klepet.',
    repairCannotSave: 'Te priloge ni mogoče shraniti. Odstranite jo in znova izberite datoteko.',
    repairOldUnavailable:
      'Ta stara priloga ni na voljo. Odstranite jo ali znova izberite datoteko.',
    repairUpload: 'Datoteke ni bilo mogoče naložiti. Odstranite jo ali jo znova izberite.',
    repairUnavailable: 'Datoteka ni na voljo. Odstranite jo ali jo znova izberite.',
    repairMessageUnavailable: 'To sporočilo ni na voljo. Znova naložite klepet.',
    repairChatChanged: 'Ta klepet se je spremenil. Pred shranjevanjem urejanja ga znova naložite.',
    turnUnavailable: 'Ta korak ni na voljo. Znova naložite klepet in pošljite novo sporočilo.',
    imagePreparation: 'Te slike ni bilo mogoče pripraviti. Odstranite jo in izberite drugo sliko.',
    documentSize: '{name} je večja od 4 MB.',
    projectFileJoinFailed:
      '{name} je v vaših datotekah, vendar ga ni bilo mogoče dodati v projekt. Dodajte ga iz projekta.',
  },
  es: {
    uploadRule:
      'Usa archivos de Excel (.xlsx), Word (.docx), PowerPoint (.pptx), PDF, CSV, TSV, texto, JSON, transcripciones, audio o video de hasta 500 MB. Guarda primero los archivos antiguos de Office como .xlsx, .docx o .pptx.',
    emptyFile: '{name} está vacío. Elige un archivo que tenga contenido.',
    uploadPart: 'No se pudo cargar una parte del archivo. Inténtalo de nuevo.',
    uploadEtag:
      'El servicio de almacenamiento no confirmó una parte del archivo. Inténtalo de nuevo.',
    uploadGrant: 'Falta la autorización de almacenamiento de la sesión de carga.',
    uploadParts: 'La sesión de carga devolvió autorizaciones incompletas para las partes.',
    browserChecksum:
      'Este navegador no puede verificar una carga grande. Usa un navegador actualizado.',
    history401: 'Vuelve a iniciar sesión para buscar en tus conversaciones.',
    history403: 'Se necesita una invitación de la empresa para buscar conversaciones.',
    historyUnavailable: 'La búsqueda de conversaciones no está disponible. Inténtalo de nuevo.',
    historyUnreadable:
      'La búsqueda de conversaciones devolvió una respuesta ilegible. Inténtalo de nuevo.',
    search401: 'Vuelve a iniciar sesión para buscar en tu espacio de trabajo.',
    searchUnavailable:
      'La búsqueda en el espacio de trabajo no está disponible. Inténtalo de nuevo.',
    searchUnreadable:
      'La búsqueda en el espacio de trabajo devolvió una respuesta ilegible. Inténtalo de nuevo.',
    skillsUnavailable: 'Las habilidades no están disponibles. Inténtalo de nuevo.',
    skillsUnreadable: 'Las habilidades devolvieron una respuesta ilegible. Inténtalo de nuevo.',
    csvColumns: 'Este archivo tiene más de 200 columnas. Descarga el original para revisarlo.',
    csvField:
      'Un valor es demasiado grande para mostrarlo en la vista previa. Descarga el original para revisarlo.',
    csvQuote:
      'Este CSV tiene un valor entre comillas sin cerrar. Descarga el original o carga un archivo corregido.',
    meetingRecording: 'Esta reunión aún no tiene una grabación conservada.',
    uploadInterrupted: 'La carga se interrumpió. Inténtalo de nuevo.',
    uploadRefused: 'El servicio de almacenamiento rechazó el archivo. Inténtalo de nuevo.',
    downloadDeck: 'No pudimos crear el archivo de PowerPoint. Inténtalo de nuevo.',
    downloadFile: 'No pudimos descargar este archivo. Inténtalo de nuevo.',
    integrationsUnavailable: 'No pudimos cargar las integraciones. Inténtalo de nuevo.',
    inboxActionUnavailable: 'Esta acción no está disponible.',
    inboxUnavailable: 'No pudimos cargar tu bandeja de entrada.',
    referencesUnavailable:
      'Algunas referencias seleccionadas no están disponibles. Comprueba que tengas acceso a ellas antes de continuar.',
    fileVerification: 'No se pudo verificar el archivo.',
    uploadChecksum:
      'Este navegador no puede verificar una carga grande. Usa un navegador actualizado.',
    agentUnreadable: 'Poneva devolvió una respuesta que esta página no pudo leer.',
    office_unreadable:
      'No se pudo leer {name}. Ábrelo en Office y guarda una copia nueva en formato .xlsx, .docx o .pptx.',
    office_encrypted:
      '{name} está protegido con contraseña. Quita la contraseña y vuelve a subirlo.',
    office_has_no_text:
      '{name} no contiene texto ni celdas legibles. Sube una versión de texto o un PDF con texto seleccionable.',
    office_too_complex:
      '{name} es demasiado complejo para leerlo. Divídelo en archivos más pequeños.',
    office_extraction_timeout:
      '{name} tardó demasiado en leerse. Divídelo en archivos más pequeños.',
    file_text_too_large:
      '{name} se ha guardado para descargar. Sube una copia de menos de 50 MiB para que Poneva pueda leerla.',
    pdf_has_no_text:
      '{name} no contiene texto seleccionable, por lo que podría ser una imagen escaneada. Adjunta una copia de hasta 4 MB directamente en un mensaje de chat o sube una versión de texto.',
    pdf_encrypted: '{name} está protegido con contraseña. Quita la contraseña y vuelve a subirlo.',
    pdf_unreadable: 'No se pudo leer {name} como PDF.',
    pdf_too_many_pages: '{name} tiene más de 1.000 páginas. Divídelo en PDF más pequeños.',
    pdf_too_complex: '{name} es demasiado complejo para leerlo. Divídelo en PDF más pequeños.',
    file_too_complex:
      '{name} tiene demasiados niveles de anidamiento o tarda demasiado en comprobarse. Simplifica su estructura o divídelo en archivos más pequeños.',
    pdf_extraction_timeout:
      '{name} tardó demasiado en leerse. Divídelo en PDF más pequeños e inténtalo de nuevo.',
    file_too_large: '{name} supera los 500 MB.',
    invalid_workspace_input:
      'No se pudo guardar {name}. Comprueba que el archivo coincida con su tipo (por ejemplo, que sea un JSON válido).',
    workspace_storage_full:
      'La Biblioteca de tu empresa ha alcanzado el límite de almacenamiento, por lo que no se ha guardado este archivo.',
    workspace_capacity_reached:
      'La Biblioteca de tu empresa ha alcanzado el límite de archivos, por lo que no se ha guardado este archivo.',
    workspace_upload_busy:
      'Aún se están procesando otras cargas. Inténtalo de nuevo en un momento.',
    workspace_storage_unavailable:
      'Las cargas de archivos no están disponibles actualmente. Contacta con el servicio de asistencia de Poneva.',
    company_admin_required:
      'Solo los propietarios de la empresa pueden añadir archivos a la Biblioteca.',
    uploadGeneric: 'No se pudo cargar {name}. Inténtalo de nuevo.',
    uploadTooLarge: '{name} supera los 500 MB.',
    attachmentWorkspaceChanged: 'Tu espacio de trabajo ha cambiado. Elige la imagen de nuevo.',
    attachmentSignInImage: 'Inicia sesión para adjuntar una imagen.',
    attachmentImageSize: 'Elige una imagen de menos de 16 MiB o elimínala para continuar.',
    attachmentImageUnavailable: 'La imagen no está disponible. Elimínala o elígela de nuevo.',
    attachmentUploadImage: 'No se ha podido subir la imagen. Elimínala o elige otra imagen.',
    attachmentFileChanged: 'Tu espacio de trabajo ha cambiado. Adjunta el archivo de nuevo.',
    attachmentSignInFile: 'Inicia sesión para subir un archivo empresarial.',
    attachmentEmpty: '{name} está vacío.',
    attachmentFileTypes: 'Elige un archivo PDF, de texto, Excel, Word o PowerPoint.',
    attachmentUploadFile: 'No se ha podido subir este archivo.',
    attachmentReadable: 'Elimina el archivo adjunto fallido y sube una copia legible.',
    repairWorkspaceChanged: 'Tu espacio de trabajo ha cambiado. Recarga el chat antes de editar.',
    repairCannotSave:
      'No se puede guardar este archivo adjunto. Elimínalo y vuelve a elegir el archivo.',
    repairOldUnavailable:
      'Este archivo adjunto antiguo no está disponible. Elimínalo o vuelve a elegir el archivo.',
    repairUpload: 'No se ha podido subir el archivo. Elimínalo o vuelve a elegirlo.',
    repairUnavailable: 'El archivo no está disponible. Elimínalo o vuelve a elegirlo.',
    repairMessageUnavailable: 'Este mensaje no está disponible. Recarga el chat.',
    repairChatChanged: 'Este chat ha cambiado. Recárgalo antes de guardar la edición.',
    turnUnavailable: 'Este turno no está disponible. Recarga el chat y envía un mensaje nuevo.',
    imagePreparation: 'No se ha podido preparar esta imagen. Elimínala y elige otra imagen.',
    documentSize: '{name} supera los 4 MB.',
    projectFileJoinFailed:
      '{name} está en tus archivos, pero no se pudo añadir al proyecto. Añádelo desde el proyecto.',
  },
  sv: {
    uploadRule:
      'Använd Excel (.xlsx), Word (.docx), PowerPoint (.pptx), PDF, CSV, TSV, text, JSON, transkriptioner, ljud eller video på upp till 500 MB. Spara äldre Office-filer som .xlsx, .docx eller .pptx först.',
    emptyFile: '{name} är tom. Välj en fil som innehåller data.',
    uploadPart: 'En fildel kunde inte laddas upp. Försök igen.',
    uploadEtag: 'Lagringstjänsten bekräftade inte en fildel. Försök igen.',
    uploadGrant: 'Uppladdningssessionen saknar sitt lagringstillstånd.',
    uploadParts: 'Uppladdningssessionen returnerade ofullständiga tillstånd för fildelar.',
    browserChecksum:
      'Den här webbläsaren kan inte verifiera en stor uppladdning. Använd en aktuell webbläsare.',
    history401: 'Logga in igen för att söka i dina konversationer.',
    history403: 'En företagsinbjudan krävs för att söka i konversationer.',
    historyUnavailable: 'Det går inte att söka i konversationer just nu. Försök igen.',
    historyUnreadable: 'Konversationssökningen returnerade ett oläsligt svar. Försök igen.',
    search401: 'Logga in igen för att söka i din arbetsyta.',
    searchUnavailable: 'Det går inte att söka i arbetsytan just nu. Försök igen.',
    searchUnreadable: 'Arbetsytans sökning returnerade ett oläsligt svar. Försök igen.',
    skillsUnavailable: 'Skills är inte tillgängliga just nu. Försök igen.',
    skillsUnreadable: 'Skills returnerade ett oläsligt svar. Försök igen.',
    csvColumns: 'Den här filen har fler än 200 kolumner. Ladda ner originalet för att granska den.',
    csvField:
      'Ett värde är för stort för att förhandsgranska. Ladda ner originalet för att granska det.',
    csvQuote:
      'Den här CSV-filen har ett oavslutat citerat värde. Ladda ner originalet eller ladda upp en korrigerad fil.',
    meetingRecording: 'Det finns ännu ingen inspelning kvar av det här mötet.',
    uploadInterrupted: 'Uppladdningen avbröts. Försök igen.',
    uploadRefused: 'Lagringstjänsten nekade filen. Försök igen.',
    downloadDeck: 'Vi kunde inte skapa PowerPoint-filen. Försök igen.',
    downloadFile: 'Vi kunde inte ladda ner den här filen. Försök igen.',
    integrationsUnavailable: 'Vi kunde inte läsa in integrationerna. Försök igen.',
    inboxActionUnavailable: 'Den här åtgärden är inte tillgänglig.',
    inboxUnavailable: 'Vi kunde inte läsa in din inkorg.',
    referencesUnavailable:
      'Vissa valda referenser är inte tillgängliga. Kontrollera åtkomsten till dem innan du fortsätter.',
    fileVerification: 'Filverifieringen misslyckades.',
    uploadChecksum:
      'Den här webbläsaren kan inte verifiera en stor uppladdning. Använd en aktuell webbläsare.',
    agentUnreadable: 'Poneva returnerade ett svar som den här sidan inte kunde läsa.',
    office_unreadable:
      '{name} kunde inte läsas. Öppna filen i Office och spara en ny .xlsx-, .docx- eller .pptx-kopia.',
    office_encrypted: '{name} är lösenordsskyddad. Ta bort lösenordet och ladda upp filen igen.',
    office_has_no_text:
      '{name} innehåller ingen läsbar text eller några läsbara celler. Ladda upp en textversion eller en PDF med markerbar text.',
    office_too_complex: '{name} är för komplex för att läsas. Dela upp den i mindre filer.',
    office_extraction_timeout: '{name} tog för lång tid att läsa. Dela upp den i mindre filer.',
    file_text_too_large:
      '{name} har sparats för nedladdning. Ladda upp en kopia på under 50 MiB som Poneva kan läsa.',
    pdf_has_no_text:
      '{name} innehåller ingen markerbar text och kan därför vara en skannad bild. Bifoga en kopia på högst 4 MB direkt i ett chattmeddelande eller ladda upp en textversion.',
    pdf_encrypted: '{name} är lösenordsskyddad. Ta bort lösenordet och ladda upp filen igen.',
    pdf_unreadable: '{name} kunde inte läsas som PDF.',
    pdf_too_many_pages: '{name} innehåller fler än 1 000 sidor. Dela upp den i mindre PDF-filer.',
    pdf_too_complex: '{name} är för komplex för att läsas. Dela upp den i mindre PDF-filer.',
    file_too_complex:
      '{name} är för djupt nästlad eller tar för lång tid att kontrollera. Förenkla strukturen eller dela upp den i mindre filer.',
    pdf_extraction_timeout:
      '{name} tog för lång tid att läsa. Dela upp den i mindre PDF-filer och försök igen.',
    file_too_large: '{name} är större än 500 MB.',
    invalid_workspace_input:
      '{name} kunde inte sparas. Kontrollera att filen överensstämmer med sin filtyp (till exempel giltig JSON).',
    workspace_storage_full:
      'Ditt företags Bibliotek har nått sin lagringsgräns, så filen sparades inte.',
    workspace_capacity_reached:
      'Ditt företags Bibliotek har nått sin filkapacitet, så filen sparades inte.',
    workspace_upload_busy: 'Andra uppladdningar bearbetas fortfarande. Försök igen om en stund.',
    workspace_storage_unavailable:
      'Filuppladdningar är för närvarande inte tillgängliga. Kontakta Ponevas support.',
    company_admin_required: 'Endast företagsägare kan lägga till filer i Biblioteket.',
    uploadGeneric: 'Vi kunde inte ladda upp {name}. Försök igen.',
    uploadTooLarge: '{name} är större än 500 MB.',
    attachmentWorkspaceChanged: 'Din arbetsyta har ändrats. Välj bilden igen.',
    attachmentSignInImage: 'Logga in för att bifoga en bild.',
    attachmentImageSize: 'Välj en bild på mindre än 16 MiB eller ta bort den för att fortsätta.',
    attachmentImageUnavailable: 'Bilden är inte tillgänglig. Ta bort den eller välj den igen.',
    attachmentUploadImage:
      'Det gick inte att ladda upp bilden. Ta bort den eller välj en annan bild.',
    attachmentFileChanged: 'Din arbetsyta har ändrats. Bifoga filen igen.',
    attachmentSignInFile: 'Logga in för att ladda upp en företagsfil.',
    attachmentEmpty: '{name} är tom.',
    attachmentFileTypes: 'Välj en PDF-, text-, Excel-, Word- eller PowerPoint-fil.',
    attachmentUploadFile: 'Det gick inte att ladda upp filen.',
    attachmentReadable: 'Ta bort den misslyckade bilagan och ladda upp en läsbar kopia.',
    repairWorkspaceChanged: 'Din arbetsyta har ändrats. Läs in chatten igen innan du redigerar.',
    repairCannotSave: 'Den här bilagan kan inte sparas. Ta bort den och välj filen igen.',
    repairOldUnavailable:
      'Den här gamla bilagan är inte tillgänglig. Ta bort den eller välj filen igen.',
    repairUpload: 'Det gick inte att ladda upp filen. Ta bort den eller välj den igen.',
    repairUnavailable: 'Filen är inte tillgänglig. Ta bort den eller välj den igen.',
    repairMessageUnavailable: 'Det här meddelandet är inte tillgängligt. Läs in chatten igen.',
    repairChatChanged: 'Den här chatten har ändrats. Läs in den igen innan du sparar redigeringen.',
    turnUnavailable:
      'Den här turen är inte tillgänglig. Läs in chatten igen och skicka ett nytt meddelande.',
    imagePreparation: 'Den här bilden kunde inte förberedas. Ta bort den och välj en annan bild.',
    documentSize: '{name} är större än 4 MB.',
    projectFileJoinFailed:
      '{name} finns bland dina filer, men gick inte att lägga till i projektet. Lägg till den från projektet.',
  },
} as const satisfies NamespaceCatalog<WorkspaceErrorKey>;

export default catalog;
